import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Location from 'expo-location';
import {
  View, Text, ScrollView, TouchableOpacity, Image, Alert,
  StyleSheet, ActivityIndicator, FlatList, RefreshControl,
  Platform, StatusBar, Animated, Easing, Pressable,
} from 'react-native';
import ThemedRefreshControl from '../components/ThemedRefreshControl';
import { showAppAlert } from '../components/AppDialog';
import { SafeAreaView } from 'react-native-safe-area-context';
import { colors, typography, spacing, radius, shadow } from '../theme';
import { useI18n } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { CJ_IMPRESSION_TAG } from '../services/MangoService';
import { useTheme } from '../context/ThemeContext';
import { getFirstName } from '../utils/userDisplay';
import { useResetScrollOnFocus } from '../hooks/useResetScrollOnFocus';
import { DeviceEventEmitter, InteractionManager } from 'react-native';
import { getWardrobe } from '../services/DatabaseService';
import UpgradeModal from '../components/UpgradeModal';
import { usePremiumGuard } from '../hooks/usePremiumGuard';
import useWebBackToClose from '../hooks/useWebBackToClose';
import { AWS_CONFIG } from '../config/aws_config';
// v1.12.340 — Zara/ASOS/Amazon/H&M/Zalando/Sovrn supprimés du HomeScreen
//   (cohérence avec CatalogScreen v1.12.326 → 4 partenaires Mytenue uniquement)
import { Modal } from 'react-native';
import { openProductForPurchase } from '../services/PurchaseTrackingService';
import { applySkimlinksToProducts } from '../services/SkimlinksService';
import { normalizedNameKey } from '../utils/productNameUtils';
import { mergeDuplicateProducts } from '../utils/mergeProducts';
import { displayName } from '../utils/displayName';
// v1.13.46 — expo-image (cache disque) pour les images styliste : évite le
// rechargement après éviction mémoire du <Image> RN brut
import { Image as ExpoImageSafe } from '../utils/safeExpoImage';
import { hasUsableImage } from '../utils/imageFilter';
// v1.13.43 — Registre global images mortes : exclut aussi de la sélection styliste
import { markImageDead, useDeadImagesVersion } from '../utils/deadImages';
import TrendingBadge from '../components/TrendingBadge';
import PurchaseConfirmModal from '../components/PurchaseConfirmModal';
import ProductDetailModal from '../components/ProductDetailModal';
import { generateOutfits, suggestCatalogItems, getTemperatureFromWeather } from '../services/OutfitMatchingService';
// ═══ v1.9.21 : Import du moteur OutfitEngine pour Sélection styliste ═══
// Avant : Sélection styliste utilisait des schemes hardcodés (20 patterns).
// Maintenant : utilise le même moteur que Looks (combinatoire complète,
// scoring cohésion + tendance + tags), appliqué au CATALOGUE (sans dressing).
import { generateOutfits as OutfitEngineGenerate, computeCohesion as OutfitEngineComputeCohesion } from '../services/OutfitEngine';
import { getOutfitOfTheDay, normalizeLookForScoring, stylistSelection, buildBadges, scoreLook } from '../services/OutfitOfTheDayService';
// v1.12.388 — Backend AWS Looks (mytenue-aws-cdk). Behind USE_BACKEND_LOOKS flag.
import {
  USE_BACKEND_LOOKS,
  fetchLooksAI,
  fetchLooksStyliste,
  fetchLooksWithRetry,
  adaptApiLookToStylistOutfit,
} from '../services/LooksApiService';
import { getColorBank } from '../services/ColorBankService';
import { getMaterialBank } from '../services/MaterialBankService';
// ═══ v1.9.28 : Import StyleBankService pour utiliser les 500 combos IA en Sélection styliste ═══
import { getStyleBank, findMatchingCombinations } from '../services/StyleBankService';
import OutfitCard from '../components/OutfitCard';
import FlatLayButton from '../components/FlatLayButton';
import WardrobeItemDetailModal from '../components/WardrobeItemDetailModal';
// v1.12.280 — FAB "Créer un look" avec animation expand→collapse
import CreateLookModal from '../components/CreateLookModal';
// ═══ v1.10.0 : Modal essayage virtuel sur mannequin (FASHN.ai) ═══
import TryOnModal from '../components/TryOnModal';
import CalendarIcon from '../components/CalendarIcon';
import AddOutfitToCalendarModal from '../components/AddOutfitToCalendarModal';
import PreferencesIcon from '../components/PreferencesIcon';
import WeatherIcon from '../components/WeatherIcon';
import Tooltip from '../components/Tooltip';
// ═══ v1.10.9 : Modal préférences du jour (accès rapide depuis l'accueil) ═══
import DailyPreferencesModal from '../components/DailyPreferencesModal';
import { setSessionPrefs } from '../services/DailyPreferencesService';
import { fetchDynamicCatalog } from '../services/DynamicCatalogService';
import { scoreOutfit, inferFormality, enrichCatalogWithAI, analyzeCatalogInBackground } from '../services/AIStylistService';
import { getCurrentWeather, getWeatherDesc } from '../services/WeatherService';
import { rankByPersonalization, computeAge } from '../services/CatalogPersonalizer';

// v1.12.205 — Manteaux ajouté (long, hivernal). Filtrage tempC dans HomeScreen
// affiche Manteaux quand tempC ≤ 12, Vestes quand tempC entre 8 et 22.
const CATS = ['Robes','Hauts','Vestes','Manteaux','Bas','Chaussures','Sacs','Bijoux','Accessoires'];
const CAT_ICONS = { Robes:'👗',Hauts:'👚',Vestes:'🧥',Manteaux:'🧥',Bas:'👖',Chaussures:'👟',Sacs:'👜',Bijoux:'💍',Accessoires:'✨' };

// v1.12.736 — Logo header agrandi (visible) sans créer d'espace blanc en haut.
function MyTenueLogo({ color, dark = false }) {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const scaleAnim = useRef(new Animated.Value(0.92)).current;
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, { toValue: 1, duration: 700, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
      Animated.spring(scaleAnim, { toValue: 1, friction: 7, tension: 40, useNativeDriver: true }),
    ]).start();
  }, []);
  return (
    <Animated.View style={{ opacity: fadeAnim, transform: [{ scale: scaleAnim }] }}>
      <Image
        source={require('../../assets/logo-app.png')}
        style={{ width: 88, height: 88, resizeMode: 'contain' }}
      />
    </Animated.View>
  );
}

// v1.13.x — Écriture SÛRE du cache styliste. Sur web, localStorage = ~5 Mo PARTAGÉS
// entre toutes les clés → écrire 40 looks faisait dépasser le quota (QuotaExceededError
// non catché = "Uncaught (in promise)" → crash page). On limite à 8 looks sur web,
// on catch le rejet async, et en cas de quota on purge les vieux caches styliste.
async function safeCacheStylist(key, wardrobeSig, looks) {
  const isWeb = Platform.OS === 'web';
  const limit = isWeb ? 8 : 40;
  const payload = (n) => JSON.stringify({ wardrobeSig, looks: (looks || []).slice(0, n) });
  try {
    await AsyncStorage.setItem(key, payload(limit));
  } catch (_) {
    // Quota dépassé (web) → purger les autres caches styliste puis réessayer minimal
    try {
      if (isWeb) {
        const keys = await AsyncStorage.getAllKeys();
        const old = (keys || []).filter(k => k.startsWith('stylist_cache_') && k !== key);
        if (old.length) await AsyncStorage.multiRemove(old);
        await AsyncStorage.setItem(key, payload(4)).catch(() => {});
      }
    } catch (_) {}
  }
}

export default function HomeScreen({ navigation }) {
  const { dark, theme } = useTheme();
  const { t, lang } = useI18n();
  const { user, getValidToken, updateUserProfile, isAuthenticated } = useAuth();
  // v1.12.267 — Helper pour traduire les catégories FR ('Hauts','Bas','Vestes'...) en UPPER selon langue
  const tCatUpper = (cat) => {
    if (!cat) return t('piece_caps');
    const KEY = { 'Robes':'cat_dresses','Hauts':'cat_tops','Vestes':'cat_jackets','Manteaux':'cat_coats','Bas':'cat_bottoms','Chaussures':'cat_shoes','Sacs':'cat_bags','Bijoux':'cat_jewelry','Accessoires':'cat_accessories','Maillots':'cat_swimwear','Sport':'cat_sportswear' };
    const k = KEY[cat];
    return (k ? t(k) : cat).toUpperCase();
  };
  const { showPremium: showUpgrade, limitInfo, guardCheck, closePremium: closeUpgrade } = usePremiumGuard(user);

  // Scroll en haut quand on revient sur cet écran
  const scrollRef = useResetScrollOnFocus();

  const [wardrobe,      setWardrobe]      = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  // v1.12.280 — FAB "Créer un look" : modale + animation expand→collapse
  // v1.12.281 — expand au hover (desktop) et au scroll (mobile)
  const [showCreateLook, setShowCreateLook] = useState(false);
  // ⚠️ onClose STABLE (useCallback) : AppModal web pousse une entrée history ;
  // un onClose inline (nouvelle réf à chaque render) re-déclenche l'effet history
  // → history.back() → le modal se ferme tout seul. Cf. bug AppModal documenté.
  const closeCreateLook = React.useCallback(() => {
    setShowCreateLook(false);
  }, []);
  // ═══ DIAG FREEZE (temporaire) : écrit l'étape en cours AVANT chaque calcul lourd.
  // Si l'app gèle, App.js affichera cette étape au lancement suivant. À retirer après. ═══
  // v1.13.22 — Diagnostic freeze démarrage retiré (gel localisé + corrigé).
  // No-op conservé pour ne pas toucher les nombreux call-sites lf('...').
  const lf = React.useCallback(() => {}, []);
  // v1.13.50 — FAB "composer un look" SANS animation de largeur (le width interpolé
  //   forçait useNativeDriver:false → l'anim se figeait quand le thread JS était
  //   occupé). Maintenant : largeur pilotée par un ÉTAT (snap instantané, zéro anim
  //   JS) + fondu du label en driver NATIF (jamais bloqué par le JS).
  const [fabExpanded, setFabExpanded] = useState(true);
  const fabLabelOpacity = useRef(new Animated.Value(1)).current; // driver natif
  const fabCollapseTimerRef = useRef(null);
  const collapseFab = useCallback((delay = 0) => {
    if (fabCollapseTimerRef.current) { clearTimeout(fabCollapseTimerRef.current); fabCollapseTimerRef.current = null; }
    fabCollapseTimerRef.current = setTimeout(() => {
      Animated.timing(fabLabelOpacity, {
        toValue: 0, duration: 160, easing: Easing.out(Easing.cubic), useNativeDriver: true,
      }).start(({ finished }) => { if (finished) setFabExpanded(false); });
    }, delay);
  }, [fabLabelOpacity]);
  const expandFab = useCallback(() => {
    if (fabCollapseTimerRef.current) { clearTimeout(fabCollapseTimerRef.current); fabCollapseTimerRef.current = null; }
    setFabExpanded(true);
    Animated.timing(fabLabelOpacity, {
      toValue: 1, duration: 200, easing: Easing.out(Easing.cubic), useNativeDriver: true,
    }).start();
  }, [fabLabelOpacity]);
  useEffect(() => {
    // 1er affichage : reste expanded 2.5s, puis collapse
    collapseFab(2500);
    return () => { if (fabCollapseTimerRef.current) clearTimeout(fabCollapseTimerRef.current); };
  }, [collapseFab]);

  // v1.12.281 — détection scroll : expand brièvement puis recollapse
  const lastScrollYRef = useRef(0);
  const handleFabScroll = useCallback((e) => {
    const y = e?.nativeEvent?.contentOffset?.y || 0;
    const delta = Math.abs(y - lastScrollYRef.current);
    lastScrollYRef.current = y;
    if (delta > 8) {
      expandFab();
      collapseFab(1500); // recollapse 1.5s après dernier scroll
    }
  }, [expandFab, collapseFab]);
  const [boughtProduct,   setBoughtProduct]   = useState(null);
  // v1.12.140 — clic sur article du dressing depuis Tenue du jour ouvre la fiche detaillee
  const [selectedWardrobeItem, setSelectedWardrobeItem] = useState(null);
  // ═══ Sélection styliste : nombre de tenues affichées ═══
  // v1.9.35 : démarre à 10 (avant 5), incrément +10 par clic, PAS de limite max
  // → laisse l'user explorer librement le catalogue à travers les looks
  // v1.12.94 — défaut réduit de 10 → 3 (l'user voit moins de tenues d'emblée
  // et utilise "Voir plus" pour explorer s'il veut). Incrément par clic = 5.
  const STYLIST_DEFAULT = 3;
  const STYLIST_INCREMENT = 5;
  const [stylistOutfitCount, setStylistOutfitCount] = useState(STYLIST_DEFAULT);
  // v1.12.136 — Pagination Sélection styliste sans freeze : on monte une tenue
  // par frame (requestAnimationFrame) après que la pression du bouton soit
  // gérée (InteractionManager.runAfterInteractions). Chaque carte a beaucoup
  // de SmartImage + sous-vues, donc tout monter d'un coup figeait l'UI ~500ms.
  const [loadingMoreStylist, setLoadingMoreStylist] = useState(false);
  const loadMoreStylist = React.useCallback(() => {
    if (loadingMoreStylist) return;
    setLoadingMoreStylist(true);
    InteractionManager.runAfterInteractions(() => {
      let added = 0;
      const step = () => {
        setStylistOutfitCount(c => c + 1);
        added += 1;
        if (added < STYLIST_INCREMENT) {
          // setTimeout au lieu de requestAnimationFrame : laisse react finir
          // le commit + paint avant la prochaine montée d'une carte.
          setTimeout(step, 50);
        } else {
          setLoadingMoreStylist(false);
        }
      };
      step();
    });
  }, [loadingMoreStylist]);
  // v1.13.19 — FIX freeze « styliste-compute » (diag breadcrumb) : on diffère le
  // MONTAGE des cartes Sélection styliste APRÈS le 1er paint (InteractionManager).
  // Au démarrage à froid (réinstall = cache image vide), monter ces cartes (beaucoup
  // de SmartImage + sous-vues) dans le même commit que le reste de Home figeait le
  // thread JS au lancement. Le skeleton existant s'affiche pendant ce court délai.
  // N'affecte QUE le rendu (pas la donnée ni les memos).
  const [stylistCardsReady, setStylistCardsReady] = useState(false);
  // v1.13.36 — Montage PROGRESSIF des cartes styliste initiales (1 par frame).
  // Monter les 3 cartes d'un coup décodait plusieurs images pleine résolution
  // simultanément sur cache FROID (1ère installation) → pic mémoire iOS → gel
  // permanent au démarrage sur l'Accueil. On les monte une à une pour étaler la
  // charge. Une fois les 3 initiales montées, on lève le plafond (9999) pour que
  // « Voir plus » fonctionne normalement.
  const [stylistMountCount, setStylistMountCount] = useState(0);
  // v1.13.50 — Rendu progressif GÉNÉRAL : après le 1er paint (runAfterInteractions),
  // on monte les cartes UNE PAR FRAME (+1 / ~90ms) jusqu'au nombre demandé
  // (stylistOutfitCount). S'applique aussi aux « Voir +5 » : les nouvelles cartes
  // se montent progressivement au lieu d'un coup → pas de pic mémoire/décodage
  // image, thread JS libre (démarrage fluide mobile + web).
  React.useEffect(() => {
    const h = InteractionManager.runAfterInteractions(() => setStylistCardsReady(true));
    return () => { try { h && h.cancel && h.cancel(); } catch {} };
  }, []);
  React.useEffect(() => {
    if (!stylistCardsReady) return;
    if (stylistMountCount >= stylistOutfitCount) return;
    const t = setTimeout(() => setStylistMountCount(c => c + 1), 90);
    return () => clearTimeout(t);
  }, [stylistCardsReady, stylistMountCount, stylistOutfitCount]);
  const [trendingExpanded, setTrendingExpanded] = useState(false);
  // v1.12.96 — toggle "Voir plus" pour révéler accessoires/bijoux dans l'outfit Home
  const [homeOutfitShowAll, setHomeOutfitShowAll] = useState(false);
  // v1.12.97 — Reroll : seed initialisé aléatoirement puis incrémenté de 1 par clic.
  // Le modulo top.length garantit qu'à chaque clic on tombe sur un index différent
  // (avant : Math.random() dans useMemo pouvait répéter le même outfit ~20% du temps).
  const [homeOutfitReroll, setHomeOutfitReroll] = useState(() => Math.floor(Math.random() * 1000));
  // v1.12.224+225 — Mes Looks (cache) + recently_worn depuis calendrier
  const [userCreatedLooks, setUserCreatedLooks] = useState([]);
  // v1.14.5 — Looks IA du dressing (backend) mis en cache pour alimenter la Tenue
  //   du jour (en plus de l'OutfitEngine local et de Mes Looks).
  const [homeAiLooks, setHomeAiLooks] = useState([]);
  const [recentlyWornLookIds, setRecentlyWornLookIds] = useState([]);
  React.useEffect(() => {
    const uid = user?.id || 'guest';
    AsyncStorage.getItem(`outfit_created_looks_${uid}`).then(raw => {
      if (raw) { try { setUserCreatedLooks(JSON.parse(raw) || []); } catch {} }
    }).catch(() => {});
    // Source de vérité : entrées calendrier des 7 derniers jours
    if (!user) return;
    (async () => {
      try {
        const { getRecentlyWornLookIds } = require('../services/CalendarService');
        const token = getValidToken ? await getValidToken() : user.accessToken;
        if (!token) return;
        const ids = await getRecentlyWornLookIds({ days: 7, authToken: token });
        setRecentlyWornLookIds(ids || []);
      } catch (e) {
        console.warn('[Home] recently_worn fetch failed:', e?.message);
      }
    })();
  }, [user?.id]);

  // ═══ Daily prefs courantes (pour filtrage doux du bloc Tendances) ═══
  // Chargées au mount + mises à jour via event 'daily_prefs_changed'.
  // Si prefs.categories est non vide, on PRIORISE ces catégories dans Tendances
  // (les autres catégories restent affichées, mais en complément).
  const [currentDailyPrefs, setCurrentDailyPrefs] = useState(null);
  const [outfits,       setOutfits]       = useState([]);
  const [temperature,   setTemperature]   = useState(18);
  const [locationCity,  setLocationCity]  = useState('');
  const [weatherDesc,   setWeatherDesc]   = useState('');
  // v1.13.x — on stocke code + jour/nuit pour : (a) icône lune la nuit, (b) recalcul
  // du libellé quand la langue change (sinon "Sunny" reste figé en EN si calculé
  // avant la résolution de la langue).
  const [weatherCode,  setWeatherCode]  = useState(null);
  const [weatherIsDay, setWeatherIsDay] = useState(true);
  React.useEffect(() => {
    setWeatherDesc(weatherCode == null ? '' : getWeatherDesc(weatherCode, weatherIsDay));
  }, [weatherCode, weatherIsDay, lang]);
  const [locationReady, setLocationReady] = useState(false);
  const wardrobeRef = useRef([]);
  const catalogRef  = useRef([]);
  // v1.12.981 — top-up styliste : params du dernier fetch + dernier limit demandé,
  // pour redemander un pool plus large au backend si les filtres font tomber
  // l'affichage sous le minimum (cf. effet plus bas).
  const stylisteParamsRef = useRef(null);
  // v1.13.97 — fetch initial 60 → 200 : le backend a 292-600 tenues par requête,
  //   mais on n'en récupérait que 60 → après filtres (images Coutr mortes, temp)
  //   il n'en restait ~9-18. On demande donc BEAUCOUP plus au backend d'entrée.
  const stylistLimitRef   = useRef(200);

  // v1.12.617 — States utilisés par le useEffect shadow fetch ci-dessous.
  // DOIVENT être déclarés AVANT le useEffect sinon TDZ "Cannot access 'X'
  // before initialization" au mount sur web (minifier strict).
  // Ces states sont aussi utilisés dans le rendu/d'autres effects plus bas.
  const [stylistTimedOut, setStylistTimedOut] = React.useState(false);
  // v1.13.39 — Flag "fetch styliste en cours" : permet d'afficher un indicateur
  // de mise à jour même quand du contenu (cache/générique) est déjà affiché.
  const [stylistFetching, setStylistFetching] = React.useState(false);
  const [stylistOutfitsGen, setStylistOutfitsGen] = React.useState([]);
  // v1.13.51 — Anti-FREEZE pendant le chargement : le pool styliste peut monter à
  //   400 (top-up) et son filtrage/tri/pré-calcul est lourd et SYNCHRONE. On le
  //   passe en valeur DÉFÉRÉE (priorité basse, interruptible par React 19) → le
  //   thread JS reste libre pour l'UI urgente (spinner, scroll, FAB, navigation)
  //   pendant que la sélection se calcule en arrière-plan.
  const deferredStylistOutfitsGen = React.useDeferredValue(stylistOutfitsGen);
  // true tant que le calcul déféré n'a pas rattrapé la dernière donnée reçue.
  const stylistDeferring = deferredStylistOutfitsGen !== stylistOutfitsGen;

  // ═══ v1.12.388 — Shadow fetch backend AWS Looks (mytenue-aws-cdk) ═══
  // Mode SHADOW : on appelle l'API AWS en parallèle du calcul local.
  // L'output est LOGGUÉ uniquement (pas affiché à l'user) tant que
  // USE_BACKEND_LOOKS est `false`. Permet de valider que le backend est
  // sain avant de basculer pour de vrai.
  React.useEffect(() => {
    console.log('[LooksAPI] mount effect — user.id =', user?.id || 'null', '| gender =', user?.gender || 'null');
    lf('home-styliste-effect (mount/fetch)');
    let cancelled = false;
    // v1.12.616 — Reset le timeout flag à chaque re-fetch (changement user/prefs)
    setStylistTimedOut(false);
    setStylistFetching(true); // v1.13.39 — fetch styliste en cours
    // v1.12.873 — Timeout 30s → 120s (worker peut prendre 60-90s pour 1er compute
    // d'un pk spécifique avec catalog refreshé)
    const timeoutId = setTimeout(() => {
      if (!cancelled) {
        setStylistTimedOut(true);
      }
    }, 120000);
    (async () => {
      try {
        // Toujours tenter le styliste (cache partagé, JWT optionnel)
        const t1 = Date.now();
        // v1.12.927 — Si user authentifié mais profil pas encore hydraté → SKIP
        // (sinon on fetche avec fallback F → user homme voit des outfits femme
        // jusqu'à ce que profileHydrated arrive et déclenche un re-fetch).
        // Les deps user?.gender + user?.profileHydrated re-firent l'effet.
        if (user?.id && !user?.profileHydrated) {
          console.log('[Home] stylist : skip fetch (profil pas hydraté, gender encore inconnu)');
          return;
        }
        // v1.12.882 — Resolve gender avec fallback F si user connecté mais pas onboarded
        // (au lieu de skip qui rendait Selection styliste vide pour ces users)
        let resolvedGender = user?.gender || user?.profile?.gender || currentDailyPrefs?.gender;
        if (resolvedGender !== 'M' && resolvedGender !== 'F') {
          resolvedGender = 'F'; // Default au pool le plus grand pour ne pas bloquer le user
          console.warn('[Home] stylist gender inconnu → fallback F (user.id=', user?.id, ', profileHydrated=', user?.profileHydrated, ')');
        }
        console.log('[Home] stylist resolvedGender =', resolvedGender, '| user.gender =', user?.gender);
        // v1.13.37 — DÉFAUTS Sélection styliste quand l'user n'a pas de prefs :
        //   style = chic, occasion = soirée (robes priorisées via le tri ci-dessous).
        //   Dès que l'user choisit ses propres prefs, elles priment.
        const _effOccasion = currentDailyPrefs?.occasion || 'soirée';
        const _effStyles   = (currentDailyPrefs?.styles && currentDailyPrefs.styles.length > 0)
          ? currentDailyPrefs.styles
          : ['chic'];
        // v1.13.42 — L'user a-t-il RÉELLEMENT choisi des prefs ? (sinon = cas "skip")
        const _userChoseOccasion = !!currentDailyPrefs?.occasion;
        const _userChoseStyles   = Array.isArray(currentDailyPrefs?.styles) && currentDailyPrefs.styles.length > 0;
        const _userChoseCats     = Array.isArray(currentDailyPrefs?.categories) && currentDailyPrefs.categories.length > 0;
        const _isSkip = !_userChoseOccasion && !_userChoseStyles && !_userChoseCats;
        // v1.13.48 — On n'envoie PAS le style au backend (le pool par style est trop
        //   petit) ; le tri style se fait côté client.
        // v1.13.54 — Le pool backend "soirée" SANS catégorie ne contient plus de
        //   robes (composé de tenues haut+bas). Or le DÉFAUT prévu = robe de soirée.
        //   → au SKIP on demande explicitement categories=Robes (le backend a bien
        //   60 looks robe+soirée précalculés). Si l'user choisit ses catégories,
        //   on envoie les siennes ; s'il choisit occasion/style seuls, on n'impose
        //   pas de catégorie (pool large).
        const stylisteParams = {
          gender: resolvedGender,
          occasion: _userChoseOccasion ? currentDailyPrefs.occasion : 'soirée',
          // v1.13.70 — au SKIP : Robes par défaut UNIQUEMENT pour les femmes
          // (un homme n'a pas de robes → categories=Robes renvoyait ~0 tenue).
          categories: _userChoseCats ? currentDailyPrefs.categories : (_isSkip && resolvedGender !== 'M' ? ['Robes'] : undefined),
          // styles volontairement omis → pool large ; tri style fait côté client
          priceBucket: currentDailyPrefs?.budget,
        };
        // v1.12.981 — mémorise pour le top-up (redemander +large si <50 après filtres)
        stylisteParamsRef.current = stylisteParams;
        stylistLimitRef.current = 200; // v1.13.97 — fetch initial élargi (cf. useRef)
        // v1.12.972 — Cache local "stale-while-revalidate" : on affiche
        // INSTANTANÉMENT le dernier résultat styliste (évite l'écran skeleton
        // pendant le cold compute backend), puis on rafraîchit en arrière-plan.
        const _effCatsKey = _userChoseCats ? currentDailyPrefs.categories.join('-') : (_isSkip ? 'Robes' : 'any');
        const _stylistCacheKey = `stylist_cache_v3_${resolvedGender}_${_effOccasion || 'any'}_${_effCatsKey}_${(_effStyles || []).join('-') || 'any'}_${currentDailyPrefs?.budget || 'any'}`;
        // v1.13.35 — Clé GÉNÉRIQUE par genre : dernière sélection styliste de
        // N'IMPORTE quel combo. Sert de fallback instantané au 1er démarrage
        // (combo exact pas encore en cache) pour ne jamais montrer un écran qui
        // mouline en attendant le compute backend.
        const _stylistGenericKey = `stylist_cache_v2_${resolvedGender}_GENERIC`;
        // v1.12.974 — Signature du dressing. Si l'user a ajouté/retiré un article,
        // la sélection doit être RECALCULÉE, pas réaffichée depuis un cache périmé.
        // (Le cache n'est utilisé que si le dressing est identique à sa génération.)
        const _wardrobeSig = (wardrobe || []).length;
        try {
          let _shown = false;
          const _cachedRaw = await AsyncStorage.getItem(_stylistCacheKey);
          if (_cachedRaw && !cancelled) {
            const _cached = JSON.parse(_cachedRaw);
            // Rétro-compat : ancien format = array brut ; nouveau = { wardrobeSig, looks }
            const _looks = Array.isArray(_cached) ? _cached : _cached?.looks;
            const _cachedSig = Array.isArray(_cached) ? undefined : _cached?.wardrobeSig;
            if (Array.isArray(_looks) && _looks.length > 0 && _cachedSig === _wardrobeSig) {
              console.log('[LooksAPI] stylist local cache hit (dressing inchangé) →', _looks.length);
              setStylistOutfitsGen(_looks);
              _shown = true;
            } else if (_cachedSig !== _wardrobeSig) {
              console.log('[LooksAPI] stylist cache ignoré — dressing modifié → recalcul frais');
            }
          }
          // v1.13.35 — Fallback GÉNÉRIQUE : si le combo exact n'a rien d'affichable,
          // montrer la dernière sélection styliste de n'importe quel combo (même
          // genre) → affichage instantané au 1er démarrage. Sera remplacée par le
          // fetch frais ci-dessous dès qu'il répond.
          if (!_shown && !cancelled) {
            const _gRaw = await AsyncStorage.getItem(_stylistGenericKey);
            if (_gRaw) {
              const _g = JSON.parse(_gRaw);
              const _gLooks = Array.isArray(_g) ? _g : _g?.looks;
              if (Array.isArray(_gLooks) && _gLooks.length > 0 && !cancelled) {
                console.log('[LooksAPI] stylist GENERIC fallback →', _gLooks.length);
                setStylistOutfitsGen(_gLooks);
              }
            }
          }
        } catch (_) {}
        // v1.12.972 — limit 400 → 60 (1er affichage rapide, le reste via "Voir plus")
        //   + retries réduits (20×5s → 8×3s) : le cache local couvre l'attente,
        //   et le cron de pré-calcul backend gère le cold compute.
        const stylisteResult = await fetchLooksWithRetry(
          () => fetchLooksStyliste(stylisteParams, { limit: 200 }),
          { maxRetries: 8, delayMs: 3000, backoff: true }
        );
        // v1.12.623 — Ne PAS skip si cancelled : si currentDailyPrefs change
        // pendant le fetch, on perdait les 425 looks reçus et l'écran restait
        // vide → message timeout 30s. Maintenant on wire toujours (un fetch
        // ultérieur écrasera si besoin).
        console.log('[LooksAPI] shadow Styliste fetch:', {
          looks: stylisteResult.looks?.length || 0,
          cache_hit: stylisteResult.cache_hit,
          is_stale: stylisteResult.is_stale,
          cancelled,
          ms: Date.now() - t1,
        });

        // v1.12.389 — WIRE Sélection styliste : si flag on + looks reçus
        if (USE_BACKEND_LOOKS && Array.isArray(stylisteResult.looks) && stylisteResult.looks.length > 0) {
          const adapted = stylisteResult.looks.map((look) => adaptApiLookToStylistOutfit(look));
          console.log('[LooksAPI] wire stylistOutfitsGen ← backend (', adapted.length, 'looks)');
          setStylistOutfitsGen(adapted);
          // v1.12.972 — mémorise pour affichage instantané au prochain open
          safeCacheStylist(_stylistCacheKey, _wardrobeSig, adapted);
          safeCacheStylist(_stylistGenericKey, _wardrobeSig, adapted); // v1.13.35 fallback générique
        } else if (USE_BACKEND_LOOKS && stylisteResult.computing && !cancelled) {
          // v1.12.494 — Worker n'a pas fini après 75s : retry silent en arrière-plan
          // toutes les 20s jusqu'à 5 fois (~100s supplémentaires). Évite que l'écran
          // reste vide indéfiniment pour les pks qui prennent du temps à compute.
          console.log('[LooksAPI] Styliste still computing — schedule background retry');
          let bgAttempts = 0;
          const bgRetry = setInterval(async () => {
            if (cancelled || bgAttempts >= 5) {
              clearInterval(bgRetry);
              return;
            }
            bgAttempts++;
            try {
              const r = await fetchLooksStyliste(stylisteParams, { limit: 200 });
              if (cancelled) return;
              console.log(`[LooksAPI] bg retry ${bgAttempts}/5 → ${r.looks?.length || 0} looks (computing=${r.computing})`);
              if (Array.isArray(r.looks) && r.looks.length > 0) {
                const adapted = r.looks.map((look) => adaptApiLookToStylistOutfit(look));
                setStylistOutfitsGen(adapted);
                safeCacheStylist(_stylistCacheKey, _wardrobeSig, adapted);
          safeCacheStylist(_stylistGenericKey, _wardrobeSig, adapted); // v1.13.35 fallback générique
                clearInterval(bgRetry);
              }
            } catch (e) { console.warn('[LooksAPI] bg retry failed:', e?.message); }
          }, 20000);
        }

        // AI fetch uniquement si user logged in
        if (!user?.id) {
          console.log('[LooksAPI] shadow AI fetch skipped (no user)');
          return;
        }
        const t0 = Date.now();
        const aiResult = await fetchLooksWithRetry(() => fetchLooksAI({ limit: 12 }), { maxRetries: 2 });
        if (cancelled) return;
        console.log('[LooksAPI] shadow AI fetch:', {
          looks: aiResult.looks?.length || 0,
          cache_hit: aiResult.cache_hit,
          is_stale: aiResult.is_stale,
          ms: Date.now() - t0,
        });
        // v1.14.5 — STOCKER les Looks IA (avant : résultat jeté) pour alimenter la
        //   Tenue du jour. On reconstruit les items depuis le dressing local via
        //   adaptApiLookToStylistOutfit (le cache backend ne stocke que les itemIds).
        try {
          if (Array.isArray(aiResult.looks) && aiResult.looks.length > 0) {
            const _wbById = new Map((wardrobe || []).map(it => [String(it.id), it]));
            const _adaptedAi = aiResult.looks
              .map(l => adaptApiLookToStylistOutfit(l, _wbById))
              .filter(o => o && Array.isArray(o.items) && o.items.length >= 2);
            if (!cancelled && _adaptedAi.length > 0) setHomeAiLooks(_adaptedAi);
          }
        } catch (e) { console.warn('[Home] adapt AI looks:', e?.message); }
      } catch (e) {
        console.warn('[LooksAPI] shadow fetch failed:', e?.message, e);
      } finally {
        // v1.13.39 — fin du cycle de fetch styliste → on masque l'indicateur.
        if (!cancelled) setStylistFetching(false);
      }
    })();
    return () => { cancelled = true; clearTimeout(timeoutId); };
    // v1.12.899 — Perf : deps spécifiques au lieu de currentDailyPrefs entier
    // (évite re-fire de l'effet à chaque re-render du parent même si prefs inchangées)
  }, [user?.id, user?.gender, user?.profileHydrated,
      currentDailyPrefs?.occasion,
      currentDailyPrefs?.budget,
      currentDailyPrefs?.styles?.join?.(',') || '',
      currentDailyPrefs?.categories?.join?.(',') || '',
      // v1.12.974 — recompute la sélection quand le dressing change (ajout/retrait
      // d'article) au lieu d'afficher le cache figé.
      wardrobe.length]);

  // v1.12.616 — Si des looks arrivent après le timeout, on annule le flag
  // (l'user verra les looks au lieu du message empty).
  React.useEffect(() => {
    if (stylistOutfitsGen.length > 0 && stylistTimedOut) {
      setStylistTimedOut(false);
    }
  }, [stylistOutfitsGen.length, stylistTimedOut]);
  const tempRef     = useRef(18);
  const [catalogItems,  setCatalogItems]  = useState([]);
  const [loadingCat,    setLoadingCat]    = useState(true);
  // v1.10.81 : selectedCat supprimé (plus de filtre catégorie sur l'accueil)
  const [refreshing,    setRefreshing]    = useState(false);
  // ═══ v1.9.28 : State pour la banque IA (500 combos par style) ═══
  // Chargée 1× au montage (cache 7 jours côté StyleBankService).
  // Utilisée par stylistOutfitsGen pour matérialiser des outfits du catalogue
  // basés sur les inspirations IA (au lieu de simple combinatoire).
  const [styleBank, setStyleBank] = useState(null);
  const [colorBank, setColorBank] = useState(null); // Phase C.3 v1.12.61
  const [materialBank, setMaterialBank] = useState(null); // Phase D.3 v1.12.69
  // ═══ v1.10.0 : State pour le modal d'essayage virtuel ═══
  const [tryOnOutfit, setTryOnOutfit] = useState(null); // outfit en cours d'essayage
  const [tryOnAuthToken, setTryOnAuthToken] = useState(null);
  // ═══ v1.10.9 : State pour le modal de préférences (accès depuis l'accueil) ═══
  const [showPrefsModal, setShowPrefsModal] = useState(false);
  // v1.12.250 — Raccourci ajout calendrier depuis Tenue du jour card
  const [calendarShortcut, setCalendarShortcut] = useState(null);

  // v1.12.220 — Reset à l'état initial quand l'utilisateur quitte le Catalogue
  React.useEffect(() => {
    const sub = DeviceEventEmitter.addListener('tabs_reset_to_default', () => {
      try { scrollRef.current?.scrollTo?.({ y: 0, animated: false }); } catch {}
      setSelectedProduct(null);
      setBoughtProduct(null);
      setShowPrefsModal(false);
    });
    return () => sub.remove();
  }, []);

  // v1.12.545 — Tap sur le tab Home alors qu'on est déjà dessus → scroll top
  React.useEffect(() => {
    const parent = navigation?.getParent?.();
    if (!parent?.addListener) return;
    const unsub = parent.addListener('tabPress', () => {
      try { scrollRef.current?.scrollTo?.({ x: 0, y: 0, animated: true }); } catch {}
    });
    return unsub;
  }, [navigation]);

  // Helper : ouvrir le modal Try-On en récupérant le token JWT
  // v1.12.122 : gate les free users -> popup d'abonnement directe.
  //             gate les non-authentifies -> popup connexion requise.
  const openTryOn = useCallback(async (outfit) => {
    if (!outfit?.items?.length) return;
    if (!user) {
      showAppAlert(t('login_required'), t('login_required_for_tryon'));
      return;
    }
    // v1.12.620 — Plus de shortcut "if (tier === 'free') reject". Le quota
    // backend tient compte du bonus addons (onboarding + packs achetés), donc
    // un Free avec un free_onboarding_tryon doit pouvoir ouvrir la modale.
    // Le pre-check fetchTryOnUsage ci-dessous fait l'arbitrage.
    try {
      const token = getValidToken ? await getValidToken() : null;
      // v1.12.491 — Pre-check quota AVANT ouverture modal pour tous les tiers.
      // Évite ouverture+close en cascade → crash SIGABRT iOS Animated listeners.
      if (token) {
        try {
          const { fetchTryOnUsage } = require('../services/TryOnService');
          const u = await fetchTryOnUsage(token);
          if (u && (u.limit === 0 || u.count >= u.limit)) {
            DeviceEventEmitter.emit('tryon_limit_reached', {
              tier: u.tier, limit: u.limit, current: u.count,
              isPaidTier: u.isPaidTier, nextResetDate: u.nextResetDate,
            });
            return;
          }
        } catch (e) { console.warn('[Home] tryon quota check failed:', e.message); }
      }
      setTryOnAuthToken(token);
      setTryOnOutfit(outfit);
    } catch (e) {
      console.warn('[Home] openTryOn auth failed:', e.message);
    }
  }, [getValidToken, user]);

  // v1.12.121 — flow simplifie : "Essayer cette tenue" ouvre directement
  // le try-on sur mannequin (Vertex). Plus de popup choix flat-lay/mannequin.
  const showTryOnChoice = useCallback((outfit) => {
    if (!outfit?.items?.length) return;
    openTryOn(outfit);
  }, [openTryOn]);


  // Pull-to-refresh : recharge météo + dressing + catalogue
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const { getCurrentWeather, invalidateWeatherCache } = await import('../services/WeatherService');
      await invalidateWeatherCache();
      const weather = await getCurrentWeather(true);
      if (weather) {
        setTemperature(weather.temperature);
        setLocationCity(weather.city || '');
        setWeatherCode(weather.weatherCode); setWeatherIsDay(weather.isDay !== false);
        // v1.13.x — GA city PRÉCIS : ville résolue par GPS (reverse-geocode), pas
        // par IP (les IP mobiles FR géolocalisent toutes vers "Paris" = POP opérateur).
        if (weather.city) { try { const { setUserGeo } = require('../services/AnalyticsService'); setUserGeo({ city: weather.city, region: weather.region, country: weather.country }); } catch (_) {} }
      }
    } catch {}
    // Emit event for other hooks to refresh
    try { DeviceEventEmitter.emit('outfit:refresh-home'); } catch {}
    setTimeout(() => setRefreshing(false), 600);
  }, []);

  const hour   = new Date().getHours();
  const greet  = hour < 12 ? t('good_morning') : hour < 18 ? t('good_afternoon') : t('good_evening');
  // Affichage propre : si user a un firstName/lastName, on les utilise.
  // Si pas de nom mais juste un email (cas Apple relay), on affiche "Invité"
  // plutôt que l'alias xxx@privaterelay.appleid.com qui n'est pas humain.
  const name   = getFirstName(user, t('guest'));
  // v1.12.381 — résolution gender en 3 cas :
  //   A) user connecté avec gender hydraté → utiliser
  //   B) user connecté mais gender pas encore résolu → null (skip trending)
  //   C) GUEST (non connecté) → default 'F' (audience par défaut, browse anonyme)
  let gender = user?.gender || null;
  if (!gender && typeof window !== 'undefined') {
    try { gender = window.localStorage?.getItem('mt_user_gender') || null; } catch {}
  }
  // Guest : fallback F pour browse anonyme (cohérent avec catalog/looks publics)
  if (!gender && !user) {
    gender = 'F';
  }
  // Sync localStorage quand user.gender change
  React.useEffect(() => {
    if (user?.gender && typeof window !== 'undefined') {
      try { window.localStorage?.setItem('mt_user_gender', user.gender); } catch {}
    }
  }, [user?.gender]);

  // Stats dressing
  const dressingCount = wardrobe.length;
  const dressingCats  = [...new Set(wardrobe.map(i => i.category))].length;
  // v1.12.91 — affiche les 3 derniers articles ajoutés (slice 5 → 3)
  const recentItems   = wardrobe.slice(0, 3);

  // Produits filtrés
  // Filtrer par catégorie ET par pertinence météo
  const tempFilteredItems = catalogItems.filter(p => {
    // Filtrer par température : exclure manteaux/doudounes si chaud, maillots si froid
    const cat  = (p.category || '').toLowerCase();
    const name = (p.name     || '').toLowerCase();
    if (temperature >= 22) {
      // Chaud : pas de manteaux, doudounes, pulls épais
      if (name.includes('manteau') || name.includes('doudoune') || name.includes('parka')) return false;
      if (name.includes('pull épais') || name.includes('laine épaisse')) return false;
    }
    if (temperature <= 10) {
      // Froid : pas de robes légères, débardeurs, shorts
      if (name.includes('débardeur') || name.includes('short') || name.includes('bikini')) return false;
    }
    // ═══ FILTRE BUDGET : appliquer la fourchette de prix choisie dans préférences ═══
    // Bornes définies dans DailyPreferencesModal : budget < 50€, mid-range 50-150€,
    // premium 150-500€, luxury > 500€, null = illimité (pas de filtre).
    // Si l'user choisit "Économique" → on n'affiche QUE les articles < 50€ partout
    // sur l'accueil, pas seulement en tri prioritaire.
    const budget = currentDailyPrefs?.budget;
    if (budget) {
      const price = Number(p.price) || 0;
      if (budget === 'budget'    && price >= 50)               return false;
      if (budget === 'mid-range' && (price < 50 || price >= 150))  return false;
      if (budget === 'premium'   && (price < 150 || price >= 500)) return false;
      if (budget === 'luxury'    && price < 500)               return false;
    }
    return true;
  });
  // ═══ PERSONNALISATION : tri selon préférences du jour + âge + analyse IA ═══
  // Trie le catalogue pour que les produits qui matchent le mieux le profil
  // (styles cochés, occasion, budget, catégories cochées, tranche d'âge)
  // remontent en premier. Utilise ai_metadata.styleScores/occasions/etc.
  // Si ni prefs ni âge, retourne la liste inchangée → comportement actuel préservé.
  const userAge = React.useMemo(() => computeAge(user?.birthDate), [user?.birthDate]);
  const personalizedItems = React.useMemo(
    () => rankByPersonalization(tempFilteredItems, currentDailyPrefs, userAge),
    [tempFilteredItems, currentDailyPrefs, userAge]
  );
  // v1.10.81 : filteredItems = personalizedItems (plus de filtre par selectedCat
  // car la section "Catalogue dynamique" a été supprimée de l'accueil)
  const filteredItems = personalizedItems;

  // Suggestions basées sur le dressing (catégories manquantes)
  const ownedCats   = new Set(wardrobe.map(i => i.category));
  const missingCats = CATS.filter(c => !ownedCats.has(c));

  // ═══ v1.12.224 — Tenue du Jour via OutfitOfTheDayService ═══
  // Pipeline :
  //   1. OutfitEngine génère des outfits candidats depuis le dressing
  //   2. On dédupe par signature CORE (Robe/Haut+Bas/Chaussures)
  //   3. On normalise + on injecte les userCreatedLooks (Mes Looks)
  //   4. OutfitOfTheDayService score / range / explique selon les prefs + météo
  //   5. Reroll cycle dans le top-3 (best + alternatives) pour varier les recos
  const homeRecommendation = React.useMemo(() => {
    if (!wardrobe || wardrobe.length === 0) return null;
    try {
      const tempC = temperature ?? 18;
      const prefs = currentDailyPrefs || {};
      const targetStyle = (prefs.styles && prefs.styles[0])
        ? String(prefs.styles[0]).toLowerCase()
        : (prefs.occasion ? String(prefs.occasion).toLowerCase() : null);
      const targetOccasion = prefs.occasion ? String(prefs.occasion).toLowerCase() : null;

      const seedTags = new Set();
      for (const it of wardrobe) {
        const tags = Array.isArray(it.ai_metadata?.styleTags) ? it.ai_metadata.styleTags : [];
        for (const tag of tags) if (tag) seedTags.add(String(tag).toLowerCase());
      }
      if (targetOccasion) seedTags.add(targetOccasion);
      const candidateTags = Array.from(seedTags).slice(0, 30);

      // Étape 1 : avec prefs (cible style+occasion)
      let generated = OutfitEngineGenerate({
        wardrobe, tempC, maxOutfits: 200, candidateTags, guide: null,
        targetStyle, targetOccasion, colorBank, materialBank,
      });
      let approxPrefs = false;
      if ((!generated || generated.length === 0) && (targetStyle || targetOccasion)) {
        generated = OutfitEngineGenerate({
          wardrobe, tempC, maxOutfits: 200, candidateTags, guide: null,
          targetStyle: null, targetOccasion: null, colorBank, materialBank,
        });
        approxPrefs = true;
      }
      // v1.14.5 — On NE court-circuite PLUS ici si l'OutfitEngine local ne produit
      //   rien : les LOOKS IA (backend, déjà adaptés aux préférences) et MES LOOKS
      //   pourront alimenter la Tenue du jour (voir Étape 3 plus bas). _needsMore
      //   ne sera renvoyé QUE si AUCUNE source (engine + Looks IA + Mes Looks) n'a
      //   de tenue. (Avant, un engine vide bloquait tout sur "Pas assez d'articles"
      //   même quand Looks IA en avait généré.)
      if (!generated) generated = [];

      // Étape 2 : dédup par CORE
      const CORE_CATS = new Set(['Robes', 'Hauts', 'Bas', 'Chaussures']);
      const itemKey = (it) => it?.id || (it?.name ? `${it.category}|${it.name}` : '');
      const bestPerCore = new Map();
      for (const o of generated) {
        const coreSig = (o.items || [])
          .filter(it => CORE_CATS.has(it?.category))
          .map(itemKey).filter(Boolean).sort().join('||');
        if (!coreSig) continue;
        const existing = bestPerCore.get(coreSig);
        if (!existing || (o.score || 0) > (existing.score || 0)) bestPerCore.set(coreSig, o);
      }
      // v1.12.435 — Filtre Tenue du jour par préférences (style + occasion + categories,
      // PAS budget car c'est le dressing). Si pas de prefs, garde tout (rotation aléatoire).
      const allEngine = Array.from(bestPerCore.values())
        .sort((a, b) => (b.score || 0) - (a.score || 0));

      const dailyStyleLower = currentDailyPrefs?.styles?.[0] ? String(currentDailyPrefs.styles[0]).toLowerCase() : null;
      const dailyOccLower = currentDailyPrefs?.occasion ? String(currentDailyPrefs.occasion).toLowerCase() : null;
      const dailyCats = Array.isArray(currentDailyPrefs?.categories) && currentDailyPrefs.categories.length > 0
        ? new Set(currentDailyPrefs.categories) : null;

      const STYLE_KEYS = {
        chic:['chic','élégant','elegant'], casual:['casual','décontracté'],
        sport:['sport','sportif'], streetwear:['streetwear','street','urban'],
        minimaliste:['minimaliste','minimal'], romantique:['romantique','féminin'],
        bohème:['bohème','boheme'], workwear:['workwear','business'],
        preppy:['preppy','workwear'], edgy:['edgy','streetwear'], vintage:['vintage'],
      };
      const OCC_KEYS = {
        weekend:['weekend','quotidien'], casual:['quotidien','weekend'],
        bureau:['bureau'], 'soirée':['soirée','cocktail'], soiree:['soirée','cocktail'],
        cocktail:['cocktail','soirée'], date:['date','cocktail'],
        sport:['sport'], 'cérémonie':['cérémonie','cocktail'],
        plage:['vacances'], voyage:['vacances','weekend'],
      };

      const scoreOutfitForPrefs = (o) => {
        const items = (o.items || []);
        const CORE = new Set(['Robes','Hauts','Bas','Vestes']);
        const core = items.filter(it => CORE.has(it?.category));
        if (core.length === 0) return 0.5;
        let total = 0, weight = 0;
        for (const it of core) {
          const ai = it?.ai_metadata || {};
          if (dailyStyleLower) {
            const keys = STYLE_KEYS[dailyStyleLower] || [dailyStyleLower];
            const ss = ai.styleScores || {};
            const best = Math.max(...keys.map(k => ss[k] || 0), 0);
            total += best / 100; weight += 1;
          }
          if (dailyOccLower) {
            const keys = OCC_KEYS[dailyOccLower] || [dailyOccLower];
            const occ = ai.occasions || {};
            const best = Math.max(...keys.map(k => occ[k] || 0), 0);
            total += best / 100; weight += 1;
          }
        }
        return weight > 0 ? total / weight : 0.5;
      };

      // Hard filter categories (chaque cat demandée doit être présente)
      let candidates = allEngine;
      if (dailyCats) {
        const matching = candidates.filter(o => {
          const cats = new Set((o.items || []).map(it => it?.category));
          for (const wanted of dailyCats) if (!cats.has(wanted)) return false;
          return true;
        });
        if (matching.length > 0) candidates = matching;
      }
      // Soft sort par pertinence prefs si style/occasion sélectionnés
      if (dailyStyleLower || dailyOccLower) {
        candidates = [...candidates]
          .map(o => ({ o, rel: scoreOutfitForPrefs(o) }))
          .sort((a, b) => {
            // Bucket relevance par 0.1 + score original tiebreaker
            const aB = Math.floor(a.rel * 10), bB = Math.floor(b.rel * 10);
            if (aB !== bB) return bB - aB;
            return (b.o.score || 0) - (a.o.score || 0);
          })
          .map(x => x.o);
      }
      const engineOutfits = candidates.slice(0, 8); // top 8 filtrés/triés

      // v1.12.268 — Étape 3 : normaliser + injecter Mes Looks (AVANT garde _needsMore)
      // Si engine ne peut rien générer mais user a des looks sauvegardés, on les utilise.
      const normalizedEngine = engineOutfits.map((o, idx) =>
        normalizeLookForScoring({ ...o, id: o.id || `engine_${idx}` }, 'looks_ia')
      ).filter(Boolean);
      const normalizedMine = (userCreatedLooks || []).map(l =>
        normalizeLookForScoring(l, 'mes_looks')
      ).filter(Boolean);
      // v1.14.5 — injecter aussi les LOOKS IA du dressing (backend) comme source.
      const normalizedAiLooks = (homeAiLooks || []).map((o, idx) =>
        normalizeLookForScoring({ ...o, id: o.id || `homeai_${idx}` }, 'looks_ia')
      ).filter(Boolean);

      const available = [...normalizedEngine, ...normalizedAiLooks, ...normalizedMine];
      if (available.length === 0) return { _needsMore: true };

      // Étape 4 : appel agent
      const today = new Date();
      const result = getOutfitOfTheDay({
        action: 'GET_OUTFIT_OF_THE_DAY',
        user_id: user?.id,
        data: {
          preferences: {
            ...prefs,
            occasions: prefs.occasion ? [prefs.occasion] : (prefs.occasions || []),
          },
          context: {
            date: today.toISOString().slice(0, 10),
            day_of_week: ['dimanche','lundi','mardi','mercredi','jeudi','vendredi','samedi'][today.getDay()],
            season: prefs._season || undefined,
            weather: { temp_celsius: tempC, condition: weatherDesc || undefined },
          },
          user_gender: user?.gender || null,
          recently_worn_look_ids: recentlyWornLookIds,
          available_looks: available,
        },
      });

      if (!result?.success || !result?.data?.outfit_of_the_day) {
        // Fallback : si agent rejette, on cycle parmi les top engineOutfits
        // (8 max) via homeOutfitReroll pour que le bouton Régénérer marche.
        const pool = [...engineOutfits.slice(0, 8), ...(userCreatedLooks || []).slice(0, 4)];
        if (pool.length === 0) return { _needsMore: true };
        const idx = ((homeOutfitReroll % pool.length) + pool.length) % pool.length;
        const fallback = pool[idx];
        return { ...fallback, _approxPrefs: true, _agentResult: result };
      }

      // Étape 5 : reroll cycle parmi best + alternatives. Si agent fournit
      // 0 ou 1 alternative, on étend le pool avec les top engine outfits
      // pour que le bouton "Régénérer" change vraiment de tenue à chaque clic.
      const rankedAgent = [result.data.outfit_of_the_day, ...(result.data.alternatives || [])];
      const agentLookIds = new Set(rankedAgent.map(r => r.look_id).filter(Boolean));
      const ranked = [...rankedAgent];
      // Si on a < 5 alternatives, on étend avec les top engineOutfits (par score)
      // pour permettre une vraie rotation aléatoire au clic regenerate.
      if (rankedAgent.length < 5) {
        for (const o of engineOutfits) {
          if (agentLookIds.has(o.id)) continue;
          ranked.push({ look_id: o.id, _fromEngine: true });
          if (ranked.length >= 8) break;
        }
      }
      const idx = ((homeOutfitReroll % ranked.length) + ranked.length) % ranked.length;
      const chosen = ranked[idx];
      // Re-trouver les items source (engineOutfits, Looks IA, ou Mes Looks)
      // v1.14.6 — inclure homeAiLooks : sinon, quand la tenue choisie vient des
      //   Looks IA et que l'engine est vide, sourceLook devenait undefined
      //   (engineOutfits[0] vide) → tenue sans items → Tenue du jour cachée.
      const sourceLook =
        engineOutfits.find(o => (o.id || '') === chosen.look_id) ||
        (homeAiLooks || []).find(l => (l.id || '') === chosen.look_id) ||
        (userCreatedLooks || []).find(l => l.id === chosen.look_id) ||
        engineOutfits[0] || (homeAiLooks || [])[0] || (userCreatedLooks || [])[0] || null;
      // v1.14.6 — garde-fou : jamais de tenue SANS items (sinon crash rendu →
      //   Tenue du jour disparaît). Si aucune source exploitable → _needsMore.
      if (!sourceLook || !Array.isArray(sourceLook.items) || sourceLook.items.length === 0) {
        return { _needsMore: true };
      }

      // v1.12.243+248 — exposer les top 20 outfits (engine + Mes Looks) pour le
      // bouton Régénérer dans CalendarScreen → plus de variété (avant : 5 max).
      const topOptions = [
        ...engineOutfits.slice(0, 15).map(o => ({
          id: o.id,
          name: t('outfit_of_day_section'),
          items: (o.items || []).map(it => ({
            id: it.id, name: it.name, category: it.category,
            image_url: it.image_url || it.image,
          })),
        })),
        ...((userCreatedLooks || []).slice(0, 5).map(l => ({
          id: l.id,
          name: l.name || t('my_look'),
          items: (l.items || []).map(it => ({
            id: it.id, name: it.name, category: it.category,
            image_url: it.image_url || it.image,
          })),
        }))),
      ];

      return {
        ...sourceLook,
        _approxPrefs: approxPrefs,
        _topOptions: topOptions,
        _agent: {
          score: chosen.score,
          breakdown: chosen.score_breakdown,
          explanation: chosen.explanation || result.data.outfit_of_the_day.explanation,
          reasons: chosen.reasons || result.data.outfit_of_the_day.reasons,
          insights: result.data.insights,
          alternatives: result.data.alternatives,
          source: chosen.source,
          badges: chosen.badges || result.data.outfit_of_the_day.badges || [],
          total_price: chosen.total_price || result.data.outfit_of_the_day.total_price || 0,
          budget_status: chosen.budget_status || result.data.outfit_of_the_day.budget_status || 'no_budget',
        },
      };
    } catch (e) {
      console.warn('[Home] homeRecommendation failed:', e?.message);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // v1.12.899 — Perf : deps spécifiques au lieu de currentDailyPrefs entier
  }, [wardrobe, temperature, weatherDesc, colorBank, materialBank, homeOutfitReroll, userCreatedLooks, homeAiLooks, recentlyWornLookIds, user?.id, user?.gender,
      currentDailyPrefs?.occasion,
      currentDailyPrefs?.budget,
      currentDailyPrefs?.styles?.join?.(',') || '',
      currentDailyPrefs?.categories?.join?.(',') || '',
      currentDailyPrefs?.colors?.join?.(',') || '']);

  // Backward compat : `homeOutfit` consommé partout dans le JSX
  const homeOutfit = homeRecommendation;

  // v1.12.225 — persister la Tenue du jour pour CalendarScreen (source 'tenue_du_jour')
  React.useEffect(() => {
    if (!homeOutfit || homeOutfit._needsMore) return;
    const uid = user?.id || 'guest';
    try {
      const slim = {
        id: homeOutfit.id || `home_${Date.now()}`,
        name: homeOutfit._agent?.explanation ? t('outfit_of_day_section') : (homeOutfit.name || t('outfit_of_day_section')),
        items: (homeOutfit.items || []).map(it => ({
          id: it.id, name: it.name, category: it.category, image_url: it.image_url || it.image,
        })),
        occasion: currentDailyPrefs?.occasion || null,
      };
      AsyncStorage.setItem(`outfit_today_recommendation_${uid}`, JSON.stringify(slim)).catch(() => {});
      // v1.12.243 — top 5 options pour bouton "Régénérer" dans CalendarScreen
      const options = (homeOutfit._topOptions || []).slice(0, 20);
      if (options.length > 0) {
        AsyncStorage.setItem(`outfit_today_options_${uid}`, JSON.stringify(options)).catch(() => {});
      }
    } catch {}
  }, [homeOutfit, user?.id, currentDailyPrefs?.occasion]);

  // Géolocalisation + météo (via service centralisé WeatherService)
  useEffect(() => {
    let cancelled = false;
    async function loadLocationAndWeather() {
      try {
        const weather = await getCurrentWeather();
        if (cancelled) return;
        if (weather) {
          setTemperature(weather.temperature);
          setLocationCity(weather.city || '');
          setWeatherCode(weather.weatherCode); setWeatherIsDay(weather.isDay !== false);
        // v1.13.x — GA city PRÉCIS : ville résolue par GPS (reverse-geocode), pas
        // par IP (les IP mobiles FR géolocalisent toutes vers "Paris" = POP opérateur).
        if (weather.city) { try { const { setUserGeo } = require('../services/AnalyticsService'); setUserGeo({ city: weather.city, region: weather.region, country: weather.country }); } catch (_) {} }
        }
        setLocationReady(true);
      } catch(e) {
        if (!cancelled) {
          setTemperature(18);
          setWeatherCode(null);
          setLocationReady(true);
        }
      }
    }
    loadLocationAndWeather();
    return () => { cancelled = true; };
  }, []);

  // ═══ v1.9.28 : Charger la banque IA (500 combos par style) au montage ═══
  // La banque est mise en cache 7 jours par StyleBankService.
  // Utilisée par stylistOutfitsGen pour matérialiser des outfits du catalogue
  // basés sur les inspirations IA hebdomadaires.
  useEffect(() => {
    let cancelled = false;
    async function loadStyleBank() {
      lf('stylebank-load');
      try {
        const gender = user?.gender || 'F';
        // v1.12.686 — token refreshé (getValidToken) pour éviter 401 authorizer
        const sbToken = getValidToken ? await getValidToken().catch(() => null) : null;
        const bank = await getStyleBank({
          gender,
          temp: tempRef.current ?? 18,
          limit: 500,
          token: sbToken,
        });
        if (cancelled) return;
        if (bank?.combinations?.length > 0) {
          console.log(`[Home] StyleBank chargée : ${bank.combinations.length} inspirations (week ${bank.week_of})`);
          setStyleBank(bank);
        } else {
          console.log('[Home] StyleBank vide, fallback combinatoire pure');
        }
      } catch (e) {
        console.warn('[Home] StyleBank load failed:', e.message);
      }
    }
    loadStyleBank();
    return () => { cancelled = true; };
  }, [user?.gender]);

  // ═══ Phase C.3 v1.12.61 : Charger la color bank (recettes couleur par cat/sub-cat) ═══
  // Cache 7j AsyncStorage (= rythme du cron color-bank-cron). Consommée par
  // OutfitEngineGenerate via le facteur bankColor (10% du score cohesion).
  useEffect(() => {
    let cancelled = false;
    async function loadColorBank() {
      try {
        const bank = await getColorBank();
        if (cancelled) return;
        if (bank?.recipes?.length > 0) {
          console.log(`[Home] ColorBank chargée : ${bank.recipes.length} recettes (week ${bank.week_of})`);
          setColorBank(bank);
        }
      } catch (e) {
        console.warn('[Home] ColorBank load failed:', e.message);
      }
    }
    loadColorBank();
    return () => { cancelled = true; };
  }, []);

  // ═══ Phase D.3 v1.12.69 : Charger la material bank (recettes matières par cat/sub-cat) ═══
  useEffect(() => {
    let cancelled = false;
    async function loadMaterialBank() {
      try {
        const bank = await getMaterialBank();
        if (cancelled) return;
        if (bank?.recipes?.length > 0) {
          console.log(`[Home] MaterialBank chargée : ${bank.recipes.length} recettes (week ${bank.week_of})`);
          setMaterialBank(bank);
        }
      } catch (e) {
        console.warn('[Home] MaterialBank load failed:', e.message);
      }
    }
    loadMaterialBank();
    return () => { cancelled = true; };
  }, []);

  // Charger le dressing — externalisé pour pouvoir rafraîchir au focus
  // v1.12.122 : si user est null (deconnexion), on clear le state pour
  // ne pas afficher les donnees du compte precedent a un invite/free.
  const loadWardrobe = useCallback(async () => {
    if (!user) {
      setWardrobe([]);
      return;
    }
    try {
      const token = getValidToken ? await getValidToken().catch(() => null) : null;
      const userId = user.id || user.sub;
      if (!token || !userId) {
        console.log('[Home] loadWardrobe skipped - no token');
        setWardrobe([]);
        return;
      }
      const data = await getWardrobe(userId, token);
      const arrFull = Array.isArray(data) ? data : [];
      // v1.12.580 — V5.1-bis : exclure les items _hidden (au-delà du quota tier+bonus)
      // pour la Tenue du jour et les suggestions Home
      const arr = arrFull.filter(it => !it._hidden);
      const aiCount = arr.filter(i => i.ai_metadata?.analyzedAt).length;
      console.log(`[Home] wardrobe loaded: ${arr.length}/${arrFull.length} visible, ${aiCount} analysés IA`);
      setWardrobe(arr);
    } catch(e) { console.warn('[Home] loadWardrobe:', e.message); }
  }, [user, getValidToken]);

  useEffect(() => { loadWardrobe(); }, [loadWardrobe]);
  // Recharger quand on revient sur Home (après analyse dans le dressing)
  useFocusEffect(useCallback(() => { loadWardrobe(); }, [loadWardrobe]));

  // v1.12.386 — au retour sur Accueil, reset les sections expanded (trending + stylist)
  //   pour réafficher la version par défaut "rangée" (cf. retour user).
  useFocusEffect(useCallback(() => {
    setTrendingExpanded(false);
    setStylistOutfitCount(STYLIST_DEFAULT);
  }, []));
  // Écouter les events de mise à jour du dressing (ajout/suppression/analyse IA)
  useEffect(() => {
    const sub = DeviceEventEmitter.addListener('wardrobe_updated', () => {
      console.log('[Home] wardrobe_updated event received → reloading');
      loadWardrobe();
    });
    return () => sub.remove();
  }, [loadWardrobe]);

  // ═══ Re-tri des tendances quand les prefs du jour changent ═══
  // L'user a soumis ou modifié ses préférences via le popup → on réordonne
  // catalogItems (les produits trending sont en tête) avec le nouveau boost.
  useEffect(() => {
    const sub = DeviceEventEmitter.addListener('daily_prefs_changed', (prefs) => {
      console.log('[Home] daily_prefs_changed → re-sorting trending');
      // ═══ Mémoriser les prefs pour le filtrage doux du bloc Tendances ═══
      setCurrentDailyPrefs(prefs);
      try {
        const { applyPrefsBoostToTrending } = require('../services/TrendingService');
        setCatalogItems(curr => {
          if (!curr || curr.length === 0) return curr;
          // Re-sort en place : les items "trending" passent par le boost.
          // Les non-trending restent dans leur ordre actuel.
          const trending = curr.filter(p => p.is_trending || p.isTrendy);
          const others   = curr.filter(p => !(p.is_trending || p.isTrendy));
          if (trending.length === 0) return curr;
          const boosted = applyPrefsBoostToTrending(trending, prefs);
          return [...boosted, ...others];
        });
      } catch (e) {
        console.warn('[Home] re-sort trending failed:', e.message);
      }
    });
    return () => sub.remove();
  }, []);

  // ═══ Chargement initial des daily prefs (pour le filtrage doux du bloc Tendances) ═══
  // Au mount, si l'user a déjà soumis ses prefs aujourd'hui, on les récupère pour
  // filtrer les Tendances dès le premier render.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getSessionPrefs } = require('../services/DailyPreferencesService');
        const prefs = await getSessionPrefs();
        // v1.12.138 — si pas de prefs sauvegardees, on set un objet vide pour
        // debloquer la generation Selection styliste (avant : currentDailyPrefs
        // restait null → tryGenerateStylist ne s'executait jamais → loading infini).
        if (!cancelled) {
          setCurrentDailyPrefs(prefs || {});
        }
      } catch (e) {
        console.warn('[Home] initial daily prefs load failed:', e?.message);
        if (!cancelled) setCurrentDailyPrefs({});
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Charger le catalogue (mix des partenaires + dynamic Cashmere/iQueens)
  useEffect(() => {
    // Guard : ne charger qu'une seule fois par valeur de gender valide
    if (!gender) return;
    let cancelled = false;

    async function loadCatalog() {
      setLoadingCat(true);
      try {
        // 🚀 Catalogue dynamique complet (2700+ produits)
        let dynamic = [];
        try {
          dynamic = await fetchDynamicCatalog(gender) || [];
        } catch {}
        if (cancelled) return;

        // v1.12.340 — Catalogue restreint : iQueens (via dynamic) + TOUS + SUIT + ANN + COUTR
        // v1.12.761 — Ajout drestige (multi-marques luxe Awin, 13% commission).
        const { getCatalog } = require('../services/DatabaseService');
        // v1.12.877 — Ajout tamaris (Tradedoubler 6%)
        const [suit, ann, coutr, drestige, tamaris] = await Promise.allSettled([
          getCatalog({ partner: 'suit_negozi',        limit: 1500, available: true, gender }),
          getCatalog({ partner: 'ann_demeulemeester', limit: 500,  available: true, gender }),
          getCatalog({ partner: 'coutr',              limit: 2000, available: true, gender }),
          getCatalog({ partner: 'drestige',           limit: 2000, available: true, gender }),
          getCatalog({ partner: 'tamaris',            limit: 2000, available: true, gender }),
        ]);
        if (cancelled) return;
        console.log('[HomeScreen.catalog] gender=', gender);
        console.log('[HomeScreen.catalog] suit:',
          suit.status, suit.status === 'fulfilled' ? `${suit.value?.length ?? 0} items` : suit.reason?.message);
        console.log('[HomeScreen.catalog] ann:',
          ann.status, ann.status === 'fulfilled' ? `${ann.value?.length ?? 0} items` : ann.reason?.message);
        console.log('[HomeScreen.catalog] coutr:',
          coutr.status, coutr.status === 'fulfilled' ? `${coutr.value?.length ?? 0} items` : coutr.reason?.message);
        console.log('[HomeScreen.catalog] drestige:',
          drestige.status, drestige.status === 'fulfilled' ? `${drestige.value?.length ?? 0} items` : drestige.reason?.message);
        console.log('[HomeScreen.catalog] tamaris:',
          tamaris.status, tamaris.status === 'fulfilled' ? `${tamaris.value?.length ?? 0} items` : tamaris.reason?.message);

        // Normalise les produits Rakuten (currency USD pour COUTR, affiliateUrl depuis description.buy_url)
        const normalizeRakutenPartner = (items, partnerKey) => (items || []).map(p => {
          let currency = 'EUR';
          let buy_url = null;
          try {
            const desc = typeof p.description === 'string' ? JSON.parse(p.description) : p.description;
            currency = desc?.currency || (partnerKey === 'coutr' ? 'USD' : 'EUR');
            buy_url  = desc?.buy_url || null;
          } catch {
            if (partnerKey === 'coutr') currency = 'USD';
          }
          return {
            id: p.id, name: p.name, price: Number(p.price) || 0, currency,
            image: p.image_url || p.image, image_url: p.image_url || p.image,
            affiliateUrl: buy_url || p.affiliateUrl || p.affiliate_url,
            affiliate_url: buy_url || p.affiliateUrl || p.affiliate_url,
            originalUrl: buy_url,
            url: buy_url,
            partner: partnerKey, brand: p.brand, category: p.category,
            ai_metadata: p.ai_metadata, discount: p.discount,
            original_price: p.original_price, originalPrice: p.original_price,
          };
        });

        const all = [
          ...dynamic,
          ...normalizeRakutenPartner(suit.value,  'suit_negozi'),
          ...normalizeRakutenPartner(ann.value,   'ann_demeulemeester'),
          ...normalizeRakutenPartner(coutr.value, 'coutr'),
          ...normalizeRakutenPartner(drestige.value, 'drestige'),
          ...normalizeRakutenPartner(tamaris.value, 'tamaris'),
        ].filter(p => {
          if (!p) return false;
          const img = p.image || p.image_url;
          if (!img || typeof img !== 'string' || !/^https?:\/\//.test(img)) return false;
          const link = p.affiliateUrl || p.originalUrl || p.url;
          if (!link || typeof link !== 'string' || !/^https?:\/\//.test(link)) return false;
          const priceNum = parseFloat(p.price);
          if (isNaN(priceNum) || priceNum <= 0) return false;
          return true;
        });
        // ═══ FUSION des doublons (avec agrégation des tailles) ═══
        // Au lieu de simplement supprimer les doublons (filter), on les fusionne :
        // un même article iQueens en plusieurs tailles devient 1 produit dont le
        // champ `sizes` contient toutes les tailles disponibles.
        // → Réduit le nombre d'articles dans le catalogue ET dans l'analyse IA.
        // → Cohérent avec TrendingService et TravelWardrobeScreen.
        const seenIds  = new Set();
        const seenSkus = new Set();
        // 1. Premier passage : retirer les vrais doublons par id/sku exacts
        //    (cas où le MÊME flux renvoie 2x la même ligne — peut arriver)
        const passOne = all.filter(p => {
          if (p.id && seenIds.has(p.id)) return false;
          const sku = p.zaraId || p.zalandoSku || p.hmCode || p.asosId || p.asin;
          if (sku && seenSkus.has(sku)) return false;
          if (p.id) seenIds.add(p.id);
          if (sku)  seenSkus.add(sku);
          return true;
        });
        // 2. Deuxième passage : fusionner les variantes de tailles iQueens
        //    (même nom_normalisé + brand + prix → 1 produit avec sizes agrégées)
        let deduped = mergeDuplicateProducts(passOne);
        console.log(`[Home] catalog: ${all.length} → ${passOne.length} (id/sku) → ${deduped.length} (variantes fusionnees)`);

        // ═══ RECLASSIFICATION CATÉGORIE via nom (corrige erreurs partners) ═══
        try {
          const { normalizeCategoriesBatch } = require('../services/CategoryNormalizer');
          const before = deduped.length;
          deduped = normalizeCategoriesBatch(deduped);
          const corrected = deduped.filter(p => p._originalCategory).length;
          if (corrected > 0) console.log(`[Home] catégorie corrigée pour ${corrected} articles (sur ${before})`);
        } catch(e) {}

        // ═══ FILTRE GENRE ═══
        // Ne montre que les articles du genre de l'utilisateur (+ unisexes).
        // Ne s'applique que si user.gender est connu (M/F/N).
        try {
          const { filterByGender } = require('../services/GenderFilter');
          const userGender = user?.gender;
          if (userGender && ['M', 'F'].includes(userGender)) {
            const before = deduped.length;
            deduped = filterByGender(deduped, userGender);
            if (deduped.length !== before) console.log(`[Home] gender filter: ${deduped.length}/${before} (target=${userGender})`);
          }
        } catch(e) {}
        console.log(`[Home] catalog loaded: ${deduped.length} items (${dynamic.length} dynamic)`);

        // ═══ FIX 2026-04 : enrichir les produits avec ai_metadata depuis le backend ═══
        // Avant : HomeScreen fetch les produits directement depuis les services partenaires
        // (Zara/Zalando/Asos/etc.) sans passer par /catalog du Lambda → pas de ai_metadata
        // → 0 produit trending (cf. log "ai_metadata=0/4526").
        //
        // Maintenant : avant d'appeler TrendingService, on appelle /catalog-analysis en
        // batch (500 IDs max) pour récupérer les ai_metadata déjà stockés en DB
        // (produits qui ont été analysés via /analyze-catalog-item). Cache AsyncStorage
        // évite les refetch inutiles. Les produits non-analysés restent sans ai_metadata
        // (ils ne seront pas trending tant qu'ils n'auront pas été analysés, ce qui est
        // correct — inutile de les promouvoir si Claude n'a pas validé qu'ils sont trendy).
        let productsWithAiMeta = deduped;

        // ═══ FIX 2026-04 v5 : récupérer le token AccessToken depuis useAuth ═══
        // L'app stocke le token via SecureStore (pas Amplify). Les helpers qui
        // utilisent Amplify renvoient null. Il faut passer explicitement le
        // token obtenu via AuthContext.getValidToken() (qui gère le refresh).
        let authToken = null;
        try {
          authToken = await getValidToken();
          console.log(`[Home] 🔑 getValidToken: ${authToken ? 'got token' : 'null (user not logged in?)'}`);
        } catch (e) {
          console.warn('[Home] getValidToken failed:', e.message);
        }

        try {
          productsWithAiMeta = await enrichCatalogWithAI(deduped, authToken);
        } catch(e) {
          console.warn('[Home] enrichCatalogWithAI failed:', e.message);
        }
        if (cancelled) return;

        // ═══ ENRICHISSEMENT TENDANCES ═══
        // Claude identifie les tendances mode actuelles et on match avec nos produits
        let withTrends = productsWithAiMeta;
        try {
          const { getTrendingProducts } = require('../services/TrendingService');
          const { products: enriched, trendingCount } = await getTrendingProducts(productsWithAiMeta, gender);
          console.log(`[Home] ${trendingCount} produits tendance détectés`);
          withTrends = enriched;
        } catch(e) {
          console.warn('[Home] trending failed:', e.message);
        }
        if (cancelled) return;

        // v1.12.207 — Reclassification runtime : la DB a beaucoup de trenches/
        // manteaux taggés "Vestes" historiquement (avant la séparation Vestes/
        // Manteaux). On détecte par keywords dans le nom et on reclasse.
        const COAT_KW = /\b(manteau|trench|caban|parka|doudoune|pardessus|duffle|overcoat|wool coat|long coat|peacoat)\b/i;
        const reclassed = withTrends.map(p => {
          if (p?.category !== 'Vestes') return p;
          const txt = `${p.name || ''} ${p.description || ''}`.toLowerCase();
          if (COAT_KW.test(txt)) {
            return { ...p, category: 'Manteaux', _reclassedFrom: 'Vestes' };
          }
          return p;
        });
        const reclassCount = reclassed.filter(x => x._reclassedFrom).length;
        if (reclassCount > 0) {
          console.log(`[Home] reclassed ${reclassCount} items from Vestes → Manteaux (keyword match)`);
        }

        if (!cancelled) setCatalogItems(applySkimlinksToProducts(reclassed, user?.id || ''));

        // ═══ FIX 2026-04 v2 : analyse catalog en CASCADE (plusieurs batchs en série) ═══
        // v1 : 1 seul batch de 5 produits par session → ~5 produits/jour si user ouvre
        //      l'app 1×/jour. Beaucoup trop lent pour remplir 4526 produits.
        //
        // v2 : on lance plusieurs batchs consécutifs (jusqu'à 5 batchs × 10 produits =
        //      50 produits par session) avec petit délai entre chaque. Coût par session:
        //      ~0.10€ (50 × 0.002€). Reste raisonnable. Un user actif remplit le
        //      catalogue en ~90 sessions.
        //
        // Le debounce de 5s dans analyzeCatalogInBackground n'est plus bloquant puisqu'on
        // re-appelle après que le précédent batch ait fini (loop await).
        if (!cancelled) {
          (async () => {
            // ═══ v1.9.11 : 5 batchs × 10 produits = 50 / session (avant : ~25) ═══
            //   Plus 10 batchs (au lieu de 5) pour accélérer la couverture initiale.
            const MAX_BATCHES = 10;
            const BATCH_SIZE = 10;
            let totalAnalyzed = 0;
            // IMPORTANT : on réutilise la même référence productsWithAiMeta; chaque
            // batch analyse les X premiers non-analysés. On ré-interroge avant chaque
            // batch pour ne pas re-tenter les mêmes échecs.
            for (let i = 0; i < MAX_BATCHES; i++) {
              if (cancelled) break;
              try {
                const r = await analyzeCatalogInBackground(productsWithAiMeta, authToken, BATCH_SIZE);
                if (!r || r.length === 0) {
                  console.log(`[Home] 🛑 batch ${i+1}: no products returned, stopping cascade`);
                  break;
                }
                totalAnalyzed += r.length;
                console.log(`[Home] 🎯 batch ${i+1}/${MAX_BATCHES}: ${r.length} products analyzed (total: ${totalAnalyzed})`);
                // Petit délai entre batchs pour respecter rate limit Anthropic
                await new Promise(res => setTimeout(res, 1500));
              } catch(e) {
                console.warn(`[Home] batch ${i+1} failed:`, e.message);
                break;
              }
            }
            if (totalAnalyzed > 0) {
              console.log(`[Home] ✅ background analysis done: ${totalAnalyzed} new products analyzed — refresh to see updated trending`);
            }
          })();
        }
      } catch(e) {
        console.warn('[Home] catalog error:', e.message);
      } finally {
        if (!cancelled) setLoadingCat(false);
      }
    }
    loadCatalog();
    return () => { cancelled = true; };
  }, [gender]);

  // ═══ v1.9.28 : Sélection styliste = StyleBank (500 combos IA) sur le CATALOGUE ═══
  //
  //   ARCHITECTURE EN 2 NIVEAUX :
  //
  //   Niveau 1 (PRIMAIRE) : StyleBank-driven
  //     → 500 inspirations IA générées hebdomadairement par le cron Lambda
  //     → Pour chaque inspiration, on cherche dans le catalogue les items qui
  //       correspondent aux `typical_pieces` (ex: "chemise oxford blanche")
  //     → On obtient un outfit "MATÉRIALISÉ" : la combo IA réalisée avec de
  //       vrais articles du catalogue, achetables tout de suite.
  //     → Trié par trend_score de la banque + nb d'items isTrendy
  //
  //   Niveau 2 (FALLBACK) : OutfitEngine combinatoire
  //     → Si la banque n'est pas chargée ou retourne 0 match
  //     → Combinatoire pure du catalogue (Robe+Chaussure, Haut+Bas+Chaussure...)
  //     → Toujours fonctionnel même offline ou banque vide
  //
  //   La SEULE différence avec Looks : on passe `catalogItems` comme wardrobe
  //   (pas le dressing personnel).
  // v1.12.119 — Génération deferred (setTimeout 0) pour éviter le freeze UI
  // au retour des préférences. Avant : useMemo synchrone qui calculait 1-3s
  // sur le main thread → freeze visible. Maintenant : useState + useEffect
  // qui defer le calcul, affiche un skeleton pendant ~1s, UI reste fluide.
  // v1.12.122 — Génération UNE seule fois au démarrage, déclenchée par
  // l'event 'daily_prefs_resolved' (popup soumise OU ignorée). Plus de
  // re-génération automatique sur changement de catalog/temp/styleBank.
  // v1.12.617 — stylistOutfitsGen + stylistTimedOut déplacés tout en haut
  // du composant (avant le useEffect shadow fetch) pour éviter TDZ. Voir
  // commentaire ligne ~265.
  const [stylistLoading, setStylistLoading] = React.useState(false);
  // v1.12.541 — Filtre par catégories choisies dans les préférences daily
  // Ex : user coche "Robes" → ne garder que les outfits avec au moins une Robe
  // v1.12.764 — Aussi filtre par température : backend cache des looks sans
  // tempC dans le hash, donc on filtre côté client à chaque change de météo.
  // v1.12.769 — Filtre availableIdSet retiré : HomeScreen ne fetch que 2000 coutr
  // sur 5376 DB → outfits backend avec items hors-pool étaient exclus à tort.
  // Côté broken images : LazyImage doit gérer onError silently (pas afficher
  // placeholder cassé bloquant). Cache stylist invalidation backend à faire.
  // v1.12.771 — Mode 'graduel' (vs 'strict') pour Sélection styliste : c'est
  // exploratoire (catalogue partenaire), on tolère ±8°C de marge. Sinon à 30°C
  // on filtrait 400 → 91 outfits (trop strict, sentiment de pool vide).
  // Tenue du jour reste en strict ailleurs (élimination par item dans scoreWeather).
  // v1.12.789 — Exclure l'outfit ENTIER si un CORE item (Robes/Hauts/Bas/
  // Chaussures) est warm-incompatible avec tempC. Vestes/Manteaux non-CORE
  // sont retirées visuellement par OutfitCard (banner ☀️).
  const stylistOutfitsFiltered = React.useMemo(() => {
    // v1.13.37 — Défaut : si l'user n'a pas choisi de catégorie, on priorise les
    // ROBES (femmes) → les tenues à base de robe remontent en premier, classées
    // par meilleur score. Dès que l'user choisit ses catégories, elles priment.
    const wantedCats = (Array.isArray(currentDailyPrefs?.categories) && currentDailyPrefs.categories.length > 0)
      ? currentDailyPrefs.categories
      : (user?.gender === 'M' ? null : ['Robes']);
    const wantedSet = (Array.isArray(wantedCats) && wantedCats.length > 0) ? new Set(wantedCats) : null;
    const t = typeof temperature === 'number' ? temperature : null;
    const CORE_CATS = new Set(['Robes', 'Hauts', 'Bas', 'Chaussures']);
    // v1.12.858 — Filtre genre: rejette outfit si > 30% items de genre opposé
    // Empêche TOUS (F-only) d'apparaître dans selection styliste homme et inverse.
    const userGender = user?.gender;
    const filterByOppositeGender = userGender && (userGender === 'M' || userGender === 'F');
    const oppositeGender = userGender === 'M' ? 'F' : 'M';
    // v1.12.804 — Aligné sur removedWarm (rendu) : si un CORE warm est retiré
    // au rendu, on doit aussi le filtrer ici sinon l'outfit reste sans pièce.
    const isCoreWarmIncompatible = (it, tempC) => {
      if (!it || !CORE_CATS.has(it.category)) return false;
      const name = (it.name || '').toLowerCase();
      const mat = (it.ai_metadata?.material || '').toLowerCase();
      const isWarmMaterial = /pull|sweat|hoodie|cardigan|cachemire|laine|polaire|cashmere|mohair/.test(name)
        || /laine|wool|cachemire|cashmere|mohair|polaire/.test(mat);
      if (!isWarmMaterial) return false;
      // v1.12.870 — Relaxé : ne rejette qu'au delà de 28°C (avant >22°C →
      // rejetait tous les outfits avec pull en été → message "0 tenue")
      if (tempC > 28) return true;
      return false;
    };
    const pool = deferredStylistOutfitsGen || [];
    // v1.13.42 — Mode "défaut" (l'user n'a choisi ni occasion ni style) : le backend
    // renvoie désormais le pool GÉNÉRIQUE (large). On fait remonter en tête les
    // meilleures combinaisons souhaitées : ROBES > occasion soirée > style chic.
    // Les scores backend départagent à rang égal (tri stable Hermes/V8).
    const _defaultMode = !currentDailyPrefs?.occasion
      && !(Array.isArray(currentDailyPrefs?.styles) && currentDailyPrefs.styles.length > 0);
    // v1.12.869 — Tri prioritaire : outfits matchant les prefs en premier, mais on garde tout.
    // v1.13.42 — En mode défaut, on lit les tags RÉELS de l'outfit adapté
    // (bestTag + dominantTags) pour faire remonter soirée puis chic.
    const rankOutfit = (o) => {
      let r = 0;
      if (wantedSet && (o?.items || []).some(it => wantedSet.has(it?.category))) r += 4; // catégorie voulue (robes par défaut)
      if (_defaultMode) {
        const tags = new Set([
          (o?.bestTag || '').toString().toLowerCase(),
          ...((o?.dominantTags || []).map(x => (x || '').toString().toLowerCase())),
        ].filter(Boolean));
        if (tags.has('soirée') || tags.has('soiree') || tags.has('evening') || tags.has('cocktail')) r += 2; // occasion soirée
        if (tags.has('chic') || tags.has('élégant') || tags.has('elegant')) r += 1; // style chic
      }
      return r;
    };
    const sortedAll = pool.slice().sort((a, b) => rankOutfit(b) - rankOutfit(a));
    // v1.13.72 — EXCLUSION DURE genre : un HOMME ne doit JAMAIS voir une tenue
    //   contenant une robe. Certains articles robe sont mal taggés gender=M côté
    //   catalogue (ex. "black cutout turtleneck sweater dress" Coutr) → ils
    //   passaient le seuil 30% (1 robe / 5 items = 20%). Ici : une SEULE pièce
    //   robe (catégorie OU nom "dress/robe") = tenue exclue en totalité.
    const isDressItem = (it) => {
      const c = String(it?.category || '').toLowerCase();
      const n = String(it?.name || '').toLowerCase();
      return c === 'robes' || c === 'robe' || /\bdress\b|\brobe\b/.test(n);
    };
    const sorted = (userGender === 'M')
      ? sortedAll.filter(o => !(o?.items || []).some(isDressItem))
      : sortedAll;
    const CORE_BODY = new Set(['Hauts', 'Vestes', 'Manteaux', 'Robes']);
    const minimalValid = (outfit) => {
      const items = outfit?.items || [];
      if (items.length < 2) return false;
      return items.some(it => CORE_BODY.has(it?.category));
    };
    const strictPass = (outfit) => {
      const items = outfit?.items || [];
      if (!minimalValid(outfit)) return false;
      if (t !== null && items.some(it => isCoreWarmIncompatible(it, t))) return false;
      if (filterByOppositeGender) {
        const wrongGender = items.filter(it => {
          const g = it?.gender || it?.ai_metadata?.gender;
          if (!g) return false;
          if (g === oppositeGender) return true;
          if (userGender === 'M' && it?.category === 'Robes') return true;
          return false;
        });
        if (items.length > 0 && (wrongGender.length / items.length) > 0.3) return false;
      }
      return true;
    };
    const strict = sorted.filter(strictPass);
    // v1.12.870 — Safety net : si TOUT est rejeté par filters stricts (temp/gender),
    // fallback sur minimal valid (>=2 items + CORE_BODY) plutôt que message vide.
    const base = (strict.length === 0 && sorted.length > 0)
      ? sorted.filter(minimalValid)
      : strict;
    // v1.13.46 — FILTRE catégorie RÉEL quand l'user a EXPLICITEMENT choisi des
    // catégories (ex. "Robes") : on ne garde que les tenues contenant au moins
    // une pièce de ces catégories, au lieu d'un simple tri "float" (qui laissait
    // remonter des tenues sans robe). Fallback anti-vide : si aucune tenue ne
    // matche, on retombe sur `base` (mieux qu'un écran vide).
    const _userChoseCats = Array.isArray(currentDailyPrefs?.categories) && currentDailyPrefs.categories.length > 0;
    if (_userChoseCats && wantedSet) {
      const catMatch = base.filter(o => (o?.items || []).some(it => wantedSet.has(it?.category)));
      if (catMatch.length > 0) {
        console.log(`[Home] stylistOutfitsFiltered: filtre catégories ${[...wantedSet].join(',')} → ${catMatch.length}/${base.length}`);
        return catMatch;
      }
      console.log(`[Home] stylistOutfitsFiltered: 0 tenue pour catégories ${[...wantedSet].join(',')} → fallback ${base.length}`);
    }
    return base;
  }, [deferredStylistOutfitsGen, currentDailyPrefs?.categories, currentDailyPrefs?.occasion, currentDailyPrefs?.styles, temperature, user?.gender]);
  const stylistGeneratedRef = React.useRef(false);

  // v1.12.226 — Enrichissement agent : score+badges+reasons par outfit Sélection styliste
  // Les outfits eux-mêmes sont générés par StyleBank+OutfitEngine sur le catalogue.
  // L'agent rescore les top visibles pour ajouter les badges UI premium.
  const stylistAgentMap = React.useMemo(() => {
    if (!stylistOutfitsGen || stylistOutfitsGen.length === 0) return new Map();
    lf('styliste-agentmap (stylistSelection)');
    try {
      const today = new Date();
      const looksNorm = stylistOutfitsGen.slice(0, 30).map((o, i) =>
        normalizeLookForScoring({ ...o, id: o.id || `stylist_${i}` }, 'looks_ia')
      );
      const ctx = {
        date: today.toISOString().slice(0, 10),
        weather: { temp_celsius: temperature ?? 18, condition: weatherDesc || undefined },
      };
      const result = stylistSelection({
        action: 'STYLIST_SELECTION',
        user_id: user?.id,
        data: {
          preferences: {
            ...(currentDailyPrefs || {}),
            occasions: currentDailyPrefs?.occasion ? [currentDailyPrefs.occasion] : (currentDailyPrefs?.occasions || []),
            gender: user?.gender || null,
          },
          context: ctx,
          recently_worn_look_ids: recentlyWornLookIds,
          available_looks: looksNorm,
          limit: 30,
        },
      });
      const map = new Map();
      if (result?.success && result?.data?.selection) {
        for (const s of result.data.selection) {
          map.set(String(s.look_id), {
            badges: s.badges || [],
            reasons: s.reasons || [],
            score: s.score,
            budget_status: s.budget_status,
          });
        }
      }
      return map;
    } catch (e) {
      console.warn('[Home] stylistAgent enrichment failed:', e?.message);
      return new Map();
    }
    // v1.12.899 — Perf : deps spécifiques au lieu de currentDailyPrefs entier
  }, [stylistOutfitsGen, temperature, weatherDesc, recentlyWornLookIds, user?.id, user?.gender,
      currentDailyPrefs?.occasion,
      currentDailyPrefs?.budget,
      currentDailyPrefs?.styles?.join?.(',') || '',
      currentDailyPrefs?.categories?.join?.(',') || '',
      currentDailyPrefs?.colors?.join?.(',') || '',
      currentDailyPrefs?.partners?.join?.(',') || '']);

  // v1.13.43 — version du registre d'images mortes : quand une image d'un item
  // est détectée morte (404), on recalcule → l'outfit qui la contient est droppé.
  const deadImagesVersion = useDeadImagesVersion();
  // v1.12.885 — Pre-compute heavy fields per outfit (avoids recompute on every scroll/inc visibleCount)
  // Mémoïse removedWarm, displayedItems, totalPrice, etc. → render = O(visible) lookup au lieu de O(n²)
  const stylistOutfitsComputed = React.useMemo(() => {
    const poolFull = stylistOutfitsFiltered || [];
    if (poolFull.length === 0) return new Map();
    // v1.13.49 — PERF : ne pré-calcule QUE les N premières tenues (déjà triées
    // "meilleures d'abord"). Le pool peut monter à 400 après top-up ; en traiter
    // 400 (regex + map par item) bloque le thread JS → jank + FAB "composer un
    // look" figé au changement de prefs. 120 couvre largement la pagination.
    const COMPUTE_CAP = 400; // v1.13.97 — traiter tout le pool élargi (60→200/400) sinon peu survivent aux filtres
    const pool = poolFull.length > COMPUTE_CAP ? poolFull.slice(0, COMPUTE_CAP) : poolFull;
    lf('styliste-compute (memo)');
    const _t = typeof temperature === 'number' ? temperature : null;
    const PRINCIPAL_CATS = new Set(['Robes', 'Hauts', 'Bas', 'Chaussures']);
    const WARM_NAME_RE = /pull|sweat|hoodie|cardigan|cachemire|laine|polaire|cashmere|mohair/;
    const WARM_MAT_RE = /laine|wool|cachemire|cashmere|mohair|polaire/;
    const MANTEAU_RE = /manteau|doudoune|parka|coat/;
    const VESTE_RE = /veste|blazer|jacket/;
    // v1.12.976 — tolérance °C au-dessus de la plage idéale IA avant de juger "trop chaud".
    const TEMP_TOLERANCE = 2;
    const map = new Map();
    for (const o of pool) {
      if (!o?.items || o.items.length === 0) continue;
      const removedWarm = _t === null ? [] : o.items.filter(it => {
        if (!it) return false;
        const cat = it.category || '';
        const name = (it.name || '').toLowerCase();
        const mat = (it.ai_metadata?.material || '').toLowerCase();
        // v1.12.976 — Signal PRIORITAIRE : plage de température idéale IA de l'article
        // (temperatureRange=[min,max]). Ex. "lightweight cashmere sweater" = [8,18] →
        // à 22°C, max idéal (18) trop bas → article trop chaud → retiré. Si c'est un
        // article CORE (Hauts/Robes/Bas/Chaussures), toute la tenue est annulée.
        const range = it.ai_metadata?.temperatureRange;
        if (Array.isArray(range) && range.length === 2 && range[1] > 0) {
          const maxIdeal = Number(range[1]);
          if (Number.isFinite(maxIdeal) && _t > maxIdeal + TEMP_TOLERANCE) return true;
        }
        const isManteau = cat === 'Manteaux' || MANTEAU_RE.test(name);
        const isWarmMaterial = WARM_NAME_RE.test(name) || WARM_MAT_RE.test(mat);
        const isVeste = cat === 'Vestes' || VESTE_RE.test(name);
        if (_t > 25 && (isVeste || isManteau || isWarmMaterial)) return true;
        if (_t > 22 && (isManteau || isWarmMaterial)) return true;
        if (_t > 18 && isManteau) return true;
        return false;
      });
      // v1.13.2 — Si un article CORE est "trop chaud", on marque tempDrop (masqué
      // par défaut) MAIS on calcule quand même les données d'affichage → filet
      // anti-vide : si TOUTES les tenues sont tempDrop (ex. été), on les ré-affiche
      // plutôt que de montrer "0 tenue".
      const coreWarmRemoved = removedWarm.some(it => PRINCIPAL_CATS.has(it?.category));
      const removedIds = new Set(removedWarm.map(it => it?.id).filter(Boolean));
      // tempDrop → on conserve l'outfit complet ; sinon on retire les warm NON-core
      const baseItems = coreWarmRemoved
        ? o.items
        : (removedWarm.length > 0 ? o.items.filter(it => !it?.id || !removedIds.has(it.id)) : o.items);
      // v1.12.998 — Garantir `.image` pour le rendu (SmartImage uri={it.image}).
      const displayedItems = baseItems.map(it => (it && !it.image && (it.image_url || it.photoUrl || it.imageUrl))
        ? { ...it, image: it.image_url || it.photoUrl || it.imageUrl }
        : it);
      if (displayedItems.length === 0) {
        map.set(o.id || `_${map.size}`, { drop: true });
        continue;
      }
      // v1.12.977 — DROP DUR si un article n'a pas d'image exploitable (case vide).
      if (displayedItems.some(it => !hasUsableImage(it))) {
        map.set(o.id || `_${map.size}`, { drop: true });
        continue;
      }
      const totalPrice = displayedItems.reduce((s, p) => s + (p.price || 0), 0);
      const totalOriginal = displayedItems.reduce((s, p) => s + (p.originalPrice || p.price || 0), 0);
      const hasDiscount = totalOriginal > totalPrice + 1;
      const savings = hasDiscount ? (totalOriginal - totalPrice) : 0;
      const heroItem = displayedItems.find(it => it.category === 'Robes') || displayedItems[0];
      const restItems = displayedItems.filter(it => it.id !== heroItem?.id);
      map.set(o.id || `_${map.size}`, {
        outfitOverride: { ...o, items: displayedItems },
        removedWarm, displayedItems, totalPrice, totalOriginal, hasDiscount, savings,
        heroItem, restItems,
        // drop = masqué par le filtre température (mais ré-affichable via le filet anti-vide)
        drop: coreWarmRemoved, tempDrop: coreWarmRemoved,
      });
    }
    return map;
  }, [stylistOutfitsFiltered, temperature, deadImagesVersion]);

  // v1.12.973 — Liste d'affichage = outfits filtrés MOINS ceux dropés par le
  // pre-compute (CORE warm retiré pour cause de température / items vides).
  // Sans ça, la pagination "Voir +5" comptait les dropés → slice(0, count)
  // contenait des cartes qui se render en null → l'user voyait parfois 1-2
  // tenues au lieu de 5. On pagine donc sur la même liste que le render réel.
  const stylistOutfitsDisplay = React.useMemo(() => {
    const pool = stylistOutfitsFiltered || [];
    if (pool.length === 0) return pool;
    const kept = pool.filter(o => {
      const c = stylistOutfitsComputed.get(o?.id);
      return c && !c.drop;
    });
    // v1.13.2 — FILET ANTI "0 TENUE" + v1.13.70 SEUIL "TROP PEU" : si le filtre
    // température ne laisse que quelques tenues (ex. tenues de SOIRÉE HOMME jugées
    // "trop chaudes" en été — blazers/manches longues → 1 seule survivante), on
    // ré-affiche le pool complet (toute tenue valide : heroItem + images OK) au
    // lieu d'une liste ridicule. Mieux vaut des tenues un peu chaudes que 1 seule.
    const MIN_KEPT = 8;
    let list = kept.length >= MIN_KEPT ? kept : pool.filter(o => {
      const c = stylistOutfitsComputed.get(o?.id);
      return c && c.heroItem;
    });

    // v1.13.47 — RÈGLE FINALE AUTORITAIRE (immunisée contre cache / précalculés) :
    //   • Détection robuste "robe" = catégorie normalisée (robe/robes/dress) OU
    //     nom contenant robe/dress (au cas où la catégorie manque).
    //   • Si l'user a EXPLICITEMENT choisi des catégories (ex. Robes) → on ne
    //     GARDE que les tenues qui en contiennent (fallback anti-vide).
    //   • Sinon (SKIP / aucune pref) → on remonte en TÊTE evening chic robe :
    //     robe (poids 2) + style chic (poids 1). L'occasion soirée est déjà
    //     garantie par la requête backend.
    const norm = (s) => String(s || '').toLowerCase().trim();
    const ROBE_SET = new Set(['robes', 'robe', 'dress', 'dresses']);
    const itemInCats = (it, catSet) => {
      const c = norm(it?.category);
      if (catSet.has(c)) return true;
      if (catSet.has('robes') && /\brobe\b|\bdress\b|\brobes\b/.test(norm(it?.name))) return true;
      return false;
    };
    const isRobe = (o) => (o?.items || []).some(it => itemInCats(it, ROBE_SET));
    // v1.13.71 — ÉQUIVALENT HOMME de la robe = le COSTUME (veste/blazer + pantalon
    //   assorti). Un homme n'a pas de robe → pour lui, le défaut "soirée chic" met
    //   en tête les COSTUMES au lieu des robes.
    const isMale = (user?.gender || user?.profile?.gender) === 'M';
    const isSuit = (o) => {
      const items = o?.items || [];
      const hasJacket = items.some(it => {
        const c = norm(it?.category); const n = norm(it?.name);
        return c === 'vestes' || /\bblazer\b|\bcostume\b|\bsuit\b|\bveste\b|\btailleur\b/.test(n);
      });
      const hasBottom = items.some(it => {
        const c = norm(it?.category); const n = norm(it?.name);
        return c === 'bas' || /\bpantalon\b|\btrouser|\bchino\b|\bpants?\b/.test(n);
      });
      // tag explicite "costume/suit" suffit aussi
      const tagSuit = /\bcostume\b|\bsuit\b/.test(norm(o?.bestTag)) ||
        (o?.dominantTags || []).some(t => /\bcostume\b|\bsuit\b/.test(norm(t)));
      return tagSuit || (hasJacket && hasBottom);
    };
    // Pièce "vedette" du défaut selon le genre : robe (F) ou costume (M).
    const isHeroPiece = (o) => (isMale ? isSuit(o) : isRobe(o));
    // v1.13.48 — Style géré CÔTÉ CLIENT (le backend n'est plus interrogé par
    //   style → pool large). styleSet = styles choisis, sinon 'chic' par défaut.
    const chosenStyles = (Array.isArray(currentDailyPrefs?.styles) && currentDailyPrefs.styles.length > 0)
      ? currentDailyPrefs.styles.map(norm) : ['chic'];
    const styleSet = new Set(chosenStyles);
    const styleScore = (o) => {
      const tags = new Set([norm(o?.bestTag), ...((o?.dominantTags || []).map(norm))]);
      for (const st of styleSet) {
        if (tags.has(st)) return 1;
        if ((st === 'chic') && (tags.has('elegant') || tags.has('élégant'))) return 1;
      }
      return 0;
    };
    // v1.13.52 — Score QUALITÉ (« les plus jolies + compatibles ») : score de
    //   compatibilité (dominant, 96-99) départagé finement par trendScore
    //   (attrait/tendance, 87-94) + bonus cohésion si présent.
    const qual = (o) =>
      (Number(o?.score) || 0)
      + (Number(o?.trendScore) || 0) / 1000
      + (Number(o?.cohesion?.total) || 0);
    const explicitCats = (Array.isArray(currentDailyPrefs?.categories) && currentDailyPrefs.categories.length > 0)
      ? currentDailyPrefs.categories : null;

    if (explicitCats) {
      const catSet = new Set(explicitCats.map(norm));
      const matched = list.filter(o => (o?.items || []).some(it => itemInCats(it, catSet)));
      const finalList = matched.length > 0 ? matched : list;
      console.log(`[Home] display: filtre catégories ${explicitCats.join(',')} → ${matched.length}/${list.length}`);
      // Style choisi (chic par défaut) en tête, puis les plus jolies/compatibles.
      return [...finalList].sort((a, b) => (styleScore(b) - styleScore(a)) || (qual(b) - qual(a)));
    }
    // v1.13.53 — SKIP / défaut = ROBE de SOIRÉE CHIC en tête (défaut prévu) :
    //   priorité robe (poids 2) + style chic (poids 1) [l'occasion soirée est déjà
    //   garantie par la requête backend], PUIS départage par qualité (les plus
    //   jolies / compatibles d'abord à priorité égale).
    return [...list].sort((a, b) => {
      // v1.13.71 — pièce vedette selon le genre : robe (F) / costume (M)
      const ra = (isHeroPiece(a) ? 2 : 0) + styleScore(a);
      const rb = (isHeroPiece(b) ? 2 : 0) + styleScore(b);
      if (rb !== ra) return rb - ra;
      return qual(b) - qual(a);
    });
  }, [stylistOutfitsFiltered, stylistOutfitsComputed, currentDailyPrefs?.categories, currentDailyPrefs?.styles]);

  // v1.12.981 — Top-up : garantit un minimum de tenues AFFICHÉES. Si après filtres
  // (température / image cassée / prefs) il reste < 50 tenues, on redemande au backend
  // un pool plus large (60 → 200 → 400) puis on ré-affiche — quels que soient les filtres.
  // Bornes : s'arrête à 400 (limite reader) ou si le backend a renvoyé moins que demandé
  // (pool épuisé). Le ref évite les fetchs en double pendant qu'un top-up est en vol.
  const STYLIST_MIN_OUTFITS = 50;
  useEffect(() => {
    if (stylistLoading) return;
    const fetched = stylistOutfitsGen.length;
    if (fetched === 0) return;
    if (stylistOutfitsDisplay.length >= STYLIST_MIN_OUTFITS) return;
    const cur = stylistLimitRef.current || 60;
    if (cur >= 400) return;            // déjà au max backend
    if (fetched < cur) return;         // backend a renvoyé moins que demandé → pool épuisé
    if (!stylisteParamsRef.current) return;
    const nextLimit = cur < 200 ? 200 : 400;
    stylistLimitRef.current = nextLimit;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchLooksStyliste(stylisteParamsRef.current, { limit: nextLimit });
        if (cancelled || !Array.isArray(r.looks) || r.looks.length === 0) return;
        const adapted = r.looks.map((look) => adaptApiLookToStylistOutfit(look));
        setStylistOutfitsGen(adapted);
        console.log(`[LooksAPI] top-up styliste → limit ${nextLimit} (affichées ${stylistOutfitsDisplay.length} < ${STYLIST_MIN_OUTFITS})`);
      } catch (e) { console.warn('[LooksAPI] top-up failed:', e?.message); }
    })();
    return () => { cancelled = true; };
  }, [stylistOutfitsDisplay.length, stylistOutfitsGen.length, stylistLoading]);

  // v1.12.193 — Si l'utilisateur change son genre dans Profil, on doit reset
  // la sélection styliste et la regénérer (sinon on garde des outfits du
  // mauvais genre tant qu'on n'a pas reload la page).
  const prevGenderRef = React.useRef(user?.gender);
  React.useEffect(() => {
    if (prevGenderRef.current && prevGenderRef.current !== user?.gender) {
      console.log(`[Home] gender changed ${prevGenderRef.current} → ${user?.gender} : reset stylist`);
      stylistGeneratedRef.current = false;
      setStylistOutfitsGen([]);
    }
    prevGenderRef.current = user?.gender;
  }, [user?.gender]);

  // v1.12.342 — BUG FIX : quand les prefs user changent (styles/categories/
  //   partners/occasion/colors), la sélection styliste doit se RE-générer.
  //   Avant : stylistGeneratedRef bloquait toute régénération → user change
  //   "chic" ou "Robes" mais voit toujours la même sélection.
  const prevPrefsKeyRef = React.useRef(null);
  React.useEffect(() => {
    if (!currentDailyPrefs) return;
    // Signature stable des prefs qui impactent la sélection styliste
    const key = JSON.stringify({
      styles:     currentDailyPrefs.styles || [],
      categories: currentDailyPrefs.categories || [],
      partners:   currentDailyPrefs.partners || [],
      occasion:   currentDailyPrefs.occasion || null,
      colors:     currentDailyPrefs.colors || [],
      budget:     currentDailyPrefs.budget || null,
    });
    if (prevPrefsKeyRef.current !== null && prevPrefsKeyRef.current !== key) {
      console.log('[Home] prefs changed → reset stylist selection');
      stylistGeneratedRef.current = false;
      setStylistOutfitsGen([]);
    }
    prevPrefsKeyRef.current = key;
  }, [currentDailyPrefs]);

  // Trigger interne : run gen UNE FOIS quand prefs résolues + catalog prêt
  const tryGenerateStylist = React.useCallback(() => {
    // v1.12.393 — Skip le calcul local si backend Looks activé. Le useEffect
    // shadow fetch (haut du composant) injecte stylistOutfitsGen via backend.
    // Évite le freeze main thread (computeStylistOutfits = 1-3s sync).
    if (USE_BACKEND_LOOKS) {
      console.log('[Home] stylist gen LOCAL skipped (USE_BACKEND_LOOKS=true)');
      stylistGeneratedRef.current = true;
      return;
    }
    if (stylistGeneratedRef.current) {
      console.log('[Home] stylist gen skipped (already generated)');
      return;
    }
    if (!catalogItems || catalogItems.length === 0) {
      console.log('[Home] stylist gen skipped (catalog empty)');
      return;
    }
    stylistGeneratedRef.current = true;
    setStylistLoading(true);
    setTimeout(() => {
      // v1.12.138 — try/finally garantit que stylistLoading flippe false
      // meme si computeStylistOutfits throw (avant : loading infini).
      try {
        const result = computeStylistOutfits();
        setStylistOutfitsGen(result || []);
      } catch (e) {
        console.error('[Home] computeStylistOutfits failed:', e?.message, e?.stack);
        setStylistOutfitsGen([]);
        // Reset le ref pour que le prochain trigger (ex: reload prefs) re-essaye
        stylistGeneratedRef.current = false;
      } finally {
        setStylistLoading(false);
      }
    }, 16);
  }, [catalogItems]);

  // Listener event prefs résolues (popup submit OU skip)
  React.useEffect(() => {
    const sub = DeviceEventEmitter.addListener('daily_prefs_resolved', () => {
      console.log('[Home] daily_prefs_resolved → trigger stylist gen');
      tryGenerateStylist();
    });
    return () => sub.remove();
  }, [tryGenerateStylist]);

  // Si prefs déjà résolues AVANT mount (cas où popup déjà fermé / cache prefs),
  // on déclenche aussi via les prefs initiales chargées via getSessionPrefs.
  // Le ref empêche un double-trigger.
  React.useEffect(() => {
    if (currentDailyPrefs !== null && catalogItems && catalogItems.length > 0) {
      tryGenerateStylist();
    }
  }, [currentDailyPrefs, catalogItems, tryGenerateStylist]);

  const computeStylistOutfits = React.useCallback(() => {
    if (!catalogItems || catalogItems.length === 0) return [];

    const prefs = currentDailyPrefs || {};
    // v1.12.215 — température disponible dès le début (bank + engine + scoring)
    const tempC = temperature ?? 18;
    // ═══ v1.9.36 : Budget = MAX par pièce (pas de min) ═══
    //   Avant : ranges [min, max] qui filtraient les pièces hors d'une fourchette
    //     (ex: "premium" = 150-500€ → exclut les pièces < 150€).
    //   Maintenant : on prend juste le MAX. Une pièce premium 200€ est OK,
    //     une pièce mid-range 80€ est OK aussi (l'user a choisi "max 500€").
    //   Logique : "Budget MAX par pièce" = limite supérieure, pas tranche.
    const BUDGET_MAX_PER_ITEM = {
      'budget':    50,
      'mid-range': 150,
      'premium':   500,
      'luxury':    99999,
    };
    const maxPricePerItem = prefs.budget ? BUDGET_MAX_PER_ITEM[prefs.budget] : null;

    // ─── Étape 1 : filtrer le catalogue selon préférences user (budget par pièce) ───
    let valid = catalogItems.filter(p => p && p.image && p.price > 0);
    if (maxPricePerItem) {
      const filtered = valid.filter(p => (parseFloat(p.price) || 0) <= maxPricePerItem);
      // Garde le filtre seulement si on a au moins 30 items (sinon trop strict)
      if (filtered.length >= 30) valid = filtered;
    }

    // ─── Étape 2 : scoring de pré-tri (priorité aux tendance + partenaires premium + préférés) ───
    // v1.12.338 — Étendu : SUIT/ANN/COUTR boostés + filtre dur sur prefs.partners
    // v1.12.366 — iqueens retiré (produits disabled en DB)
    const PREMIUM_PARTNERS = ['cashmere', 'suit_negozi', 'ann_demeulemeester', 'coutr', 'drestige', 'tamaris'];
    const userPrefPartners = Array.isArray(prefs.partners) && prefs.partners.length > 0
      ? new Set(prefs.partners)
      : null;
    const scoreItem = (p) => {
      let sc = 50;
      const ai = p.ai_metadata || {};
      if (ai.isTrendy === true) sc += 50;
      if (typeof ai.trendScore === 'number' && ai.trendScore >= 70) sc += 25;
      // Bonus partenaires premium (tous)
      if (PREMIUM_PARTNERS.includes(p.partner)) sc += 20;
      // v1.12.338 — BONUS MAJEUR si partenaire EST dans les préférences user
      if (userPrefPartners && userPrefPartners.has(p.partner)) sc += 500;
      if (ai.analyzedAt) sc += 15;
      if (p.discount && p.discount > 20) sc += 15;
      if (p.rating && p.rating >= 4) sc += 10;
      if (prefs.categories?.length > 0 && prefs.categories.includes(p.category)) sc += 30;
      if (prefs.occasion) {
        const occScore = ai.occasions?.[prefs.occasion] || 0;
        if (occScore >= 60) sc += 20;
      }
      if (prefs.styles?.length > 0) {
        const styleScores = ai.styleScores || {};
        const hasMatch = prefs.styles.some(s => (styleScores[s] || 0) >= 60);
        if (hasMatch) sc += 15;
      }
      return sc;
    };
    // v1.12.338 — Filtre dur : si l'user a choisi des partenaires précis,
    //   on restreint au pool de ces partenaires. Sinon on garde tout.
    //   Soft fallback : si < 30 items disponibles, on relâche le filtre.
    if (userPrefPartners) {
      const filteredByPref = valid.filter(p => userPrefPartners.has(p.partner));
      if (filteredByPref.length >= 30) {
        valid = filteredByPref;
        console.log(`[Home] stylist filter: ${valid.length} items from preferred partners`);
      } else {
        console.log(`[Home] stylist filter relaxed (only ${filteredByPref.length} from prefs — keeping all)`);
      }
    }
    const sortedCatalog = [...valid].sort((a, b) => scoreItem(b) - scoreItem(a));

    // ═══ NIVEAU 1 : StyleBank-driven ═══
    //
    //   Pour chaque inspiration de la banque (filtrée par préférences),
    //   on cherche dans le catalogue les items qui correspondent aux
    //   typical_pieces. Si on trouve assez d'items pour construire une
    //   tenue valide, on la garde.
    //
    //   findMatchingCombinations applique un scoring keyword/desc :
    //     - score >= 0.30 par item (assez tolérant)
    //     - >= 50% des pièces requises matchées
    //
    //   On TRIE après par trend_score de la banque + isTrendy items pour
    //   afficher les inspirations les plus tendance en premier.
    const bankOutfits = [];
    if (styleBank?.combinations?.length > 0) {
      try {
        // Filtrer la banque par préférences user (occasion, styles, categories)
        let bankCombos = styleBank.combinations;
        if (prefs.occasion) {
          // v1.12.343 — seuil ≥1 (avant ≥30) : on applique le filtre dès qu'au moins 1 combo matche.
          //   Évite que des occasions peu représentées (Soirée, Cocktail, etc.) se voient
          //   relâchées → tous les outfits affichés non pertinents.
          const occLower = prefs.occasion.toLowerCase();
          const filteredByOcc = bankCombos.filter(c =>
            (c.occasion || '').toLowerCase() === occLower
          );
          if (filteredByOcc.length >= 1) {
            bankCombos = filteredByOcc;
            console.log(`[Home] stylist occasion filter STRICT: ${bankCombos.length} combos matching occasion=${occLower}`);
          } else {
            console.log(`[Home] stylist occasion filter ignored (0 combos for "${occLower}")`);
          }
        }
        // v1.12.343 — filtre style STRICT (seuil ≥1, avant ≥10)
        if (Array.isArray(prefs.styles) && prefs.styles.length > 0) {
          const stylesLower = prefs.styles.map(s => String(s).toLowerCase());
          const filteredByStyle = bankCombos.filter(c => {
            const cs = String(c.style || '').toLowerCase();
            return stylesLower.includes(cs);
          });
          if (filteredByStyle.length >= 1) {
            bankCombos = filteredByStyle;
            console.log(`[Home] stylist style filter STRICT: ${bankCombos.length} combos matching styles=${stylesLower.join(',')}`);
          } else {
            console.log(`[Home] stylist style filter ignored (0 combos for ${stylesLower.join(',')})`);
          }
        }
        // v1.12.343 — filtre catégorie STRICT (seuil ≥1, avant ≥5)
        if (Array.isArray(prefs.categories) && prefs.categories.length > 0) {
          const catsLower = prefs.categories.map(c => String(c).toLowerCase());
          const filteredByCat = bankCombos.filter(c => {
            const pieces = (c.typical_pieces || c.pieces || []).map(p => String(p).toLowerCase());
            return catsLower.some(cat => pieces.includes(cat));
          });
          if (filteredByCat.length >= 1) {
            bankCombos = filteredByCat;
            console.log(`[Home] stylist category filter STRICT: ${bankCombos.length} combos containing ${catsLower.join('/')}`);
          } else {
            console.log(`[Home] stylist category filter ignored (0 combos for ${catsLower.join(',')})`);
          }
        }

        // Trier par trend_score décroissant pour traiter les meilleures combos d'abord
        bankCombos = [...bankCombos].sort((a, b) => (b.trend_score || 0) - (a.trend_score || 0));

        // Matcher chaque combo avec le catalogue (trié par scoring item)
        // v1.10.83 : Avec le nouveau scoring multi-critères (keyword + style + color),
        // on peut être plus exigeant sur le minMatchScore. 0.35 = au moins 35% combiné.
        // Le filtre style_coherence dans findMatchingCombinations rejettera les outfits
        // dont moins de 30% des items matchent le style cible.
        const matches = findMatchingCombinations(sortedCatalog, bankCombos, {
          minMatchScore: 0.35,
          minPiecesMatched: 0.5,
          colorBank,              // Phase C.4 v1.12.65
          materialBank,           // Phase D.3 v1.12.69
        });

        for (const m of matches) {
          // Convertir en format outfit standard
          const items = m.items;
          if (!items || items.length < 3) continue; // outfit minimal = 3 pièces

          // Score outfit : trend_score banque + nb items isTrendy
          const trendyCount = items.filter(it => it?.ai_metadata?.isTrendy === true).length;
          const bankTrendScore = m.combination.trend_score || 75;
          const matchScore = m.matchScore || 0.5;

          // Phase C.3 v1.12.62 + D.3 v1.12.69 : calculer cohesion pour bank outfits
          let bankCohesion = null;
          try {
            const targetForCoh = (m.combination.style || prefs.styles?.[0] || '').toLowerCase() || null;
            bankCohesion = OutfitEngineComputeCohesion(items, tempC, targetForCoh, colorBank, materialBank);
          } catch (e) {
            console.warn('[Home] computeCohesion failed for bank outfit:', e.message);
          }

          bankOutfits.push({
            items,
            id: `bank_${m.combination.id || bankOutfits.length}`,
            score: Math.round(bankTrendScore * matchScore),
            cohesion: bankCohesion, // Phase C.3 v1.12.62 — pour debug box breakdown
            trendScore: bankTrendScore,
            trendyItemsCount: trendyCount,
            // v1.12.56 : exposé pour le debug box dans OutfitCard
            _matchScore: matchScore,
            _perItemScores: m.perItemScores || null,
            // Métadonnées de la banque (utiles pour afficher description/tips)
            _inspiration: {
              description: m.combination.description,
              style_tips: m.combination.style_tips,
              style: m.combination.style,
              occasion: m.combination.occasion,
              colors: m.combination.colors,
            },
            // bestTag pour la badge UI
            bestTag: (m.combination.occasion || '').toLowerCase(),
          });

          if (bankOutfits.length >= 100) break; // cap à 100 outfits bank-driven
        }
        console.log(`[Home] StyleBank → ${bankOutfits.length} outfits matérialisés depuis ${bankCombos.length} inspirations`);
      } catch (e) {
        console.warn('[Home] Bank matching failed:', e.message);
      }
    }

    // ═══ NIVEAU 2 : OutfitEngine combinatoire (TOUJOURS exécuté pour diversité) ═══
    //
    //   v1.9.33 : on lance TOUJOURS l'engine (avant : seulement si bankOutfits < 20).
    //   Raison : la banque pioche toujours les MÊMES top items du catalogue, donc
    //   après dédup par hero on tombe vite à 2-3 outfits. L'engine génère 1500
    //   outfits couvrant TOUTES les robes/hauts du catalogue, ce qui après dédup
    //   donne 30-50 outfits visuellement distincts.
    let engineOutfits = [];
    try {
      const seedTags = new Set();
      for (const it of sortedCatalog.slice(0, 200)) {
        const tags = Array.isArray(it.ai_metadata?.styleTags) ? it.ai_metadata.styleTags : [];
        for (const tag of tags) {
          if (typeof tag === 'string' && tag.trim()) {
            seedTags.add(tag.toLowerCase().trim());
          }
        }
      }
      if (prefs.occasion) seedTags.add(prefs.occasion.toLowerCase());
      const candidateTags = Array.from(seedTags).slice(0, 30);

      // v1.10.84 : Passer le style cible (1ère préférence user) à OutfitEngine
      // pour que computeCohesion vérifiez l'alignement de tous les items avec ce
      // style. Si pas de préférence style, on prend la 1ère catégorie d'occasion
      // ou null (cohérence générale).
      const targetStyle = (prefs.styles && prefs.styles[0])
        ? String(prefs.styles[0]).toLowerCase()
        : (prefs.occasion ? String(prefs.occasion).toLowerCase() : null);

      const generated = OutfitEngineGenerate({
        wardrobe: sortedCatalog,
        tempC,
        maxOutfits: 2000,
        candidateTags,
        guide: null,
        targetStyle,
        colorBank,    // Phase C.3 v1.12.61
        materialBank, // Phase D.3 v1.12.69
      });
      engineOutfits = generated.map((o, idx) => ({
        items: o.items,
        id: o.id || `engine_${idx}`,
        score: o.score,
        trendScore: o.trendScore,
        trendyItemsCount: o.trendyItemsCount,
        bestTag: o.bestTag,
        // v1.12.65 : préserver cohesion + tagMatches + targetTagMatch
        // (avant ces champs étaient strippés → debug box vide sur Sélection styliste)
        cohesion: o.cohesion,
        tagMatches: o.tagMatches,
        targetTagMatch: o.targetTagMatch,
      }));
    } catch (e) {
      console.warn('[Home] OutfitEngine failed:', e.message);
    }

    // ─── Combiner les 2 sources : banque d'abord (priorité), puis engine ───
    let allOutfits = [...bankOutfits, ...engineOutfits];

    // ═══ v1.9.42 : Filtre STRICT par catégories préférées (toutes catégories) ═══
    //
    //   Le filtre couvre 2 types de catégories :
    //
    //   A. STRUCTURELLES (forme l'ossature de la tenue) :
    //      - Robes : tenue centrée sur une robe
    //      - Hauts/Bas : tenue Haut+Bas+Chaussures
    //      → Logique exclusive : si "Robes" choisi → pas de Haut+Bas affichés.
    //      Si "Hauts/Bas" choisi → pas de robes affichées.
    //
    //   B. ACCESSOIRES (compléments à la tenue) :
    //      - Chaussures, Sacs, Bijoux, Vestes, Accessoires
    //      → Logique inclusive : chaque accessoire choisi DOIT être dans l'outfit.
    //      Ex: user choisit "Bijoux" + "Veste" → outfit doit avoir bijou ET veste.
    //
    //   Combiné : "Robes" + "Bijoux" → robe avec bijou présent.
    //
    //   Garde-fou : si AUCUN outfit ne matche, on garde tout pour ne pas afficher
    //   une liste vide.
    if (prefs.categories?.length > 0) {
      const STRUCTURAL = ['Robes', 'Hauts', 'Bas'];
      const ACCESSORIES = ['Chaussures', 'Sacs', 'Bijoux', 'Vestes', 'Manteaux', 'Accessoires'];

      const wantsRobes = prefs.categories.includes('Robes');
      const wantsHauts = prefs.categories.includes('Hauts');
      const wantsBas = prefs.categories.includes('Bas');
      const isStructuralPref = wantsRobes || wantsHauts || wantsBas;

      // Catégories accessoires choisies
      const requiredAccessories = prefs.categories.filter(c => ACCESSORIES.includes(c));

      const matching = allOutfits.filter(outfit => {
        const items = outfit.items || [];
        const itemCats = new Set(items.map(it => it?.category).filter(Boolean));
        const hasRobe = itemCats.has('Robes');
        const hasHaut = itemCats.has('Hauts');
        const hasBas = itemCats.has('Bas');

        // ─── Filtre structurel (exclusif) ───
        if (isStructuralPref) {
          if (wantsRobes && !wantsHauts && !wantsBas) {
            // Robes uniquement
            if (!hasRobe) return false;
          } else if ((wantsHauts || wantsBas) && !wantsRobes) {
            // v1.10.30 : Si Haut ET Bas sont demandés ensemble → exiger les 2
            // (sinon on accepterait des outfits avec juste un Haut sans Bas, ou inverse)
            if (hasRobe) return false;
            if (wantsHauts && wantsBas) {
              // Exiger les 2
              if (!hasHaut || !hasBas) return false;
            } else if (wantsHauts) {
              if (!hasHaut) return false;
            } else if (wantsBas) {
              if (!hasBas) return false;
            }
          } else {
            // Combinaison Robes + Hauts/Bas : on accepte les 2 types
            if (!hasRobe && !hasHaut && !hasBas) return false;
          }
        }

        // ─── Filtre accessoires (inclusif - chaque accessoire choisi doit être présent) ───
        for (const accCat of requiredAccessories) {
          if (!itemCats.has(accCat)) return false;
        }

        return true;
      });

      // Garde-fou : si AUCUN outfit ne matche, on garde tout
      if (matching.length >= 1) {
        allOutfits = matching;
        const accLabel = requiredAccessories.length > 0 ? ` + accessoires=[${requiredAccessories.join(',')}]` : '';
        console.log(`[Home] Filtre categories prefs STRICT : ${matching.length} matching (struct=${isStructuralPref}${accLabel}) sur ${bankOutfits.length + engineOutfits.length}`);
      } else {
        console.log(`[Home] Filtre categories prefs ignoré (0 matching, garde tout pour éviter liste vide)`);
      }
    }

    // v1.12.343 — Filtre STRICT par style préféré (seuil ≥1, avant ≥3)
    //   Si l'user a choisi 'chic' (ou autre), on garde seulement les outfits
    //   dont AU MOINS UN item a styleScores[style] >= 50.
    //   Garde-fou : si 0 match, on garde tout pour éviter liste vide.
    if (Array.isArray(prefs.styles) && prefs.styles.length > 0) {
      const stylesLower = prefs.styles.map(s => String(s).toLowerCase());
      const matchingStyle = allOutfits.filter(outfit => {
        const items = outfit.items || [];
        return items.some(it => {
          const scores = it?.ai_metadata?.styleScores || {};
          return stylesLower.some(s => (scores[s] || 0) >= 50);
        });
      });
      if (matchingStyle.length >= 1) {
        allOutfits = matchingStyle;
        console.log(`[Home] Filtre style prefs STRICT : ${matchingStyle.length} matching style=${stylesLower.join(',')}`);
      } else {
        console.log(`[Home] Filtre style prefs ignoré (0 matching — items pas encore analysés IA ?)`);
      }
    }

    // v1.12.343 — Filtre STRICT par occasion préférée (nouveau)
    //   Si l'user a choisi une occasion (Bureau, Soirée, etc.), on garde seulement
    //   les outfits dont AU MOINS UN item a occasions[occ] >= 50.
    //   Sans ce filtre, l'engine sortait des outfits "casual" alors que l'user
    //   demande "Soirée" → frustrant. Garde-fou : si 0 match, on garde tout.
    if (prefs.occasion) {
      const occLower = String(prefs.occasion).toLowerCase();
      const matchingOcc = allOutfits.filter(outfit => {
        const items = outfit.items || [];
        return items.some(it => {
          const occs = it?.ai_metadata?.occasions || {};
          const val = occs[occLower] || occs[prefs.occasion] || 0;
          return val >= 50;
        });
      });
      if (matchingOcc.length >= 1) {
        allOutfits = matchingOcc;
        console.log(`[Home] Filtre occasion prefs STRICT : ${matchingOcc.length} matching occasion=${occLower}`);
      } else {
        console.log(`[Home] Filtre occasion prefs ignoré (0 matching — items pas encore analysés IA ?)`);
      }
    }

    // ═══ v1.10.29 : Score de matching avec préférences (toutes les prefs) ═══
    //
    //   Avant : on triait juste par trendyItemsCount/trendScore/score → les outfits
    //   les plus "tendance" remontaient mais sans tenir compte des préférences
    //   utilisateur (style, couleur, occasion, budget).
    //
    //   Maintenant : pour chaque outfit, on calcule un "prefMatchScore" qui
    //   additionne les bonus selon combien d'items de l'outfit matchent les prefs :
    //     - Style préféré : +30 par item qui a ce style en >= 60
    //     - Occasion préférée : +25 par item qui a cette occasion en >= 60
    //     - Couleurs préférées : +20 par item dont la couleur correspond
    //     - Catégories voulues : +15 par item dans une catégorie souhaitée
    //     - Budget respecté : +10 par item dans le budget
    //
    //   Le tri final priorise prefMatchScore EN PREMIER, ce qui fait que la
    //   tenue qui matche le mieux les préférences apparaît tout en haut.
    const computePrefMatchScore = (outfit) => {
      if (!prefs || Object.keys(prefs).length === 0) return 0;
      const items = outfit.items || [];
      let score = 0;
      const userStyles = (prefs.styles || []).map(s => String(s).toLowerCase());
      const userColors = (prefs.colors || []).map(c => String(c).toLowerCase());
      const userCats = prefs.categories || [];
      const userOccasion = prefs.occasion ? String(prefs.occasion).toLowerCase() : null;
      const userBudget = prefs.budget; // string ou objet, ex 'budget'/'medium'/'premium' ou {min,max}

      for (const item of items) {
        if (!item) continue;
        const ai = item.ai_metadata || {};

        // Style match
        if (userStyles.length > 0) {
          const styleScores = ai.styleScores || {};
          const matched = userStyles.some(s => (styleScores[s] || 0) >= 60);
          if (matched) score += 30;
        }

        // Occasion match
        if (userOccasion) {
          const occScores = ai.occasions || {};
          const occVal = occScores[userOccasion] || occScores[prefs.occasion] || 0;
          if (occVal >= 60) score += 25;
          else if (occVal >= 40) score += 10;
        }

        // Couleur match
        if (userColors.length > 0) {
          const itemColor = String(ai.color || ai.primaryColor || item.color || '').toLowerCase();
          if (itemColor && userColors.some(c => itemColor.includes(c) || c.includes(itemColor))) {
            score += 20;
          }
        }

        // Catégorie match (en plus du filtre structurel déjà appliqué)
        if (userCats.length > 0 && userCats.includes(item.category)) {
          score += 15;
        }

        // Budget : item dans la fourchette
        if (typeof userBudget === 'object' && userBudget) {
          const price = Number(item.price) || 0;
          if (price > 0) {
            const min = userBudget.min ?? 0;
            const max = userBudget.max ?? Infinity;
            if (price >= min && price <= max) score += 10;
          }
        }
      }

      // Bonus additionnel : tenue 100% items tendance (couvre AUSSI le cas pas de prefs)
      const trendy = items.filter(it => it?.ai_metadata?.isTrendy === true).length;
      score += trendy * 5;

      return score;
    };

    // ═══ v1.12.215 : Score de compatibilité température (météo du jour) ═══
    //
    //   Sélection styliste prend en compte la température du jour pour
    //   privilégier les tenues adaptées :
    //     - froid (≤8°C)   : bonus si Manteaux présent, malus si rien de chaud
    //     - frais (9-15°C) : bonus si Veste OU Manteau présent
    //     - doux (16-22°C) : neutre
    //     - chaud (>22°C)  : malus si Manteaux/parka (hors saison)
    //
    //   Score de 0 à 100 (multiplié plus bas pour le tri).
    const computeTempScore = (outfit) => {
      const items = outfit.items || [];
      if (items.length === 0) return 50;
      const itemCats = new Set(items.map(it => it?.category).filter(Boolean));
      const hasManteau = itemCats.has('Manteaux');
      const hasVeste = itemCats.has('Vestes');
      const hasWarmLayer = hasManteau || hasVeste;

      let bonus = 50;
      if (tempC <= 8) {
        // Froid → on veut un manteau idéalement
        if (hasManteau) bonus += 35;
        else if (hasVeste) bonus += 10;
        else bonus -= 25;
      } else if (tempC <= 15) {
        // Frais → veste ou manteau bienvenu
        if (hasWarmLayer) bonus += 20;
        else bonus -= 5;
        if (hasManteau && tempC > 12) bonus -= 5; // manteau un peu trop chaud
      } else if (tempC <= 22) {
        // Doux → neutre, mais petite veste légère reste OK
        if (hasManteau) bonus -= 15; // manteau lourd pas adapté
      } else {
        // Chaud → on évite manteaux/vestes lourdes
        if (hasManteau) bonus -= 30;
        else if (hasVeste) bonus -= 8;
      }
      return Math.max(0, Math.min(100, bonus));
    };

    // Annoter chaque outfit avec son score de match prefs + température
    for (const o of allOutfits) {
      o.prefMatchScore = computePrefMatchScore(o);
      o.tempScore = computeTempScore(o);
    }

    // ═══ v1.12.215 : Filtre dur des outfits visiblement hors-saison ═══
    //   - tempScore < 25 : tenue trop décalée par rapport à la météo
    //   Garde-fou : si trop d'outfits filtrés (>80%), on garde tout
    const beforeTemp = allOutfits.length;
    const tempFiltered = allOutfits.filter(o => (o.tempScore || 0) >= 25);
    if (tempFiltered.length >= Math.max(20, beforeTemp * 0.2)) {
      allOutfits = tempFiltered;
      console.log(`[Home] Filtre temperature ${tempC}°C : ${tempFiltered.length}/${beforeTemp} outfits gardes (tempScore >= 25)`);
    }

    // ─── Tri final : prefMatchScore + tempScore, puis tendance ───
    //   v1.12.215 : tempScore intégré (poids 0.5 vs prefMatchScore) pour
    //   pousser en haut de liste les outfits adaptés à la météo du jour.
    allOutfits.sort((a, b) => {
      const aCombined = (a.prefMatchScore || 0) + (a.tempScore || 0) * 0.5;
      const bCombined = (b.prefMatchScore || 0) + (b.tempScore || 0) * 0.5;
      const combinedDiff = bCombined - aCombined;
      if (combinedDiff !== 0) return combinedDiff;
      const trendyDiff = (b.trendyItemsCount || 0) - (a.trendyItemsCount || 0);
      if (trendyDiff !== 0) return trendyDiff;
      const trendDiff = (b.trendScore || 0) - (a.trendScore || 0);
      if (trendDiff !== 0) return trendDiff;
      return (b.score || 0) - (a.score || 0);
    });

    // ═══ v1.9.29 : Déduplication par signature de tenue principale ═══
    //
    //   Problème : sans dédup, on peut avoir 2 outfits avec exactement la même
    //   base (Robe X + Chaussures Y) qui ne diffèrent que par les accessoires
    //   (Sac, Bijou, Veste, Accessoire). Visuellement, ils paraissent identiques.
    //
    //   Solution : on construit une "signature de tenue principale" pour chaque
    //   outfit qui ne contient QUE les pièces structurelles obligatoires :
    //     - Robe + Chaussures   (cas 1)
    //     - Haut + Bas + Chaussures (cas 2)
    //
    //   On garde uniquement le PREMIER outfit (= meilleur car la liste est triée)
    //   pour chaque signature. Les variantes d'accessoires sont éliminées.
    //
    //   Exemple :
    //     Outfit A (score 85) : Robe X + Chaussures Y + Sac Z + Bijou W
    //     Outfit B (score 78) : Robe X + Chaussures Y + Sac Z' + Bijou W'
    //     → Signature commune "robe-X|chaussures-Y" → on garde A, on supprime B
    //
    //   Helper : extrait une CLÉ NORMALISÉE pour chaque produit
    //   Utilise nom normalisé + brand + price (au lieu de l'id) pour matcher
    //   le même produit même s'il a plusieurs IDs (variantes de tailles).
    const productKey = (it) => {
      if (!it) return 'null';
      const name = normalizedNameKey(it.name || '');
      const brand = String(it.brand || it.partner || '').trim().toLowerCase();
      const price = Number(it.price) || 0;
      const cat = it.category || '';
      // Clé : catégorie|brand|nom|prix → identique pour variantes du même produit
      return `${cat}|${brand}|${name}|${price}`;
    };

    // ═══ v1.10.29 : Dédup au niveau HAUT+BAS (pas seulement HAUT) ═══
    //
    //   Avant (v1.9.32) : on dédupait par HAUT seulement → conséquence, le même
    //     BAS pouvait apparaître avec plein de hauts différents → boucle visuelle
    //     du bas (ex: jean noir × 10 hauts différents = 10 outfits avec ce jean).
    //
    //   Maintenant : la signature combine HAUT+BAS (les 2 pièces structurelles).
    //     Pour chaque paire (HAUT, BAS) unique, on garde la MEILLEURE tenue.
    //     Conséquence : un même bas n'apparaît qu'avec autant de hauts qu'on a
    //     de paires (haut, bas) distinctes dans le catalogue.
    //
    //   Robe reste prioritaire : si l'outfit a une robe, c'est elle le hero.
    //
    //   Exemple :
    //     Outfit A : Haut H1 + Bas B1 + Chaussures Y1 (score 75)
    //     Outfit B : Haut H1 + Bas B1 + Chaussures Y2 + Sac S (score 82) ⭐
    //     Outfit C : Haut H1 + Bas B2 + Chaussures Y1 (score 70)
    //     → On garde B (meilleure variante de la paire H1+B1)
    //     → On garde C (paire H1+B2 différente)
    //     → Bas B1 apparaît 1× au lieu de 2×
    const heroKey = (outfit) => {
      const items = outfit.items || [];
      // Priorité 1 : Robe (toujours hero quand présente)
      const robe = items.find(it => it?.category === 'Robes');
      if (robe) return `ROBE:${productKey(robe)}`;
      // Priorité 2 : Haut + Bas (combo structurel)
      const haut = items.find(it => it?.category === 'Hauts');
      const bas = items.find(it => it?.category === 'Bas');
      if (haut && bas) return `HAUT:${productKey(haut)}|BAS:${productKey(bas)}`;
      if (haut) return `HAUT:${productKey(haut)}`;
      if (bas) return `BAS:${productKey(bas)}`;
      // Fallback : utiliser toutes les pièces triées
      return items.map(it => productKey(it)).sort().join('||');
    };

    // ═══ v1.9.31 : Dédup + sélection de la MEILLEURE variante d'accessoires ═══
    //
    //   Pour chaque tenue principale unique (Robe+Chaussures ou Haut+Bas+Chaussures),
    //   on trouve TOUS les outfits qui partagent cette base, puis on sélectionne
    //   celui qui a la meilleure combinaison d'accessoires.
    //
    //   Critère du "meilleur" :
    //     1. Plus d'items tendance (DESC)
    //     2. Plus d'accessoires (Sac + Bijoux + Accessoires + Vestes) → richesse visuelle
    //     3. Score global (DESC)
    //
    //   Exemple :
    //     Base "Robe X + Chaussures Y" :
    //       Outfit A : + Sac Z + Bijou W (4 items, score 78)
    //       Outfit B : + Sac Z' (3 items, score 80)
    //       Outfit C : + Sac Z + Bijou W' + Veste V (5 items, score 85, 2 trendy) ⭐
    //     → On garde C (le plus complet, le plus tendance)
    const accessoryRichness = (outfit) => {
      const items = outfit.items || [];
      const ACCESSORY_CATS = new Set(['Sacs', 'Bijoux', 'Accessoires', 'Vestes']);
      return items.filter(it => ACCESSORY_CATS.has(it?.category)).length;
    };

    // Grouper les outfits par HERO ITEM (la pièce principale dominante)
    const groupedByHero = new Map();
    for (const outfit of allOutfits) {
      const sig = heroKey(outfit);
      if (!groupedByHero.has(sig)) groupedByHero.set(sig, []);
      groupedByHero.get(sig).push(outfit);
    }

    // Pour chaque groupe (= pour chaque robe/haut unique), choisir la MEILLEURE variante
    const dedupedOutfits = [];
    for (const [sig, group] of groupedByHero) {
      if (group.length === 1) {
        dedupedOutfits.push(group[0]);
        continue;
      }
      // Trier le groupe par "qualité de variante"
      group.sort((a, b) => {
        // 1. Plus d'items tendance
        const trendyDiff = (b.trendyItemsCount || 0) - (a.trendyItemsCount || 0);
        if (trendyDiff !== 0) return trendyDiff;
        // 2. Plus d'accessoires (richesse visuelle)
        const richDiff = accessoryRichness(b) - accessoryRichness(a);
        if (richDiff !== 0) return richDiff;
        // 3. Score global — avec tiebreaker cohesion.colorAll si écart < 2pts
        // (aligné LooksScreen v1.12.87 : un sac créant un rappel couleur ne
        //  doit pas perdre face à un sac "tag-fort" pour 1 point d'écart).
        const scoreDiff = (b.score || 0) - (a.score || 0);
        if (Math.abs(scoreDiff) < 2) {
          const colA = a.cohesion?.colorAll ?? a.cohesion?.color ?? 0;
          const colB = b.cohesion?.colorAll ?? b.cohesion?.color ?? 0;
          if (colB !== colA) return colB - colA;
        }
        if (scoreDiff !== 0) return scoreDiff;
        // 4. trendScore
        return (b.trendScore || 0) - (a.trendScore || 0);
      });
      dedupedOutfits.push(group[0]); // le meilleur du groupe
    }

    // ═══ v1.10.28 : Limites Bas (2) et Chaussures (5) RETIRÉES ═══
    //
    //   Avant (v1.10.10/v1.10.12), on plafonnait les répétitions d'un même bas
    //   à 2 et d'une même chaussure à 5 pour forcer la diversité visuelle.
    //
    //   Retiré : la dédup heroKey (Robe/Haut+Bas) garantit déjà l'unicité des
    //   pivots, et les utilisateurs préfèrent voir TOUTES les associations
    //   intéressantes plutôt que des outfits coupés artificiellement.
    //   Si un même pantalon noir va bien avec 6 hauts différents, on les montre.

    // Re-trier les outfits dédupliqués : prefMatchScore d'abord (v1.10.29)
    dedupedOutfits.sort((a, b) => {
      const prefDiff = (b.prefMatchScore || 0) - (a.prefMatchScore || 0);
      if (prefDiff !== 0) return prefDiff;
      const trendyDiff = (b.trendyItemsCount || 0) - (a.trendyItemsCount || 0);
      if (trendyDiff !== 0) return trendyDiff;
      const trendDiff = (b.trendScore || 0) - (a.trendScore || 0);
      if (trendDiff !== 0) return trendDiff;
      return (b.score || 0) - (a.score || 0);
    });

    // ═══ v1.9.36 : Round-robin par STYLE selon les préférences user ═══
    //
    //   Logique :
    //     - Si l'user a sélectionné des styles (ex: ["chic"] ou ["chic", "casual"]),
    //       on FAIT REMONTER les outfits qui matchent ces styles (80% si 1 style,
    //       50/50 si 2 styles, etc.).
    //     - Si pas de styles choisis, on revient au round-robin par occasion
    //       (CASUAL/CHIC/SOIRÉE/BUREAU/SPORT alternés).
    //
    //   Algo :
    //     1. Classifier chaque outfit par "style" (depuis bestTag IA + items)
    //     2. Si user a styles[] : prioriser ces styles (80%) + 20% autres
    //     3. Sinon : round-robin équilibré entre toutes les catégories
    //
    //   Exemple user choisit ["chic"] avec 50 outfits dispo :
    //     → 40 outfits classés "chic" + 10 autres alternés
    const classifyOutfitStyle = (outfit) => {
      const bestTag = (outfit.bestTag || '').toLowerCase();
      // Mapping bestTag → style preference (correspondance avec STYLES_OPTIONS dans modal)
      // Tags directs
      if (['chic', 'cérémonie', 'élégant'].includes(bestTag)) return 'chic';
      if (['casual', 'brunch', 'voyage'].includes(bestTag)) return 'casual';
      if (['bureau', 'business'].includes(bestTag)) return 'workwear';
      if (['soirée', 'soiree', 'date'].includes(bestTag)) return 'chic'; // soirée = chic
      if (['sport', 'plage'].includes(bestTag)) return 'sport';
      if (bestTag === 'romantique') return 'romantique';
      if (bestTag === 'bohème' || bestTag === 'boheme') return 'bohème';
      if (bestTag === 'streetwear') return 'streetwear';
      if (bestTag === 'minimaliste') return 'minimaliste';

      // Vérifier dans l'inspiration de la banque (priorité haute)
      const inspirationStyle = (outfit._inspiration?.style || '').toLowerCase();
      if (inspirationStyle) {
        if (inspirationStyle.includes('chic') || inspirationStyle.includes('classique') || inspirationStyle.includes('élégant')) return 'chic';
        if (inspirationStyle.includes('casual')) return 'casual';
        if (inspirationStyle.includes('business') || inspirationStyle.includes('bureau')) return 'workwear';
        if (inspirationStyle.includes('roman')) return 'romantique';
        if (inspirationStyle.includes('bohème') || inspirationStyle.includes('boheme')) return 'bohème';
        if (inspirationStyle.includes('street')) return 'streetwear';
        if (inspirationStyle.includes('minimal')) return 'minimaliste';
        if (inspirationStyle.includes('sport')) return 'sport';
      }

      // Fallback : déduire depuis styleScores des items (le plus haut score)
      const items = outfit.items || [];

      // ═══ v1.10.75 : Indicateurs catégoriels forts AVANT styleScores ═══
      //
      // Le styleScores est dominé par "minimaliste" pour beaucoup d'outfits homme
      // car les basiques (t-shirt blanc, jean droit) ont styleScores.minimaliste ~80.
      // On ajoute donc des règles catégorielles qui priment :
      //   - Sport → si un item est en "sport" avec score occasions.sport élevé
      //   - Workwear → si veste/blazer + chemise
      //   - Chic → si Robes+Bijoux ou Vestes structurées + score occasions.bureau/soiree élevé
      //   - Casual → si jean + t-shirt
      //   - Streetwear → si sweat/hoodie + sneakers + jogging
      const cats = new Set(items.map(it => it.category));
      const itemNames = items.map(it => (it.name || '').toLowerCase()).join(' ');

      // Cumul des scores d'occasions (sport, bureau, soirée)
      const occScores = { sport: 0, bureau: 0, soiree: 0, casual: 0, ceremonie: 0 };
      for (const it of items) {
        const occ = it?.ai_metadata?.occasions || {};
        for (const [k, v] of Object.entries(occ)) {
          occScores[k] = (occScores[k] || 0) + (Number(v) || 0);
        }
      }
      const numItems = items.length || 1;
      const avgOcc = {};
      for (const [k, v] of Object.entries(occScores)) avgOcc[k] = v / numItems;

      // Mots-clés sport / streetwear dans les noms
      const sportKws = /\b(survetement|survêtement|jogging|sport|tracksuit|short|legging|fitness|gym|training|run)\b/i;
      const streetKws = /\b(hoodie|sweat|sneakers?|baskets?|cap|bonnet|cargo|oversize)\b/i;

      // Sport : mots-clés OU score occasions.sport > 70
      if (sportKws.test(itemNames) || avgOcc.sport > 70) return 'sport';

      // Streetwear : mots-clés streetwear (hoodie + sneakers etc.)
      const streetMatches = (itemNames.match(streetKws) || []).length;
      if (streetMatches >= 2) return 'streetwear';

      // Cérémonie/Soirée : score occasions.soiree ou ceremonie > 70
      if (avgOcc.soiree > 70 || avgOcc.ceremonie > 70) {
        if (cats.has('Robes')) return 'romantique';
        return 'chic';
      }

      // Bureau : score occasions.bureau > 70 OU veste + chemise/pantalon
      const hasVeste = cats.has('Vestes');
      const hasChemise = /chemise|shirt(?!\s*shirt)/i.test(itemNames);
      const hasJean = /jean/i.test(itemNames);
      if (avgOcc.bureau > 70 && hasVeste) return 'workwear';
      if (hasVeste && hasChemise && !hasJean) return 'workwear';

      // Casual : jean + t-shirt OU score casual très haut
      const hasTshirt = /t-?shirt|tee/i.test(itemNames);
      if (hasJean && hasTshirt) return 'casual';
      if (avgOcc.casual > 80 && avgOcc.bureau < 60) return 'casual';

      // Sinon, regarder styleScores agrégé (top style si > 60)
      const styleScores = {};
      for (const it of items) {
        const ss = it?.ai_metadata?.styleScores || {};
        for (const [k, v] of Object.entries(ss)) {
          styleScores[k] = (styleScores[k] || 0) + v;
        }
      }
      const sorted = Object.entries(styleScores).sort((a, b) => b[1] - a[1]);
      // Ne pas accepter "minimaliste" automatiquement — il est sur-représenté.
      // On l'accepte seulement s'il domine vraiment (>40% écart avec le 2e).
      if (sorted.length > 0 && sorted[0][1] > 60) {
        const top = sorted[0][0];
        if (top !== 'minimaliste') return top;
        // minimaliste : seulement si écart fort avec le 2e style
        if (sorted.length === 1 || sorted[0][1] > sorted[1][1] * 1.4) return top;
        // Sinon, prendre le 2e style si > 60
        if (sorted[1] && sorted[1][1] > 60) return sorted[1][0];
      }

      // Dernier fallback : déduire depuis catégories d'items
      if (cats.has('Robes') && cats.has('Bijoux')) return 'chic';
      if (cats.has('Robes')) return 'romantique';
      if (hasVeste) return 'workwear';
      return 'casual';
    };

    const userStyles = (prefs.styles || []).map(s => String(s).toLowerCase());

    // Grouper par style
    const byStyle = new Map();
    for (const outfit of dedupedOutfits) {
      const st = classifyOutfitStyle(outfit);
      // v1.10.10 : stocker le style classifié sur l'outfit pour que le badge l'utilise
      outfit._classifiedStyle = st;
      if (!byStyle.has(st)) byStyle.set(st, []);
      byStyle.get(st).push(outfit);
    }

    let interleaved = [];

    if (userStyles.length > 0) {
      // ─── Mode "préférences user" : 80% des styles choisis + 20% autres ───
      //
      //   Exemple user choisit ["chic"] :
      //     → 4 outfits "chic" puis 1 outfit autre, en boucle
      //   Exemple user choisit ["chic", "casual"] :
      //     → 2 chic, 2 casual, 1 autre, en boucle (= 80% pref / 20% autres)
      //   Exemple user choisit ["chic", "casual", "sport"] :
      //     → 1 de chacun (chic, casual, sport), 1 autre, en boucle
      const preferredQueues = userStyles.map(s => byStyle.get(s) || []).filter(q => q.length > 0);
      const otherStylesKeys = [...byStyle.keys()].filter(k => !userStyles.includes(k));
      const otherQueues = otherStylesKeys.map(k => byStyle.get(k));

      // Round-robin : 4 préférés (1 de chaque queue préférée tour à tour) + 1 autre
      // Adapté pour ne pas dépasser le ratio 80/20
      const PREFERRED_PER_CYCLE = 4;
      const OTHER_PER_CYCLE = 1;

      let cycle = 0;
      while (true) {
        let addedThisCycle = 0;

        // Phase 1 : ajouter 4 outfits préférés (1 de chaque queue préférée tour à tour)
        for (let i = 0; i < PREFERRED_PER_CYCLE; i++) {
          const queueIdx = i % preferredQueues.length;
          const q = preferredQueues[queueIdx];
          if (q && q.length > 0) {
            interleaved.push(q.shift());
            addedThisCycle++;
          }
        }

        // Phase 2 : ajouter 1 outfit "autre" pour la diversité
        for (let i = 0; i < OTHER_PER_CYCLE; i++) {
          // Trouver la 1ère queue "autre" non vide
          for (const q of otherQueues) {
            if (q && q.length > 0) {
              interleaved.push(q.shift());
              addedThisCycle++;
              break;
            }
          }
        }

        if (addedThisCycle === 0) break; // toutes les queues sont vides
        cycle++;
        // Sécurité : ne pas tourner à l'infini
        if (cycle > dedupedOutfits.length + 10) break;
      }
    } else {
      // ─── Mode "pas de préférences" : round-robin équilibré entre tous styles ───
      const ORDER = ['chic', 'casual', 'workwear', 'romantique', 'minimaliste', 'streetwear', 'bohème', 'sport'];
      let remaining = true;
      while (remaining) {
        remaining = false;
        for (const st of ORDER) {
          const queue = byStyle.get(st);
          if (queue && queue.length > 0) {
            interleaved.push(queue.shift());
            remaining = true;
          }
        }
        // Aussi traiter les styles qui ne sont pas dans ORDER
        for (const [st, queue] of byStyle) {
          if (ORDER.includes(st)) continue;
          if (queue.length > 0) {
            interleaved.push(queue.shift());
            remaining = true;
          }
        }
      }
    }

    const styleBreakdown = Object.fromEntries(
      Array.from(interleaved.slice(0, 20).reduce((m, o) => {
        const s = classifyOutfitStyle(o);
        m.set(s, (m.get(s) || 0) + 1);
        return m;
      }, new Map()))
    );
    const removedCount = allOutfits.length - dedupedOutfits.length;
    console.log(`[Home] Sélection styliste : ${interleaved.length} outfits uniques (${bankOutfits.length} bank + ${engineOutfits.length} engine, ${removedCount} doublons retirés). Prefs styles=${JSON.stringify(userStyles)}. Top 20 par style :`, styleBreakdown);

    // v1.12.71 — Tri par score DESC + filtre >= 50% (meilleures tenues en premier,
    // outfits faibles cachés). Remplace le round-robin pour la liste finale.
    // v1.12.119 — cap à 300 max (les 300 meilleurs scores).
    const QUALITY_MIN = 50;
    const STYLIST_MAX = 300;
    const finalOutfits = interleaved
      .filter(o => (o.score || 0) >= QUALITY_MIN)
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, STYLIST_MAX);
    console.log(`[Home] Sélection styliste : ${finalOutfits.length}/${interleaved.length} outfits >= ${QUALITY_MIN}% (triés DESC, capé à ${STYLIST_MAX})`);
    return finalOutfits;
  }, [catalogItems, currentDailyPrefs, temperature, styleBank]);
  // ↑ useCallback qui calcule synchroniquement quand appelée
  //   (computeStylistOutfits). L'invocation est déférée par le useEffect
  //   ci-dessus pour ne pas bloquer le main thread au mount.

  const SmartImage = useCallback(({ uri, style, fallback }) => {
    const [attempt, setAttempt] = useState(0); // 0=wsrv.nl resize, 1=weserv.nl resize, 2=URL brute (fallback), 3=dead
    if (!uri || attempt >= 3) return (
      <View style={[style, { backgroundColor: '#F5F5F5', alignItems:'center', justifyContent:'center' }]}>
        <Text style={{ fontSize: 22 }}>{fallback || '👗'}</Text>
      </View>
    );
    // ═══ v1.12.749 — Resize TOUS les partenaires via wsrv.nl w=400 ═══
    //   Avant : seuls Zara + TOUS étaient proxyfiés. Les autres (COUTR,
    //   iQueens, Drestige, etc.) chargeaient des images 1200×1200 brutes
    //   → ~200-500 KB/image × 120 images sur HomeScreen = ~30 MB → lenteur Android.
    //   Maintenant : tout passe par wsrv.nl avec w=400 → ~16 KB/image → gain 10-20×.
    //   Exception : URLs déjà sur outfit-tryon-cache S3 (notre propre cache d'images
    //   déjà optimisées) → on garde l'URL brute.
    const isOwnS3 = uri.includes('outfit-tryon-cache.s3.');
    const isAlreadyResized = /[?&](w|width|h|height)=/.test(uri);
    let src;
    if (isOwnS3 || isAlreadyResized) {
      // Pas besoin de proxy
      src = uri;
    } else if (attempt === 0) {
      src = 'https://wsrv.nl/?url=' + encodeURIComponent(uri) + '&w=400';
    } else {
      // v1.13.77 — attempt 1 = URL BRUTE directement. Avant : attempt 1 =
      // images.weserv.nl = LE MÊME service que wsrv.nl (attempt 0) → 2ᵉ échec
      // redondant qui retardait le markImageDead. Maintenant on teste la source
      // réelle dès le 2ᵉ essai → détection "image morte" 1 échec plus tôt.
      src = uri;
    }
    return (
      <ExpoImageSafe
        source={{ uri: src }}
        style={style}
        contentFit={(Array.isArray(style) ? style.some(x => x?.resizeMode === 'contain') : style?.resizeMode === 'contain') ? 'contain' : 'cover'}
        cachePolicy="memory-disk"
        transition={150}
        recyclingKey={uri}
        onError={() => setAttempt(a => {
          const next = Math.min(a + 1, 3);
          // v1.13.43 — dernier essai (URL brute) échoué → image morte (404) →
          // registre global → l'outfit est exclu de la sélection styliste (via
          // hasUsableImage → drop) et la tuile catalogue est retirée.
          if (next >= 3) markImageDead(uri);
          return next;
        })}
      />
    );
  }, []);

  return (
    <SafeAreaView style={[s.safe, { backgroundColor: theme.bg }]}>
      {/* Header */}
      <View style={[s.header, { backgroundColor: theme.bg }]}>
        <View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <MyTenueLogo dark={dark} />
            <Text style={{ fontSize: 10, color: '#D4AF72', letterSpacing: 2.5, textTransform: 'uppercase', fontWeight: '600' }}>
              {t('your_stylist_ai_caps')}
            </Text>
          </View>
        </View>
        <View style={s.headerRight}>

          {/* v1.12.190 + v1.12.213 : bouton Calendrier unifié (icône SVG + tooltip web) */}
          <Tooltip text={t('outfit_calendar_tooltip')}>
            <TouchableOpacity
              style={[s.avatar, { backgroundColor: theme.bgRaised }]}
              onPress={() => navigation.navigate('Calendar')}
              accessibilityLabel={t('outfit_calendar_label')}
            >
              <CalendarIcon size={22} color={theme.text} dot="#D4AF72" />
            </TouchableOpacity>
          </Tooltip>
        </View>
      </View>
      <View style={[s.divider, { backgroundColor: theme.bgBorder }]} />

      <ScrollView
        ref={scrollRef}
        showsVerticalScrollIndicator={false}
        style={{ backgroundColor: theme.bg }}
        contentContainerStyle={{ paddingBottom: 50, backgroundColor: theme.bg }}
        refreshControl={
          <ThemedRefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        onScroll={handleFabScroll}
        // v1.12.904 — Perf scroll Android : décharge sous-vues hors viewport
        removeClippedSubviews={Platform.OS === 'android'}
        scrollEventThrottle={16}
      >

        {/* ═══ v1.12.216 — En-tête Accueil : présentation premium luxe ═══
            • Salutation en italique gold au-dessus du prénom
            • Prénom large en serif-ish (light/300) avec letter-spacing
            • Filet doré minimal sous le prénom (1px, 32px de large)
            • Bouton "Préférences du jour" en pill gold à droite
            • Bandeau météo en chip ivoire arrondie : icône SVG + temp + ville + desc */}
        <View style={s.greetBlock}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <View style={{ flex: 1, paddingRight: 12 }}>
              <Text style={[s.greetSubElegant, { color: '#9B7B5A' }]}>
                {greet}
              </Text>
              <Text style={[s.greetNameElegant, { color: theme.text }]} numberOfLines={1}>
                {name}
              </Text>
              <View style={s.greetGoldRule} />
            </View>
            <Tooltip text={t('daily_prefs_tooltip')}>
              <TouchableOpacity
                onPress={() => setShowPrefsModal(true)}
                activeOpacity={0.75}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6,
                  height: 40,
                  paddingHorizontal: 12,
                  borderRadius: 20,
                  backgroundColor: '#FFF7E0',
                  borderWidth: 1, borderColor: '#D4AF72',
                  marginTop: 4,
                  shadowColor: '#D4AF72',
                  shadowOpacity: 0.18,
                  shadowRadius: 6,
                  shadowOffset: { width: 0, height: 2 },
                  elevation: 2,
                }}>
                <PreferencesIcon size={18} color={theme.text} accent="#D4AF72" />
                <Text style={{ fontSize: 12, fontWeight: '600', color: theme.text, letterSpacing: 0.3 }}>
                  {t('daily_short')}
                </Text>
              </TouchableOpacity>
            </Tooltip>
          </View>

          {/* Bandeau météo — chip ivoire avec icône SVG + temp + ville + desc */}
          <View style={s.weatherChip}>
            <WeatherIcon size={18} color={theme.text} accent="#D4AF72" desc={weatherDesc} tempC={temperature} isDay={weatherIsDay} />
            <Text style={[s.weatherTemp, { color: theme.text }]}>
              {temperature}°
            </Text>
            {locationCity ? (
              <>
                <View style={s.weatherSep} />
                <Text style={[s.weatherCity, { color: theme.text }]} numberOfLines={1}>
                  {locationCity}
                </Text>
              </>
            ) : null}
            {weatherDesc ? (
              <>
                <View style={s.weatherSep} />
                <Text style={[s.weatherDesc2, { color: theme.textMuted }]} numberOfLines={1}>
                  {weatherDesc}
                </Text>
              </>
            ) : null}
            {!locationReady && (
              <ActivityIndicator size="small" color={theme.textMuted} style={{ marginLeft: 6 }} />
            )}
          </View>
        </View>

        {/* ═══ v1.12.96+ — Tenue du jour (replaces dressing card) ═══ */}
        {/* v1.12.100 — 3 états : */}
        {/*   1. Outfit normal (matche les prefs ou pas de prefs)              */}
        {/*   2. Outfit "approximatif" (prefs user impossibles à matcher)       */}
        {/*   3. CTA "Ajoutez des articles" (dressing trop partiel pour générer) */}

        {/* État 3 : Pas assez d'articles dans le dressing */}
        {dressingCount > 0 && homeOutfit?._needsMore && (
          <View style={s.section}>
            <View style={s.stylistHeader}>
              <View style={s.stylistHeaderLeft}>
                <Text style={s.stylistBadgeSmall}>{user?.gender === 'M' ? '🧥' : '👗'} {t('outfit_of_day_section')}</Text>
                <Text style={[s.stylistTitle, { color: theme.text }]}>{t('from_your_wardrobe')}</Text>
              </View>
            </View>
            <TouchableOpacity
              style={[{
                backgroundColor: '#FAFAFA',
                borderRadius: radius.lg,
                borderWidth: 1, borderColor: colors.bg.border,
                padding: spacing.lg,
                alignItems: 'center',
                gap: 10,
              }, shadow.sm]}
              onPress={() => navigation.navigate('Garde-robe')}
              activeOpacity={0.85}
            >
              <Text style={{ fontSize: 36 }}>🧺</Text>
              <Text style={{ fontSize: 14, fontWeight: '700', color: colors.text.primary, textAlign: 'center' }}>
                {t('not_enough_items')}
              </Text>
              <Text style={{ fontSize: 12, color: colors.text.secondary, textAlign: 'center', lineHeight: 17 }}>
                {t('add_items_for_outfit')}
              </Text>
              <View style={{
                marginTop: 4,
                paddingHorizontal: 16, paddingVertical: 10,
                backgroundColor: '#1A1A1A', borderRadius: radius.md,
                borderWidth: 1, borderColor: '#D4AF72',
              }}>
                <Text style={{ fontSize: 11, fontWeight: '700', color: '#D4AF72', letterSpacing: 1 }}>
                  {t('add_an_item_caps')}
                </Text>
              </View>
            </TouchableOpacity>
          </View>
        )}

        {/* États 1 et 2 : outfit valide (avec ou sans matching exact des prefs) */}
        {dressingCount > 0 && homeOutfit && !homeOutfit._needsMore && (() => {
          const items = homeOutfit.items || [];
          // v1.12.124 : Bijoux + Accessoires caches par defaut, autres
          // toujours visibles. Bouton "Afficher plus" -> tout afficher.
          const MAIN_CATS = new Set(['Robes', 'Hauts', 'Bas', 'Vestes', 'Chaussures', 'Sacs']);
          const ACC_CATS  = new Set(['Bijoux', 'Accessoires']);
          const mainItems = items.filter(it => MAIN_CATS.has(it.category));
          const accItems  = items.filter(it => ACC_CATS.has(it.category));
          // Tri logique d'un look complet
          const categoryOrder = (cat) => {
            const c = (cat || '').toLowerCase();
            if (/robe|dress/.test(c)) return 1;
            if (/haut|top|chemise|t-?shirt|pull|veste|sweat|polo|blouson|manteau|cardigan/.test(c)) return 2;
            if (/bas|pantalon|jupe|short|jeans/.test(c)) return 3;
            if (/chaussure|shoe|basket|sneaker|boot/.test(c)) return 4;
            if (/sac|bag/.test(c)) return 5;
            if (/accessoire|access/.test(c)) return 6;
            if (/bijou|jewel/.test(c)) return 7;
            return 8;
          };
          const visibleItems = (homeOutfitShowAll ? items : mainItems)
            .slice()
            .sort((a, b) => categoryOrder(a.category) - categoryOrder(b.category));
          if (visibleItems.length === 0) return null;

          // v1.12.131 — Piece principale a GAUCHE, autres a DROITE en colonne.
          const hero = visibleItems[0];
          const rest = visibleItems.slice(1);
          const restN = rest.length;

          return (
            <View style={s.section}>
              {/* Header */}
              <View style={s.stylistHeader}>
                <View style={s.stylistHeaderLeft}>
                  <Text style={s.stylistBadgeSmall}>{user?.gender === 'M' ? '🧥' : '👗'} {t('outfit_of_day_section')}</Text>
                  <Text style={[s.stylistTitle, { color: theme.text }]}>
                    {homeOutfit._approxPrefs ? t('closest_to_taste') : t('from_your_wardrobe')}
                  </Text>
                </View>
                <TouchableOpacity onPress={() => setHomeOutfitReroll(n => n + 1)}>
                  <Text style={[s.stylistSeeAll, { color: theme.textMuted }]}>{t('regenerate_arrow')}</Text>
                </TouchableOpacity>
              </View>
              {homeOutfit._approxPrefs && (
                <Text style={{
                  fontSize: 11, color: colors.text.faint,
                  paddingHorizontal: spacing.md, marginTop: -4, marginBottom: 8,
                  fontStyle: 'italic',
                }}>
                  {t('no_matching_outfit')}
                </Text>
              )}

              {/* v1.12.224 — Reasons de l'agent OutfitOfTheDay (bullets premium) */}
              {homeOutfit._agent?.reasons?.length > 0 && (
                <View style={{
                  marginHorizontal: spacing.md, marginBottom: 10,
                  paddingVertical: 10, paddingHorizontal: 12,
                  backgroundColor: '#FBF7EE',
                  borderRadius: 12,
                  borderWidth: 1, borderColor: 'rgba(212,175,114,0.35)',
                  gap: 4,
                }}>
                  {/* v1.12.225 — Badges agent (PERFECT MATCH / GOOD PRICE / SEASON FIT…) */}
                  {homeOutfit._agent?.badges?.length > 0 && (
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                      {homeOutfit._agent.badges.map((b, i) => (
                        <View key={i} style={{
                          paddingHorizontal: 8, paddingVertical: 3,
                          borderRadius: 10, backgroundColor: '#FFFFFF',
                          borderWidth: 1, borderColor: '#D4AF72',
                        }}>
                          <Text style={{ fontSize: 10, fontWeight: '700', color: '#B8860B', letterSpacing: 0.4 }}>
                            {b}
                          </Text>
                        </View>
                      ))}
                    </View>
                  )}
                  {homeOutfit._agent.reasons.slice(0, 5).map((r, i) => (
                    <Text key={i} style={{ fontSize: 12, color: theme.text, lineHeight: 18 }}>
                      {r}
                    </Text>
                  ))}
                </View>
              )}

              {/* Card tenue */}
              <View style={[{
                backgroundColor: '#FFFFFF',
                borderRadius: radius.lg,
                borderWidth: 1, borderColor: colors.bg.border,
                overflow: 'hidden',
              }, shadow.md]}>
                {/* v1.12.131 : piece principale a GAUCHE, autres a DROITE */}
                <View style={{ padding: spacing.md }}>
                  {restN === 0 ? (
                    <TouchableOpacity
                      style={{
                        width: '100%',
                        aspectRatio: 1,
                        backgroundColor: '#F5F2ED',
                        borderRadius: radius.md,
                        overflow: 'hidden',
                        position: 'relative',
                      }}
                      onPress={() => setSelectedWardrobeItem(hero)}
                      activeOpacity={0.85}>
                      {hero.image
                        ? <SmartImage uri={hero.image} style={{ width:'100%', height:'100%', resizeMode:'contain' }} fallback={CAT_ICONS[hero.category] || '👗'} />
                        : <View style={{ flex:1, alignItems:'center', justifyContent:'center' }}>
                            <Text style={{ fontSize: 56 }}>{CAT_ICONS[hero.category] || '👗'}</Text>
                          </View>
                      }
                      <View style={{
                        position:'absolute', bottom: 0, left: 0, right: 0,
                        backgroundColor: 'rgba(0,0,0,0.65)',
                        paddingVertical: 8, paddingHorizontal: 12,
                      }}>
                        <Text style={{ fontSize: 11, fontWeight:'700', color:'#FFD700', letterSpacing: 0.8, textAlign:'center' }} numberOfLines={1}>
                          ⭐ {tCatUpper(hero.category)} — {t('main_piece_caps')}
                        </Text>
                      </View>
                    </TouchableOpacity>
                  ) : (
                    <View style={{ flexDirection: 'row', gap: 8, alignItems: 'stretch' }}>
                      {/* GAUCHE — piece principale */}
                      <TouchableOpacity
                        style={{
                          width: '52%',
                          aspectRatio: 4 / 5,
                          backgroundColor: '#F5F2ED',
                          borderRadius: radius.md,
                          overflow: 'hidden',
                          position: 'relative',
                        }}
                        onPress={() => setSelectedWardrobeItem(hero)}
                        activeOpacity={0.85}>
                        {hero.image
                          ? <SmartImage uri={hero.image} style={{ width:'100%', height:'100%', resizeMode:'contain' }} fallback={CAT_ICONS[hero.category] || '👗'} />
                          : <View style={{ flex:1, alignItems:'center', justifyContent:'center' }}>
                              <Text style={{ fontSize: 56 }}>{CAT_ICONS[hero.category] || '👗'}</Text>
                            </View>
                        }
                        <View style={{
                          position:'absolute', bottom: 0, left: 0, right: 0,
                          backgroundColor: 'rgba(0,0,0,0.65)',
                          paddingVertical: 8, paddingHorizontal: 10,
                        }}>
                          <Text style={{ fontSize: 10, fontWeight:'700', color:'#FFD700', letterSpacing: 0.6, textAlign:'center' }} numberOfLines={2}>
                            ⭐ {tCatUpper(hero.category)}
                          </Text>
                        </View>
                      </TouchableOpacity>

                      {/* DROITE — autres pieces */}
                      <View style={
                        restN >= 5
                          ? { flex: 1, flexDirection: 'row', flexWrap: 'wrap', alignContent: 'space-between', gap: 6 }
                          : { flex: 1, flexDirection: 'column', gap: 6 }
                      }>
                        {rest.map((p, j) => (
                          <TouchableOpacity
                            key={p.id || `i_${j}`}
                            style={
                              restN >= 5
                                ? { width: '48%', aspectRatio: 1, backgroundColor: '#F5F2ED', borderRadius: radius.md, overflow: 'hidden', position: 'relative' }
                                : { flex: 1, minHeight: 56, backgroundColor: '#F5F2ED', borderRadius: radius.md, overflow: 'hidden', position: 'relative' }
                            }
                            onPress={() => setSelectedWardrobeItem(p)}
                            activeOpacity={0.85}>
                            {p.image
                              ? <SmartImage uri={p.image} style={{ width:'100%', height:'100%', resizeMode:'contain' }} fallback={CAT_ICONS[p.category] || '👗'} />
                              : <View style={{ flex:1, alignItems:'center', justifyContent:'center' }}>
                                  <Text style={{ fontSize: 28 }}>{CAT_ICONS[p.category] || '👗'}</Text>
                                </View>
                            }
                            <View style={{
                              position:'absolute', bottom: 0, left: 0, right: 0,
                              backgroundColor: 'rgba(0,0,0,0.55)',
                              paddingVertical: 4, paddingHorizontal: 6,
                            }}>
                              <Text style={{ fontSize: 9, fontWeight:'700', color:'#fff', letterSpacing: 0.5, textAlign:'center' }} numberOfLines={1}>
                                {p.category === 'Bijoux' ? `💍 ${t('cat_jewelry').toUpperCase()}` :
                                 p.category === 'Accessoires' ? `🧣 ${t('cat_accessories_short').toUpperCase()}` :
                                 tCatUpper(p.category)}
                              </Text>
                            </View>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>
                  )}
                </View>

                {/* Voir plus / moins (accessoires + bijoux) */}
                {accItems.length > 0 && (
                  <TouchableOpacity
                    onPress={() => setHomeOutfitShowAll(v => !v)}
                    activeOpacity={0.75}
                    style={{
                      marginHorizontal: spacing.md, marginBottom: 8,
                      paddingVertical: 10, borderRadius: radius.md,
                      backgroundColor: '#FAFAFA',
                      borderWidth: 1, borderColor: colors.bg.border,
                      alignItems: 'center',
                    }}>
                    <Text style={{ fontSize: 11, fontWeight: '600', color: colors.text.secondary, letterSpacing: 1 }}>
                      {homeOutfitShowAll
                        ? t('hide_accessories_caps')
                        : t('view_accessories_jewelry_caps').replace('{count}', accItems.length)}
                    </Text>
                  </TouchableOpacity>
                )}

                {/* v1.12.250 — Boutons Essai + Flat-lay + Ajouter au calendrier (raccourci) */}
                <View style={{
                  flexDirection: 'row', gap: 8,
                  marginHorizontal: spacing.md, marginBottom: spacing.md,
                }}>
                  <TouchableOpacity
                    style={{
                      flex: 1,
                      backgroundColor: '#1A1A1A',
                      paddingVertical: 12,
                      borderRadius: radius.md, alignItems: 'center', justifyContent:'center',
                      borderWidth: 1, borderColor: '#D4AF72',
                      flexDirection: 'row', gap: 8,
                    }}
                    onPress={() => showTryOnChoice(homeOutfit)}
                    activeOpacity={0.85}>
                    <Text style={{ fontSize: 12, fontWeight: '700', color: '#D4AF72', letterSpacing: 1 }}>
                      ✨ {t('try_on_caps')}
                    </Text>
                  </TouchableOpacity>
                  {/* v1.12.734 — Flat-lay (Vertex AI) */}
                  <FlatLayButton outfit={homeOutfit} style={{ borderRadius: radius.md }} />
                  <Tooltip text={t('add_outfit_to_calendar_tooltip')}>
                    <TouchableOpacity
                      style={{
                        width: 44, height: 44, borderRadius: 22,
                        backgroundColor: theme.bgRaised || '#F5EFE8',
                        alignItems: 'center', justifyContent: 'center',
                      }}
                      onPress={() => {
                        if (!homeOutfit?.items?.length) return;
                        setCalendarShortcut({
                          look: {
                            id: homeOutfit.id,
                            name: homeOutfit._agent?.explanation ? t('outfit_of_day_section') : (homeOutfit.name || t('outfit_of_day_section')),
                            items: (homeOutfit.items || []).map(it => ({
                              id: it.id, name: it.name, category: it.category,
                              image_url: it.image_url || it.image, brand: it.brand,
                            })),
                            occasion: currentDailyPrefs?.occasion || null,
                          },
                          source: 'tenue_du_jour',
                        });
                      }}
                      activeOpacity={0.8}>
                      <CalendarIcon size={22} color={theme.text} dot="#D4AF72" />
                    </TouchableOpacity>
                  </Tooltip>
                </View>
              </View>
            </View>
          );
        })()}

        {/* v1.13.x — Tenue du jour masquée quand RIEN à afficher (dressing vide ET
            aucune tenue dans "mes looks"). Sinon on garde la carte CTA. */}
        {dressingCount === 0 && (userCreatedLooks?.length || 0) > 0 && (
          // v1.12.309 — dressing vide : CTA "Créer un look" (depuis catalogue) au lieu de "Ajouter au dressing"
          <TouchableOpacity style={[s.emptyDressing, shadow.sm]}
            onPress={() => setShowCreateLook(true)}
            activeOpacity={0.85}>
            <Text style={s.emptyDressingIcon}>✨</Text>
            <Text style={s.emptyDressingTitle}>{t('create_a_look')}</Text>
            <Text style={s.emptyDressingDesc}>{t('empty_wardrobe_compose_from_catalog')}</Text>
            <View style={s.emptyDressingPlusBtn}>
              <Text style={s.emptyDressingPlusIcon}>✨</Text>
              <Text style={s.emptyDressingPlusText}>{t('create_a_look')}</Text>
            </View>
          </TouchableOpacity>
        )}

        {/* ═══ FIX 2026-04 : sections Tendances + Styliste sorties du guard dressingCount > 0 ═══ */}
        {/* Raison : avant, ces sections étaient masquées pour les nouveaux users (0 items dressing).
            Maintenant elles s'affichent dès qu'un catalogue est chargé, peu importe le dressing. */}

            {/* ═══════ SÉLECTION STYLISTE — TENUES COMPLÈTES (redesign) ═══════ */}
            {/* v1.12.784 — paddingHorizontal: spacing.xl pour aligner avec Tenue du jour (s.section) */}
            {/* v1.13.40 — Afficher la section AUSSI quand la styliste charge / a du
                contenu (même si le catalogue local n'est pas encore prêt) → le
                loading "Préparation de votre sélection…" s'affiche au 1er démarrage
                au lieu d'une section masquée. */}
            {(catalogItems.length > 0 || stylistOutfitsGen.length > 0 || stylistFetching) && (
              <View style={{ marginTop: spacing.xl * 1.2, paddingHorizontal: spacing.xl }}>
                {/* Header premium avec badge étoilé et sous-titre */}
                <View style={{
                  flexDirection: 'row', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: spacing.lg,
                }}>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <View style={{ width: 4, height: 14, backgroundColor: '#D4AF72', borderRadius: 2 }} />
                      <Text style={{ fontSize: 9, letterSpacing: 3, color: '#B8860B', fontWeight: '700' }}>
                        {t('complete_outfits')}
                      </Text>
                    </View>
                    <Text style={{ fontSize: 24, fontWeight: '300', color: theme.text, letterSpacing: 0.3, marginTop: 2 }}>
                      {t('stylist_selection')}
                    </Text>
                    {/* ═══ Sous-titre adapté aux préférences ═══ */}
                    {/* Affiche les préférences actives (catégories, occasion, style, budget) */}
                    {/* pour que le user voie clairement à quoi correspond la sélection. */}
                    {(() => {
                      const prefs = currentDailyPrefs || {};
                      const parts = [];
                      // v1.12.561 — Traduction i18n des préférences
                      const CAT_KEY = {
                        Hauts: 'cat_tops', Bas: 'cat_bottoms', Robes: 'cat_dresses',
                        Vestes: 'cat_jackets', Manteaux: 'cat_coats', Chaussures: 'cat_shoes',
                        Sacs: 'cat_bags', Accessoires: 'cat_accessories', Bijoux: 'cat_jewelry',
                        Maillots: 'cat_swimwear',
                      };
                      const STYLE_KEY = {
                        'chic':'style_chic','élégant':'style_elegant','elegant':'style_elegant',
                        'casual':'style_casual','minimaliste':'style_minimal','minimal':'style_minimal',
                        'bohème':'style_boho','boheme':'style_boho','boho':'style_boho',
                        'soirée':'style_evening','soiree':'style_evening',
                        'sport':'style_sport','sportif':'style_sport',
                        'bureau':'style_office','workwear':'style_office',
                        'urbain':'style_urban','streetwear':'style_urban','street':'style_urban',
                        'romantique':'style_romantic','romantic':'style_romantic',
                        'vintage':'style_vintage','edgy':'style_edgy','preppy':'style_preppy',
                      };
                      const OCC_KEY = {
                        'casual':'occ_casual','soirée':'occ_evening','soiree':'occ_evening',
                        'bureau':'occ_work','travail':'occ_work','sport':'occ_sport',
                        'cérémonie':'occ_ceremony','ceremonie':'occ_ceremony',
                        'week-end':'occ_weekend','weekend':'occ_weekend',
                        'voyage':'occ_travel','date':'occ_date','rendez-vous':'occ_date',
                        'brunch':'occ_brunch',
                      };
                      const trCat = (c) => (CAT_KEY[c] && t(CAT_KEY[c])) || c;
                      const trStyle = (s) => (STYLE_KEY[(s||'').toLowerCase()] && t(STYLE_KEY[(s||'').toLowerCase()])) || s;
                      const trOcc = (o) => (OCC_KEY[(o||'').toLowerCase()] && t(OCC_KEY[(o||'').toLowerCase()])) || o;
                      if (prefs.categories?.length > 0) {
                        const cats = prefs.categories.map(trCat);
                        parts.push(cats.length <= 2
                          ? cats.join(' & ')
                          : `${cats.slice(0, 2).join(' & ')} +${cats.length - 2}`);
                      }
                      if (prefs.occasion) {
                        parts.push(t('for_occasion').replace('{occasion}', trOcc(prefs.occasion)));
                      }
                      if (prefs.styles?.length > 0) {
                        const styles = prefs.styles.map(trStyle);
                        parts.push(styles.length <= 2
                          ? t('style_label').replace('{styles}', styles.join('/'))
                          : `${styles.length} ${t('styles_word')}`);
                      }
                      if (prefs.budget) {
                        const budgetLabels = {
                          'budget':    '< 50€',
                          'mid-range': '50-150€',
                          'premium':   '150-500€',
                          'luxury':    '> 500€',
                        };
                        parts.push(budgetLabels[prefs.budget] || prefs.budget);
                      }
                      // v1.12.561 — Source mention
                      parts.push(t('from_catalog') || 'depuis catalogue');
                      const summary = parts.length > 0
                        ? parts.join(' · ')
                        : t('stylist_selection_desc');
                      return (
                        <Text style={{ fontSize: 11, color: theme.textMuted, marginTop: 3, letterSpacing: 0.2 }}>
                          {summary}
                        </Text>
                      );
                    })()}
                  </View>
                </View>

                {/* Tenues en vertical (grid élégante) */}
                <View style={{ gap: spacing.md }}>
                  {/* v1.13.39 — indicateur "mise à jour" quand du contenu (cache/
                      générique) est déjà affiché mais qu'un fetch frais tourne. */}
                  {stylistFetching && stylistOutfitsGen.length > 0 && (
                    <View style={{ flexDirection:'row', alignItems:'center', justifyContent:'center', gap: 8, paddingVertical: 4 }}>
                      <ActivityIndicator size="small" color="#8B6F2E" />
                      <Text style={{ fontSize: 12, color:'#8B6F2E', fontWeight:'500' }}>
                        {lang === 'en' ? 'Updating your selection…' : 'Mise à jour de votre sélection…'}
                      </Text>
                    </View>
                  )}
                  {(() => {
                    // ═══ Tenues mémoïsées (ne se regénèrent pas au retour d'un article) ═══
                    // v1.12.541 — Utilise la version filtrée par catégories préférées
                    // v1.12.973 — ...et SANS les outfits dropés par la température
                    // (sinon la pagination "Voir +5" révélait des cartes vides).
                    const outfitsGen = stylistOutfitsDisplay;

                    // v1.12.616 — Timeout 30s : affiche message empty + CTA
                    // pour éviter le spinner infini si le worker backend n'a
                    // pas pré-compute le pk de l'user (1er compute, prefs rares).
                    if (stylistTimedOut && stylistOutfitsGen.length === 0) {
                      const isFR = !lang || lang.startsWith('fr');
                      return (
                        <View style={{
                          backgroundColor: '#FAF7F2', borderRadius: radius.md,
                          padding: spacing.lg, alignItems: 'center',
                          borderWidth: 1, borderColor: '#EFE7D6', gap: 10,
                        }}>
                          <Text style={{ fontSize: 22 }}>✨</Text>
                          <Text style={{ fontSize: 13, color: '#5B4A33', fontWeight: '600', textAlign: 'center' }}>
                            {isFR
                              ? 'Nos stylistes préparent vos tenues'
                              : 'Our stylists are preparing your outfits'}
                          </Text>
                          <Text style={{ fontSize: 11, color: '#8A7860', textAlign: 'center', lineHeight: 16 }}>
                            {isFR
                              ? 'La première sélection arrive dans quelques minutes. En attendant, explorez le catalogue.'
                              : 'Your first selection will be ready in a few minutes. Meanwhile, explore the catalog.'}
                          </Text>
                          <TouchableOpacity
                            onPress={() => { try { navigation.navigate('Catalogue'); } catch {} }}
                            style={{
                              marginTop: 6,
                              paddingHorizontal: 16, paddingVertical: 10,
                              backgroundColor: '#1a1f36', borderRadius: 8,
                            }}
                            activeOpacity={0.85}
                          >
                            <Text style={{ color: '#D4AF37', fontSize: 12, fontWeight: '700', letterSpacing: 0.4 }}>
                              {isFR ? 'VOIR LE CATALOGUE' : 'BROWSE CATALOG'}
                            </Text>
                          </TouchableOpacity>
                        </View>
                      );
                    }
                    // v1.12.119 — pendant le calcul deferred, on affiche un
                    // skeleton minimal qui rassure (UI fluide, pas freezée).
                    // v1.13.51 — inclut le 1er calcul DÉFÉRÉ (données reçues mais
                    // pas encore filtrées) → skeleton au lieu d'un flash "aucune
                    // tenue". Pendant un top-up (contenu déjà affiché), on NE
                    // remontre PAS le skeleton (outfitsGen garde l'ancien contenu).
                    if (stylistLoading || stylistOutfitsGen.length === 0 || (stylistDeferring && outfitsGen.length === 0)) {
                      // v1.13.38 — Loading CLAIR au 1er démarrage : grand spinner +
                      // titre + sous-titre pour bien informer l'user qu'un contenu
                      // est en cours de préparation (au lieu d'un petit skeleton).
                      return (
                        <View style={{
                          backgroundColor:'#FFF9E6', borderRadius: radius.md,
                          paddingVertical: spacing.xl, paddingHorizontal: spacing.lg,
                          alignItems:'center', borderWidth:1, borderColor:'#F5DEB3', gap: 12,
                        }}>
                          <ActivityIndicator size="large" color="#8B6F2E" />
                          <Text style={{ fontSize: 14, color:'#5C4F2E', fontWeight: '700', textAlign:'center' }}>
                            {lang === 'en' ? 'Preparing your stylist selection…' : 'Préparation de votre sélection styliste…'}
                          </Text>
                          <Text style={{ fontSize: 12, color:'#8B7E5E', textAlign:'center', lineHeight: 16 }}>
                            {lang === 'en'
                              ? 'We are composing your best outfits — just a moment.'
                              : 'Nous composons vos meilleures tenues — un instant.'}
                          </Text>
                        </View>
                      );
                    }
                    // v1.12.541 — Si filtre catégories vide la liste : message dédié
                    if (outfitsGen.length === 0 && stylistOutfitsGen.length > 0) {
                      const wantedCats = currentDailyPrefs?.categories || [];
                      return (
                        <View style={{
                          backgroundColor: '#FAF7F2', borderRadius: radius.md,
                          padding: spacing.lg, alignItems: 'center', borderWidth: 1, borderColor: '#EFE7D6',
                        }}>
                          <Text style={{ fontSize: 24, marginBottom: 8 }}>🧩</Text>
                          <Text style={{ fontSize: 14, color: '#5C4F2E', fontWeight: '600', textAlign: 'center' }}>
                            {lang === 'en'
                              ? `No outfit matches your selected categories: ${wantedCats.join(', ')}`
                              : `Aucune tenue ne correspond aux catégories choisies : ${wantedCats.join(', ')}`}
                          </Text>
                          <Text style={{ fontSize: 12, color: '#8B7E5E', marginTop: 6, textAlign: 'center' }}>
                            {lang === 'en'
                              ? 'Try changing or removing category filters in preferences.'
                              : 'Modifiez ou retirez les filtres de catégorie dans les préférences.'}
                          </Text>
                        </View>
                      );
                    }

                    // v1.13.19 — défère le montage des cartes après le 1er paint
                    // (anti-freeze démarrage à froid). Skeleton minimal en attendant.
                    if (!stylistCardsReady) {
                      return (
                        <View style={{
                          backgroundColor:'#FFF9E6', borderRadius: radius.md,
                          padding: spacing.lg, alignItems:'center', borderWidth:1, borderColor:'#F5DEB3',
                          flexDirection: 'row', justifyContent: 'center', gap: 10,
                        }}>
                          <ActivityIndicator size="small" color="#8B6F2E" />
                          <Text style={{ fontSize: 13, color:'#8B6F2E', fontWeight: '500' }}>
                            {t('loading_outfits')}
                          </Text>
                        </View>
                      );
                    }
                    // ═══ Limiter au nombre demandé (state) ═══
                    // v1.13.36 — + plafond de montage progressif (stylistMountCount)
                    // pour étaler le décodage image au démarrage à froid.
                    const visible = outfitsGen.slice(0, Math.min(stylistOutfitCount, Math.max(1, stylistMountCount)));
                    lf('styliste-render-cards ' + visible.length);

                    // ═══ v1.10.10 : Badge basé sur le style RÉEL de l'outfit ═══
                    //   Avant : fallback en cascade sur catégories → "ÉLÉGANT" partout
                    //   dès qu'il y avait des bijoux. Maintenant : on utilise le
                    //   `_classifiedStyle` calculé via classifyOutfitStyle() qui prend
                    //   en compte bestTag IA + inspiration banque + styleScores items.
                    const STYLE_BADGES = {
                      'chic':         { label: t('badge_chic'),       color: '#1565C0' },
                      'casual':       { label: t('badge_casual'),     color: '#558B2F' },
                      'workwear':     { label: t('badge_office'),     color: '#1565C0' },
                      'sport':        { label: t('badge_sport'),      color: '#558B2F' },
                      'romantique':   { label: t('badge_feminine'),   color: '#D4AF72' },
                      'bohème':       { label: t('badge_boho'),       color: '#8D6E63' },
                      'streetwear':   { label: t('badge_street'),     color: '#37474F' },
                      'minimaliste':  { label: t('badge_minimal'),    color: '#455A64' },
                    };
                    const TAG_BADGES = {
                      'soirée':     { label: t('badge_evening'),  color: '#8E24AA' },
                      'soiree':     { label: t('badge_evening'),  color: '#8E24AA' },
                      'chic':       { label: t('badge_chic'),     color: '#1565C0' },
                      'bureau':     { label: t('badge_office'),   color: '#1565C0' },
                      'casual':     { label: t('badge_casual'),   color: '#558B2F' },
                      'cérémonie':  { label: t('badge_evening'),  color: '#8E24AA' },
                      'romantique': { label: t('badge_feminine'), color: '#D4AF72' },
                      'élégant':    { label: t('badge_chic'),     color: '#1565C0' },
                      'date':       { label: t('badge_date'),     color: '#D4AF72' },
                      'brunch':     { label: t('badge_brunch'),   color: '#FFA726' },
                      'sport':      { label: t('badge_sport'),    color: '#558B2F' },
                      'voyage':     { label: t('badge_travel'),   color: '#26A69A' },
                      'plage':      { label: t('badge_beach'),    color: '#29B6F6' },
                    };
                    const schemeBadge = (outfit) => {
                      const bestTag = outfit.bestTag;
                      // Priorité 1 : bestTag IA (occasion précise détectée par IA)
                      if (bestTag && TAG_BADGES[bestTag]) return TAG_BADGES[bestTag];
                      // Priorité 2 : style classifié de l'outfit (source unique avec round-robin)
                      const classifiedStyle = outfit._classifiedStyle;
                      if (classifiedStyle && STYLE_BADGES[classifiedStyle]) return STYLE_BADGES[classifiedStyle];
                      // Fallback final : catégories
                      const cats = new Set((outfit.items || []).map(it => it.category));
                      if (cats.has('Robes') && cats.has('Bijoux')) return { label: t('badge_evening'), color: '#8E24AA' };
                      if (cats.has('Robes') && cats.has('Vestes')) return { label: t('badge_chic'), color: '#1565C0' };
                      if (cats.has('Robes')) return { label: t('badge_feminine'), color: '#D4AF72' };
                      if (cats.has('Vestes')) return { label: t('badge_structured'), color: '#455A64' };
                      return { label: t('badge_casual'), color: '#558B2F' };
                    };

                    return [
                      ...visible.map((outfit, i) => {
                        // v1.12.138 — Garde-fou : skip les outfits sans items
                        if (!outfit?.items || outfit.items.length === 0) return null;
                        // v1.12.885 — Lookup du pre-computed map (perf scroll: O(1) au lieu de recompute)
                        const computed = stylistOutfitsComputed.get(outfit.id);
                        // v1.13.4 — Ne plus skipper sur `drop` : la liste d'affichage
                        // (stylistOutfitsDisplay) a DÉJÀ décidé quoi montrer (kept, ou
                        // filet anti-vide tempDrop si tout était masqué par la température).
                        // On rend dès qu'il y a des données d'affichage (heroItem). Seuls
                        // les outfits sans image exploitable (pas de heroItem) sont exclus.
                        if (!computed || !computed.heroItem) return null;
                        const _tempStylist = typeof temperature === 'number' ? temperature : null;
                        const { outfitOverride, removedWarm, displayedItems, totalPrice, totalOriginal, hasDiscount, savings, heroItem, restItems } = computed;
                        outfit = outfitOverride;
                        const badge = schemeBadge(outfit);
                        const agentEnrich = stylistAgentMap.get(String(outfit.id || '')) || null;

                        return (
                          <View key={`outfit_${i}`} style={[{
                            backgroundColor: '#FFFFFF',
                            borderRadius: radius.lg,
                            borderWidth: 1, borderColor: colors.bg.border,
                            overflow: 'hidden',
                          }, shadow.sm]}>
                            {/* v1.13.5 — Bandeau "X°C … (trop chaud)" retiré de la sélection styliste (sur demande) */}
                            {/* v1.12.226 — Badges agent (PERFECT MATCH / GOOD PRICE / SEASON FIT…) */}
                            {agentEnrich?.badges?.length > 0 && (
                              <View style={{
                                flexDirection:'row', flexWrap:'wrap', gap: 4,
                                paddingHorizontal: spacing.md, paddingTop: 10, paddingBottom: 4,
                              }}>
                                {agentEnrich.badges.map((b, bi) => (
                                  <View key={bi} style={{
                                    paddingHorizontal: 8, paddingVertical: 3,
                                    borderRadius: 10, backgroundColor: '#FFF7E0',
                                    borderWidth: 1, borderColor: '#D4AF72',
                                  }}>
                                    <Text style={{ fontSize: 9, fontWeight:'700', color:'#B8860B', letterSpacing: 0.4 }}>
                                      {b}
                                    </Text>
                                  </View>
                                ))}
                              </View>
                            )}

                            {/* Bandeau numéro + badge style + prix */}
                            <View style={{
                              flexDirection:'row', alignItems:'center', justifyContent:'space-between',
                              paddingHorizontal: spacing.md, paddingVertical: 10,
                              borderBottomWidth: 1, borderBottomColor: '#F8F5EE',
                            }}>
                              <View style={{ flexDirection:'row', alignItems:'center', gap: 10 }}>
                                <View style={{
                                  width: 26, height: 26, borderRadius: 13,
                                  backgroundColor: '#1A1A1A',
                                  alignItems:'center', justifyContent:'center',
                                  borderWidth: 1, borderColor: '#D4AF72',
                                }}>
                                  <Text style={{ fontSize: 11, fontWeight:'700', color:'#D4AF72' }}>{i + 1}</Text>
                                </View>
                                <View style={{
                                  paddingHorizontal: 10, paddingVertical: 4,
                                  backgroundColor: badge.color + '18', // 18 = ~10% alpha
                                  borderRadius: 4,
                                  borderWidth: 1, borderColor: badge.color + '40',
                                }}>
                                  <Text style={{ fontSize: 9, fontWeight:'700', color: badge.color, letterSpacing: 1 }}>
                                    {badge.label}
                                  </Text>
                                </View>
                              </View>
                              {/* v1.12.559 — Total prix supprimé à la demande user */}
                            </View>

                            {/* v1.12.92 — DEBUG SCORES box (Sélection styliste) retirée sur demande user */}

                            {/* v1.12.131 — Sélection styliste : layout aligne sur Looks
                                Pièce principale a GAUCHE (50%, square, contain) — autres a DROITE
                                en colonne (1-4 items) ou grille 2 cols (5+ items). Images jamais croppees. */}
                            <View style={{ padding: spacing.md }}>
                              {restItems.length === 0 ? (
                                <TouchableOpacity
                                  style={{
                                    width: '100%', aspectRatio: 1,
                                    backgroundColor: '#F5F2ED', borderRadius: radius.md,
                                    overflow: 'hidden', position: 'relative',
                                  }}
                                  onPress={() => setSelectedProduct(heroItem)}
                                  activeOpacity={0.85}>
                                  {heroItem.image
                                    ? <SmartImage uri={heroItem.image} style={{ width:'100%', height:'100%', resizeMode:'contain' }} fallback={heroItem.emoji || '👗'} />
                                    : <View style={{ flex:1, alignItems:'center', justifyContent:'center' }}>
                                        <Text style={{ fontSize: 56 }}>{heroItem.emoji || '👗'}</Text>
                                      </View>
                                  }
                                  {heroItem.discount > 10 ? (
                                    <View style={{ position: 'absolute', top: 8, left: 8, backgroundColor: '#E53935', paddingHorizontal: 7, paddingVertical: 3, borderRadius: 4 }}>
                                      <Text style={{ fontSize: 10, fontWeight: '700', color: '#fff' }}>-{heroItem.discount}%</Text>
                                    </View>
                                  ) : null}
                                  <View style={{
                                    position:'absolute', bottom: 0, left: 0, right: 0,
                                    backgroundColor: 'rgba(0,0,0,0.65)',
                                    paddingVertical: 8, paddingHorizontal: 12,
                                  }}>
                                    <Text style={{ fontSize: 11, fontWeight:'700', color:'#FFD700', letterSpacing: 0.8, textAlign:'center' }} numberOfLines={1}>
                                      ⭐ {tCatUpper(heroItem.category)} — {t('main_piece_caps')}
                                    </Text>
                                    <Text style={{ fontSize: 11, fontWeight: '700', color: '#fff', textAlign:'center', marginTop: 2 }}>
                                      {Number(heroItem.price || 0).toFixed(0)} €
                                    </Text>
                                  </View>
                                </TouchableOpacity>
                              ) : (
                                <View style={{ flexDirection: 'row', gap: 8, alignItems: 'stretch' }}>
                                  {/* GAUCHE — pièce principale */}
                                  <TouchableOpacity
                                    style={{
                                      width: '52%', aspectRatio: 4 / 5,
                                      backgroundColor: '#F5F2ED', borderRadius: radius.md,
                                      overflow: 'hidden', position: 'relative',
                                    }}
                                    onPress={() => setSelectedProduct(heroItem)}
                                    activeOpacity={0.85}>
                                    {heroItem.image
                                      ? <SmartImage uri={heroItem.image} style={{ width:'100%', height:'100%', resizeMode:'contain' }} fallback={heroItem.emoji || '👗'} />
                                      : <View style={{ flex:1, alignItems:'center', justifyContent:'center' }}>
                                          <Text style={{ fontSize: 56 }}>{heroItem.emoji || '👗'}</Text>
                                        </View>
                                    }
                                    {heroItem.discount > 10 ? (
                                      <View style={{ position: 'absolute', top: 6, left: 6, backgroundColor: '#E53935', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 3 }}>
                                        <Text style={{ fontSize: 9, fontWeight: '700', color: '#fff' }}>-{heroItem.discount}%</Text>
                                      </View>
                                    ) : null}
                                    <View style={{
                                      position:'absolute', bottom: 0, left: 0, right: 0,
                                      backgroundColor: 'rgba(0,0,0,0.65)',
                                      paddingVertical: 6, paddingHorizontal: 8,
                                    }}>
                                      <Text style={{ fontSize: 9, fontWeight:'700', color:'#FFD700', letterSpacing: 0.5, textAlign:'center' }} numberOfLines={1}>
                                        ⭐ {tCatUpper(heroItem.category)}
                                      </Text>
                                      <Text style={{ fontSize: 10, fontWeight: '700', color: '#fff', textAlign:'center', marginTop: 1 }}>
                                        {Number(heroItem.price || 0).toFixed(0)} €
                                      </Text>
                                    </View>
                                  </TouchableOpacity>

                                  {/* DROITE — autres pièces */}
                                  <View style={
                                    restItems.length >= 5
                                      ? { flex: 1, flexDirection: 'row', flexWrap: 'wrap', alignContent: 'space-between', gap: 6 }
                                      : { flex: 1, flexDirection: 'column', gap: 6 }
                                  }>
                                    {restItems.map((p, j) => (
                                      <TouchableOpacity key={p.id || j}
                                        style={
                                          restItems.length >= 5
                                            ? { width: '48%', aspectRatio: 1, backgroundColor: '#F5F2ED', borderRadius: radius.md, overflow: 'hidden', position: 'relative' }
                                            : { flex: 1, minHeight: 56, backgroundColor: '#F5F2ED', borderRadius: radius.md, overflow: 'hidden', position: 'relative' }
                                        }
                                        onPress={() => setSelectedProduct(p)}
                                        activeOpacity={0.85}>
                                        {p.image
                                          ? <SmartImage uri={p.image} style={{ width:'100%', height:'100%', resizeMode:'contain' }} fallback={p.emoji || '👗'} />
                                          : <View style={{ flex:1, alignItems:'center', justifyContent:'center' }}>
                                              <Text style={{ fontSize: 28 }}>{p.emoji || '👗'}</Text>
                                            </View>
                                        }
                                        <View style={{
                                          position:'absolute', bottom: 0, left: 0, right: 0,
                                          backgroundColor: 'rgba(0,0,0,0.65)',
                                          paddingVertical: 3, paddingHorizontal: 4,
                                        }}>
                                          <Text style={{ fontSize: 8, fontWeight:'700', color:'#fff', letterSpacing: 0.4, textAlign:'center' }} numberOfLines={1}>
                                            {p.category === 'Bijoux' ? `💍 ${t('cat_jewelry').toUpperCase()}` :
                                             p.category === 'Accessoires' ? `🧣 ${t('cat_accessories_short').toUpperCase()}` :
                                             tCatUpper(p.category)}
                                          </Text>
                                          <Text style={{ fontSize: 9, fontWeight:'700', color: '#D4AF72', textAlign:'center', marginTop: 1 }}>
                                            {Number(p.price || 0).toFixed(0)} €
                                          </Text>
                                        </View>
                                        {p.discount > 10 ? (
                                          <View style={{ position: 'absolute', top: 4, left: 4, backgroundColor: '#E53935', paddingHorizontal: 4, paddingVertical: 1.5, borderRadius: 3 }}>
                                            <Text style={{ fontSize: 8, fontWeight: '700', color: '#fff' }}>-{p.discount}%</Text>
                                          </View>
                                        ) : null}
                                      </TouchableOpacity>
                                    ))}
                                  </View>
                                </View>
                              )}
                            </View>

                            {/* Footer : catégories en chips + actions */}
                            <View style={{
                              flexDirection: 'row', alignItems:'center', gap: 8,
                              paddingHorizontal: spacing.md, paddingBottom: spacing.md, paddingTop: 4,
                            }}>
                              <TouchableOpacity
                                style={{
                                  width: 42, height: 42,
                                  borderRadius: 8, alignItems:'center', justifyContent:'center',
                                  backgroundColor: '#FFEBEE',
                                  borderWidth: 1, borderColor: '#E53935',
                                }}
                                onPress={async () => {
                                  // v1.12.285 — confirme avant d'ajouter aux favoris + propose Voir favoris après
                                  const count = (outfit.items || []).length;
                                  const confirmIdx = await showAppAlert(
                                    `❤️ ${t('add_to_favorites_title')}`,
                                    t('add_outfit_to_favorites_confirm').replace('{count}', String(count)),
                                    [
                                      { text: t('cancel'), style: 'cancel' },
                                      { text: t('confirm_add'), style: 'default' },
                                    ]
                                  );
                                  if (confirmIdx !== 1) return;
                                  // v1.12.917 — Track + log détaillé pour diag
                                  let addedCount = 0;
                                  console.log('[Home] addFavorite batch — user.id =', user?.id, 'items =', (outfit.items || []).length);
                                  try {
                                    const { addFavorite } = require('../services/FavoritesService');
                                    for (const p of outfit.items) {
                                      console.log('[Home] addFav item', { id: p?.id, partner: p?.partner, hasImage: !!(p?.image || p?.image_url || p?.photoUrl) });
                                      const ok = await addFavorite(p, user?.id);
                                      if (ok) addedCount++;
                                      else console.warn('[Home] addFavorite FAILED for', p?.id, p?.name);
                                    }
                                    try { DeviceEventEmitter.emit('favorites_updated'); } catch {}
                                    console.log(`[Home] result: ${addedCount}/${(outfit.items || []).length} items ajoutés`);
                                  } catch(e) { console.warn('[Home] addFavorite outer error:', e?.message, e); }
                                  // v1.12.915 — Si aucun item n'a pu être sauvegardé :
                                  //   - guest (pas d'user.id) → propose de se connecter
                                  //   - logged in mais 0 ajouté → erreur générique
                                  if (addedCount === 0) {
                                    const isGuest = !user?.id;
                                    if (isGuest) {
                                      const goSignIn = await showAppAlert(
                                        '❤️ ' + (t('favorites') || 'Favoris'),
                                        t('favorites_signin_required') || 'Connectez-vous pour ajouter à vos favoris.',
                                        [
                                          { text: t('cancel') || 'Annuler', style: 'cancel' },
                                          { text: t('sign_in') || t('login') || 'Se connecter', style: 'default' },
                                        ]
                                      );
                                      if (goSignIn === 1) {
                                        try { navigation.navigate('Login'); } catch {}
                                      }
                                    } else {
                                      await showAppAlert(
                                        t('error_title') || 'Erreur',
                                        t('favorites_save_failed') || 'Impossible d\'enregistrer les favoris. Réessayez.',
                                        [{ text: t('close') || 'OK', style: 'cancel' }]
                                      );
                                    }
                                    return;
                                  }
                                  await showAppAlert(
                                    `❤️ ${t('favorites')}`,
                                    t('items_added_to_favorites').replace('{count}', String(count)),
                                    [
                                      { text: t('close'), style: 'cancel' },
                                      { text: t('view_my_favorites'), style: 'default', onPress: () => {
                                        try { navigation.navigate('Favoris'); } catch(e) { console.warn('[Home] nav Favoris failed:', e?.message); }
                                      }},
                                    ]
                                  );
                                }}
                                activeOpacity={0.75}>
                                <Text style={{ fontSize: 17 }}>❤️</Text>
                              </TouchableOpacity>

                              {/* ═══ v1.10.22 : Bouton "Essayer sur un mannequin" (cohérent avec Looks) ═══
                                  Avant : "Découvrir cette tenue"
                                  Maintenant : libellé identique à celui de l'écran Looks pour
                                  une UX homogène. Le bouton ouvre l'essayage virtuel FASHN. */}
                              <Tooltip text={t('try_on_outfit_tooltip')}>
                                <TouchableOpacity
                                  style={{
                                    flex: 1,
                                    backgroundColor: '#1A1A1A',
                                    paddingVertical: 12,
                                    borderRadius: 8, alignItems: 'center', justifyContent:'center',
                                    borderWidth: 1, borderColor: '#D4AF72',
                                    flexDirection: 'row', gap: 8,
                                  }}
                                  onPress={() => openTryOn(outfit)}
                                  activeOpacity={0.85}>
                                  <Text style={{ fontSize: 12, fontWeight: '700', color: '#D4AF72', letterSpacing: 1 }}>
                                    ✨ {t('try_on_caps')}
                                  </Text>
                                </TouchableOpacity>
                              </Tooltip>
                              {/* v1.12.734 — Flat-lay Vertex AI (quota partagé tryon) */}
                              <FlatLayButton outfit={outfit} style={{ borderRadius: 8 }} />
                            </View>
                          </View>
                        );
                      }),
                      // ═══ BOUTON "Voir plus" / "Voir moins" ═══
                      // Pas de limite max : on peut explorer tout le catalogue
                      (outfitsGen.length > stylistOutfitCount || stylistOutfitCount > STYLIST_DEFAULT) && (
                        <View key="show_more" style={{
                          flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm,
                        }}>
                          {outfitsGen.length > stylistOutfitCount && (
                            <TouchableOpacity
                              style={{
                                flex: 1,
                                backgroundColor: '#FFFFFF',
                                borderWidth: 1.5, borderColor: '#D4AF72',
                                paddingVertical: 14, borderRadius: radius.md,
                                alignItems: 'center', justifyContent: 'center',
                                flexDirection: 'row', gap: 8,
                              }}
                              onPress={loadMoreStylist}
                              disabled={loadingMoreStylist}
                              activeOpacity={0.85}>
                              {loadingMoreStylist ? (
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                  <ActivityIndicator size="small" color="#B8860B" />
                                  <Text style={{ fontSize: 12, fontWeight: '700', color: '#B8860B', letterSpacing: 1 }}>
                                    {t('loading_caps')}
                                  </Text>
                                </View>
                              ) : (
                                <Text style={{ fontSize: 12, fontWeight: '700', color: '#B8860B', letterSpacing: 1 }}>
                                  {t('see_more_outfits_caps').replace('{count}', String(Math.min(STYLIST_INCREMENT, outfitsGen.length - stylistOutfitCount)))}
                                </Text>
                              )}
                            </TouchableOpacity>
                          )}
                          {stylistOutfitCount > STYLIST_DEFAULT && (
                            <TouchableOpacity
                              style={{
                                paddingHorizontal: 18,
                                backgroundColor: '#F5F5F5',
                                paddingVertical: 14, borderRadius: radius.md,
                                alignItems: 'center', justifyContent: 'center',
                              }}
                              onPress={() => setStylistOutfitCount(STYLIST_DEFAULT)}
                              activeOpacity={0.85}>
                              <Text style={{ fontSize: 11, fontWeight: '600', color: '#666', letterSpacing: 0.5 }}>
                                {t('collapse')}
                              </Text>
                            </TouchableOpacity>
                          )}
                        </View>
                      ),
                      // Badge compteur
                      <Text key="count" style={{
                        fontSize: 10, color: colors.text.faint, textAlign: 'center',
                        marginTop: 6, letterSpacing: 0.5,
                      }}>
                        {Math.min(stylistOutfitCount, outfitsGen.length)} / {outfitsGen.length} {t('outfits_word')}
                      </Text>,
                    ].filter(Boolean);
                  })()}
                </View>
              </View>
            )}

            {/* ═══════ 🔥 TENDANCES DU MOMENT (horizontal scroll, diversifié) ═══════ */}
            {/* v1.12.912 — Section masquée pour le lancement (mobile + web) */}
            {false && (() => {
              const { pickDiversifiedTrending } = require('../services/TrendingService');
              // v1.12.119 — Web : 5 par défaut (compact, 1 catégorie)
              //              Natif : 12 par défaut (multi-catégories)
              //              Mode expand : 50 (les deux)
              const isWebLayout = Platform.OS === 'web';
              const defaultLimit = isWebLayout ? 5 : 12;
              const displayLimit = trendingExpanded ? 50 : defaultLimit;

              // ═══ v1.9.40 : Filtrage FORT par TOUTES les préférences ═══
              //
              //   Avant : seules les catégories étaient priorisées (apparaissaient en
              //     premier mais les autres restaient).
              //   Maintenant : on FILTRE par budget, occasion, style ET catégories.
              //
              //   Logique :
              //     1. Filtrer par budget MAX par pièce (≤ 50/150/500€)
              //     2. Filtrer par catégories préférées (si "Robes" → robes uniquement)
              //     3. Booster (mais pas filtrer) par style et occasion
              //
              //   Garde-fou : si après filtrage on a moins de 8 tendances, on relâche
              //   les contraintes pour ne pas afficher une liste vide.
              // v1.12.119 — sur web, sans préférences explicites, on défault à
              // la catégorie "Hauts" pour démarrer compact. L'user peut changer
              // ses prefs pour voir d'autres catégories.
              const userPrefs = currentDailyPrefs || {};
              const prefs = (isWebLayout && (!userPrefs.categories || userPrefs.categories.length === 0))
                ? { ...userPrefs, categories: ['Hauts'] }
                : userPrefs;
              const BUDGET_MAX_PER_ITEM_TRENDING = {
                'budget':    50,
                'mid-range': 150,
                'premium':   500,
                'luxury':    99999,
              };
              const maxPriceTrending = prefs.budget ? BUDGET_MAX_PER_ITEM_TRENDING[prefs.budget] : null;

              // Filtre items selon préférences (strict sur catégories)
              const filterByPrefs = (item) => {
                // Budget MAX par pièce
                if (maxPriceTrending) {
                  const price = parseFloat(item.price) || 0;
                  if (price > maxPriceTrending) return false;
                }
                // Catégories (filtre dur si spécifié)
                if (prefs.categories?.length > 0) {
                  if (!prefs.categories.includes(item.category)) return false;
                }
                // Style (boost si match, mais on filtre seulement si très loin)
                // → pas de filtre dur sur style/occasion (gardés pour le boost via applyPrefsBoostToTrending)
                return true;
              };

              // v1.12.381 — Garde-fou gender en 3 cas :
              //   A) user connecté + gender hydraté → strict filter
              //   B) user connecté + gender absent → STOP (return null)
              //   C) GUEST (pas de user) → default F (browse anonyme)
              let userG = user?.gender;
              if (!userG && typeof window !== 'undefined') {
                try { userG = window.localStorage?.getItem('mt_user_gender'); } catch {}
              }
              if (user?.gender && typeof window !== 'undefined') {
                try { window.localStorage?.setItem('mt_user_gender', user.gender); } catch {}
              }
              // Guest : pas de filtre strict, on laisse passer (audience F par défaut)
              if (!user) {
                userG = userG || 'F';
              } else if (userG !== 'M' && userG !== 'F') {
                // User logged in mais gender pas résolu → vide
                console.log('[Home] ⏸ trending hidden — logged in but gender unknown');
                return null;
              }
              // v1.12.384 — Élargissement après diag : 42 articles femme dans le pool M
              //   avec ai_gender='M' ou 'unisex' faux (bug Claude Vision sur les
              //   COUTR/Ann Demeulemeester). Ajout des signaux explicites :
              //     "for Women", "Women's", "pour Femme" → rejet pour profil M
              //     "for Men", "Men's", "pour Homme" → rejet pour profil F
              const FEMALE_NAME_RE = /\b(robes?|dress(es)?|jupes?|skirts?|escarpins?|talons?|heels?|brassi[eè]res?|crop\s*tops?|bustiers?|combinaisons?|jumpsuits?|ballerines?|porte[-\s]?jarretelles?|leggings?|chemisiers?|blouses?|playsuits?|tutus?|dos[-\s]?nu|for\s+women|women['']?s|pour\s+femmes?|woman)\b/i;
              const MALE_NAME_RE   = /\b(costumes?\s+homme|cravates?|nœuds?\s*papillon|boxer\s*shorts?|pour\s+hommes?|for\s+men|men['']?s|gentleman|boyfriend\s+(jeans|fit))\b/i;
              // v1.12.384 — Signaux EXPLICITES name-based : prioritaires sur ai_gender
              //   ai_metadata.gender Claude Vision peut être faux (42 articles avérés
               //   dans le pool M avec ai_gender='M' alors que nom = "for Women" etc).
              //   On override avec le nom quand il y a signal explicite.
              const EXPLICIT_FEMALE_RE = /\b(for\s+women|women['']?s|pour\s+femmes?|woman|robes?|dress(es)?|jupes?|skirts?|escarpins?|talons?|brassi[eè]res?|crop\s*tops?|bustiers?|combinaisons?|jumpsuits?|ballerines?|leggings?|blouses?|chemisiers?|playsuits?|dos[-\s]?nu)\b/i;
              const EXPLICIT_MALE_RE   = /\b(for\s+men|men['']?s|pour\s+hommes?|costumes?\s+homme|cravates?|gentleman)\b/i;
              const strictGenderOk = (p) => {
                const name = (p?.name || '').toLowerCase();
                // 1. Signal name explicite (override ai_gender potentiellement buggé)
                if (userG === 'M' && EXPLICIT_FEMALE_RE.test(name)) return false;
                if (userG === 'F' && EXPLICIT_MALE_RE.test(name)) return false;
                // 2. ai_metadata.gender
                const aiG = p?.ai_metadata?.gender;
                if (aiG === 'F' || aiG === 'M') return aiG === userG;
                if (aiG === 'unisex') return true;
                // 3. Regex sur nom (large, déjà couvert par EXPLICIT mais robuste)
                if (userG === 'M' && FEMALE_NAME_RE.test(name)) return false;
                if (userG === 'F' && MALE_NAME_RE.test(name)) return false;
                // 4. detectProductGender (fallback)
                try {
                  const { detectProductGender } = require('../services/GenderFilter');
                  const g = detectProductGender(p);
                  if (g === null) return false; // strict
                  return g === userG;
                } catch { return false; }
              };
              const trendingPool = catalogItems.filter(p => p.isTrending && p.image && p.price > 0 && strictGenderOk(p));
              let filteredCatalog = catalogItems.filter(filterByPrefs).filter(strictGenderOk);
              const filteredTrending = filteredCatalog.filter(p => p.isTrending && p.image && p.price > 0);

              // ═══ v1.9.42 : Si user a choisi des catégories, on garde STRICT ═══
              //   Avant : seuil de relâchement à 8 (si <8 matches → on affichait tout).
              //   Maintenant : si user a explicitement choisi des catégories (même
              //     accessoires comme Bijoux/Vestes), on garde le filtre dur. On relâche
              //     UNIQUEMENT si l'user n'a pas choisi de catégorie (pour le budget seul).
              const hasExplicitCategories = prefs.categories?.length > 0;
              const useFiltered = hasExplicitCategories
                ? filteredTrending.length >= 1   // 1 seul item suffit, on garde strict
                : filteredTrending.length >= 8;  // sinon on garde le seuil de 8 pour budget seul
              const sourceItems = useFiltered ? filteredCatalog : catalogItems;

              const preferredCats = prefs.categories || null;
              // ═══ v1.9.42 : strict=true si user a choisi des catégories ═══
              //   Quand l'user a explicitement filtré (ex: "Bijoux"), on n'ajoute
              //   PAS d'autres catégories pour atteindre 12. Si on a 5 bijoux
              //   trending, on affiche 5 bijoux et c'est tout.
              const top = pickDiversifiedTrending(sourceItems, displayLimit, preferredCats, {
                strict: hasExplicitCategories
              });
              if (top.length < 1) return null;

              const totalTrending = useFiltered ? filteredTrending.length : trendingPool.length;
              // v1.12.119 — sur web, "Voir plus" toujours visible si non expanded
              // ET au moins quelque chose de plus à montrer. Sur natif on garde
              // le seuil 12 d'origine.
              const canShowMore = isWebLayout
                ? (totalTrending > defaultLimit && !trendingExpanded)
                : (totalTrending > 12 && !trendingExpanded);

              // Construit le sous-titre selon les préférences appliquées
              const prefsLabels = [];
              if (prefs.categories?.length > 0) prefsLabels.push(prefs.categories.join(', '));
              if (prefs.styles?.length > 0) prefsLabels.push(prefs.styles.join(', '));
              if (prefs.occasion) prefsLabels.push(prefs.occasion);
              if (prefs.budget) prefsLabels.push(`≤ ${maxPriceTrending}€`);
              const subtitle = useFiltered && prefsLabels.length > 0
                ? `${top.length} ${t('items_word_plural')} · ${prefsLabels.join(' · ')}`
                : `${top.length} ${t('items_word_plural')} · ${t('diversified_by_category')}`;

              return (
                <View style={{ marginTop: spacing.xl * 1.2 }}>
                  {/* Header section */}
                  <View style={{ marginBottom: spacing.md, paddingHorizontal: 2 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <View style={{ width: 4, height: 14, backgroundColor: '#FF4D4F', borderRadius: 2 }} />
                      <Text style={{ fontSize: 9, letterSpacing: 3, color: '#FF4D4F', fontWeight: '700' }}>
                        🔥 {t('trending_week')}
                      </Text>
                    </View>
                    <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontSize: 22, fontWeight: '300', color: theme.text, letterSpacing: 0.3, marginTop: 2 }}>
                          {t('trending_subtitle')}
                        </Text>
                        <Text style={{ fontSize: 11, color: theme.textMuted, marginTop: 3, letterSpacing: 0.2 }}>
                          {subtitle}
                        </Text>
                      </View>
                      {(canShowMore || trendingExpanded) && (
                        <TouchableOpacity
                          onPress={() => setTrendingExpanded(!trendingExpanded)}
                          activeOpacity={0.7}
                          style={{ paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20, backgroundColor: trendingExpanded ? colors.text.primary : '#F9F7F3', borderWidth: 1, borderColor: trendingExpanded ? colors.text.primary : '#EDEBE8' }}
                        >
                          <Text style={{ fontSize: 11, letterSpacing: 0.5, fontWeight: '600', color: trendingExpanded ? '#fff' : colors.text.primary }}>
                            {trendingExpanded ? `↑ ${t('collapse')}` : `+ ${t('see').replace('{count}', String(Math.min(50, totalTrending) - defaultLimit))}`}
                          </Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  </View>

                  {/* Scroll horizontal (mode compact) OU grille 2 cols (mode expand) */}
                  {!trendingExpanded ? (
                    <ScrollView
                      horizontal
                      showsHorizontalScrollIndicator={false}
                      contentContainerStyle={{ gap: 10, paddingLeft: 2, paddingRight: 16 }}
                    >
                      {top.map(p => {
                        const hasPromo = p.originalPrice && p.originalPrice > p.price + 0.5;
                        const discountPct = hasPromo
                          ? Math.round(((p.originalPrice - p.price) / p.originalPrice) * 100)
                          : 0;
                        return (
                        <TouchableOpacity
                          key={`trend_${p.id || p.name || Math.random()}`}
                          style={{
                            width: 130,
                            backgroundColor: '#fff',
                            borderRadius: 12,
                            overflow: 'hidden',
                            borderWidth: 1,
                            borderColor: '#EDEBE8',
                          }}
                          onPress={() => setSelectedProduct(p)}
                          activeOpacity={0.85}
                        >
                          <View style={{ position: 'relative', width: '100%', height: 150 }}>
                            <SmartImage
                              uri={p.image}
                              style={{ width: '100%', height: '100%' }}
                              fallback={CAT_ICONS[p.category] || '👗'}
                            />
                            <View style={{ position: 'absolute', top: 6, left: 6, zIndex: 2 }}>
                              <TrendingBadge tag={p.trendTag} compact />
                            </View>
                            {hasPromo && (
                              <View style={{ position: 'absolute', top: 6, right: 6, zIndex: 2, backgroundColor: '#FF3B30', paddingHorizontal: 6, paddingVertical: 3, borderRadius: 4 }}>
                                <Text style={{ fontSize: 9, fontWeight: '800', color: '#fff', letterSpacing: 0.3 }}>-{discountPct}%</Text>
                              </View>
                            )}
                          </View>
                          <View style={{ padding: 8 }}>
                            <Text style={{ fontSize: 9, letterSpacing: 1.5, color: '#999', textTransform: 'uppercase' }}
                                  numberOfLines={1}>
                              {p.brand || p.partner || ''}
                            </Text>
                            <Text style={{ fontSize: 11, fontWeight: '500', color: '#1A1A1A', lineHeight: 15, marginVertical: 3 }}
                                  numberOfLines={2}>
                              {displayName(p, lang)}
                            </Text>
                            <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 5, flexWrap: 'wrap' }}>
                              <Text style={{ fontSize: 12, fontWeight: '700', color: hasPromo ? '#FF3B30' : '#1A1A1A' }}
                                    numberOfLines={1}>
                                {p.price} €
                              </Text>
                              {hasPromo && (
                                <Text style={{ fontSize: 10, color: '#999', textDecorationLine: 'line-through' }}
                                      numberOfLines={1}>
                                  {p.originalPrice} €
                                </Text>
                              )}
                            </View>
                          </View>
                        </TouchableOpacity>
                        );
                      })}
                    </ScrollView>
                  ) : (
                    /* Grille 2 colonnes en mode expand (jusqu'à 50 articles) */
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                      {top.map(p => {
                        const hasPromo = p.originalPrice && p.originalPrice > p.price + 0.5;
                        const discountPct = hasPromo
                          ? Math.round(((p.originalPrice - p.price) / p.originalPrice) * 100)
                          : 0;
                        return (
                        <TouchableOpacity
                          key={`trend_expand_${p.id || p.name || Math.random()}`}
                          style={{
                            width: '48%',
                            backgroundColor: '#fff',
                            borderRadius: 12,
                            overflow: 'hidden',
                            borderWidth: 1,
                            borderColor: '#EDEBE8',
                          }}
                          onPress={() => setSelectedProduct(p)}
                          activeOpacity={0.85}
                        >
                          <View style={{ position: 'relative', width: '100%', aspectRatio: 0.8 }}>
                            <SmartImage
                              uri={p.image}
                              style={{ width: '100%', height: '100%' }}
                              fallback={CAT_ICONS[p.category] || '👗'}
                            />
                            <View style={{ position: 'absolute', top: 6, left: 6, zIndex: 2 }}>
                              <TrendingBadge tag={p.trendTag} compact />
                            </View>
                            {hasPromo && (
                              <View style={{ position: 'absolute', top: 6, right: 6, zIndex: 2, backgroundColor: '#FF3B30', paddingHorizontal: 7, paddingVertical: 3, borderRadius: 4 }}>
                                <Text style={{ fontSize: 10, fontWeight: '800', color: '#fff', letterSpacing: 0.3 }}>-{discountPct}%</Text>
                              </View>
                            )}
                          </View>
                          <View style={{ padding: 10 }}>
                            <Text style={{ fontSize: 9, letterSpacing: 1.5, color: '#999', textTransform: 'uppercase' }}
                                  numberOfLines={1}>
                              {p.brand || p.partner || ''}
                            </Text>
                            <Text style={{ fontSize: 12, fontWeight: '500', color: '#1A1A1A', lineHeight: 16, marginVertical: 4 }}
                                  numberOfLines={2}>
                              {displayName(p, lang)}
                            </Text>
                            <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 5 }}>
                              <Text style={{ fontSize: 13, fontWeight: '700', color: hasPromo ? '#FF3B30' : '#1A1A1A' }}>
                                {p.price} €
                              </Text>
                              {hasPromo && (
                                <Text style={{ fontSize: 11, color: '#999', textDecorationLine: 'line-through' }}>
                                  {p.originalPrice} €
                                </Text>
                              )}
                            </View>
                          </View>
                        </TouchableOpacity>
                        );
                      })}
                    </View>
                  )}
                </View>
              );
            })()}

        {/* Combinaisons suggérées */}
        {outfits.length > 0 && (
          <View style={s.section}>
            <View style={s.sectionHeader}>
              <Text style={[s.sectionLabel, { color: theme.textMuted }]}>
                {t('daily_selection_caps')} · {temperature}°C{locationCity ? ' · ' + locationCity.split(',')[0] : ''}
              </Text>
              <TouchableOpacity onPress={() => navigation.navigate('Suggestions')}>
                <Text style={[s.seeAll, { color: theme.textMuted }]}>{t('view_all')} →</Text>
              </TouchableOpacity>
            </View>
            {outfits.slice(0, 2).map(function(outfit, outfitIdx) {
              return (
                <OutfitCard
                  key={`home_outfit_${outfitIdx}_${outfit.id || outfit.label || 'x'}`}
                  outfit={outfit}
                  tempC={typeof temperature === 'number' ? temperature : null}
                  onSuggestPress={(p) => navigation.navigate('Catalogue')}
                  onItemPress={(item) => setSelectedWardrobeItem(item)}
                />
              );
            })}
          </View>
        )}

        {/* v1.10.81 : Section "Catalogue dynamique" supprimée de l'accueil.
            L'accès au catalogue se fait via le bouton dans la grille de
            navigation rapide (ci-dessous) et l'onglet "Catalogue" du menu. */}

        {/* ═════ ACCÈS RAPIDE — Design premium ═════ */}
        {/* v1.12.280 — espacement supplémentaire pour séparer du bloc Trending au-dessus */}
        <View style={[s.section, { marginTop: spacing.xl * 1.5 }]}>
          <View style={s.quickHeader}>
            <View style={{ flex: 1 }}>
              <Text style={s.quickBadgeLabel}>✨ {t('navigation_caps')}</Text>
              <Text style={[s.quickTitle, { color: theme.text }]}>{t('quick_nav')}</Text>
            </View>
          </View>
          <View style={s.quickGrid}>
            {[
              // v1.12.280 — accès rapide condensé : Préférences + Voyage + Cashback uniquement
              // v1.12.911 — Voyage retiré (feature reportée pour après le lancement)
              { icon:'✨', label:t('daily_prefs'),    sub:t('quick_daily_prefs_sub'), action:'daily_prefs', color:'#FFF7E0', border:'#D4AF72' },
              { icon:'💰', label:t('cashback'),       sub:t('cashback_earnings'),     nav:'Earnings',     color:'#F0FDF4', border:'#BBF7D0' },
            ].map(q => (
              <TouchableOpacity
                key={q.nav || q.action}
                style={[s.quickCard, { backgroundColor: q.color, borderColor: q.border }]}
                onPress={() => {
                  if (q.action === 'daily_prefs') {
                    // Émet un event écouté par App.js qui ouvre le popup
                    const { DeviceEventEmitter } = require('react-native');
                    DeviceEventEmitter.emit('open_daily_prefs');
                  } else {
                    navigation.navigate(q.nav);
                  }
                }}
                activeOpacity={0.85}
              >
                <View style={s.quickCardLeft}>
                  <Text style={s.quickCardIcon}>{q.icon}</Text>
                </View>
                <View style={s.quickCardRight}>
                  <Text style={s.quickCardLabel}>{q.label}</Text>
                  <Text style={s.quickCardSub}>{q.sub}</Text>
                </View>
                <Text style={s.quickCardArrow}>→</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

      </ScrollView>
      {/* Modal détail produit — composant partagé (identique au Catalogue) */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          userId={user?.id || null}
          screen="home"
          onClose={() => setSelectedProduct(null)}
          onBought={(p) => { setSelectedProduct(null); setBoughtProduct(p); }}
        />
      )}
      {/* v1.12.140 — Detail d'un article du dressing (clic depuis Tenue du jour) */}
      <WardrobeItemDetailModal
        visible={!!selectedWardrobeItem}
        item={selectedWardrobeItem}
        onClose={() => setSelectedWardrobeItem(null)}
      />
      {/* ═══ v1.10.0 : Modal d'essayage virtuel sur mannequin ═══ */}
      <TryOnModal
        visible={!!tryOnOutfit}
        onClose={() => { setTryOnOutfit(null); setTryOnAuthToken(null); }}
        outfit={tryOnOutfit}
        userGender={user?.gender || 'F'}
        authToken={tryOnAuthToken}
        onUpgradePress={() => navigation?.navigate?.('Profile')}
        onProductPress={(product) => setSelectedProduct(product)}
      />
      {/* ═══ v1.10.9 : Modal préférences du jour (accès depuis le bouton ⚙️) ═══ */}
      <DailyPreferencesModal
        visible={showPrefsModal}
        onClose={() => setShowPrefsModal(false)}
        onSubmit={async (newPrefs) => {
          try {
            await setSessionPrefs(newPrefs);
            setCurrentDailyPrefs(newPrefs);
            setShowPrefsModal(false);
            console.log('[Home] Préférences mises à jour :', newPrefs);
          } catch (e) {
            console.warn('[Home] save prefs failed:', e?.message);
          }
        }}
      />
      {boughtProduct && (
        <PurchaseConfirmModal
          product={boughtProduct}
          onClose={() => setBoughtProduct(null)}
          onConfirmed={() => { setBoughtProduct(null); }}
        />
      )}

      {/* v1.12.250 — Raccourci calendrier depuis Tenue du jour */}
      <AddOutfitToCalendarModal
        visible={!!calendarShortcut}
        preselectedLook={calendarShortcut?.look || null}
        preselectedSource={calendarShortcut?.source || null}
        onClose={() => setCalendarShortcut(null)}
        onAdded={() => {
          setCalendarShortcut(null);
          try { navigation.navigate('Calendar'); } catch {}
        }}
      />

      {/* Upgrade popup premium */}
      <UpgradeModal
        visible={showUpgrade}
        onClose={closeUpgrade}
        currentTier={limitInfo?.tier || 'free'}
        feature={limitInfo?.trigger || 'wardrobe'}
        usage={limitInfo ? { current: limitInfo.current, limit: limitInfo.limit, nextResetDate: limitInfo.nextResetDate, isPaidTier: limitInfo.isPaidTier } : null}
        onSubscribe={async (planId) => {
          const { activateSubscription } = require('../services/TestSubscribeService');
          await activateSubscription(planId, user, updateUserProfile);
        }}
      />

      {/* v1.12.280 — FAB "Créer un look" en bas-centre, au-dessus de la tab bar.
          Animation : expanded (icon + texte) → collapsed (icon seul) après 2.5s. */}
      <Animated.View
        pointerEvents="box-none"
        style={{
          position: 'absolute',
          left: 0, right: 0,
          // v1.12.281 — descendu pour rapprocher de la tab bar (40px au lieu de 80px)
          bottom: 40,
          alignItems: 'center',
          zIndex: 100,
        }}
      >
        <View
          style={{
            // v1.13.50 — largeur par ÉTAT (snap), plus d'interpolation JS
            width: fabExpanded ? 180 : 56,
            height: 56,
            borderRadius: 28,
            backgroundColor: '#1A1A1A',
            shadowColor: '#000', shadowOpacity: 0.25, shadowRadius: 10, shadowOffset: { width: 0, height: 4 },
            elevation: 8,
            overflow: 'hidden',
          }}
        >
          <Pressable
            onPress={() => setShowCreateLook(true)}
            // v1.12.281 — hover (desktop web) : expand au survol, recollapse à la sortie
            onHoverIn={Platform.OS === 'web' ? expandFab : undefined}
            onHoverOut={Platform.OS === 'web' ? () => collapseFab(300) : undefined}
            style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingHorizontal: 12 }}
          >
            <Text style={{ fontSize: 22 }}>✨</Text>
            {fabExpanded && (
              <Animated.Text
                numberOfLines={1}
                style={{
                  color: '#D4AF72',
                  fontSize: 13,
                  fontWeight: '700',
                  letterSpacing: 0.8,
                  marginLeft: 8,
                  maxWidth: 120,
                  opacity: fabLabelOpacity, // fondu driver natif
                }}
              >
                {t('create_a_look')}
              </Animated.Text>
            )}
          </Pressable>
        </View>
      </Animated.View>

      {/* v1.12.280 — CreateLookModal accessible depuis l'accueil */}
      {showCreateLook && (
        <CreateLookModal
          visible={showCreateLook}
          onClose={closeCreateLook}
          wardrobe={wardrobe}
          catalog={catalogItems}
          gender={user?.gender || 'F'}
          onProductPress={(p) => { setShowCreateLook(false); setSelectedProduct(p); }}
          // v1.12.286 — persistance DB via LookService (avec cache AsyncStorage)
          onLookCreated={async (newLook) => {
            try {
              const { saveLook } = require('../services/LookService');
              await saveLook(newLook, { user, getValidToken });
              console.log('[Home] look saved to DB:', newLook.name);
            } catch (e) { console.warn('[Home] save look failed:', e?.message); }
          }}
          // v1.12.282 — bouton "Voir mes looks" → onglet Mes Looks du dressing
          // v1.12.287 — defer la navigation pour laisser le temps au modal de se démonter
          onViewMyLooks={() => {
            setShowCreateLook(false);
            setTimeout(() => {
              try {
                DeviceEventEmitter.emit('wardrobe_set_tab', 'mylooks');
                navigation.navigate('Garde-robe');
              } catch(e) { console.warn('[Home] navigate Wardrobe mylooks failed:', e?.message); }
            }, 250);
          }}
          // v1.12.287 — try-on depuis HomeScreen FAB : ouvre la TryOnModal locale
          // v1.12.288 — ouvre AVANT de fermer (sinon le setState perd la ref), puis ferme
          onTryOnLook={(look) => {
            console.log('[Home] onTryOnLook called with look:', look?.name, 'items:', look?.items?.length, 'user.tier:', user?.tier);
            try {
              openTryOn(look);
            } catch(e) { console.warn('[Home] openTryOn failed:', e?.message); }
            // Ferme le CreateLookModal après que la TryOnModal soit demandée
            setTimeout(() => setShowCreateLook(false), 50);
          }}
          // v1.12.282 — gate save : guest doit se connecter pour sauvegarder
          isAuthenticated={isAuthenticated}
          onRequestSignIn={() => { try { navigation.navigate('Login'); } catch {} }}
          // v1.12.283 — gate try-on : free → upgrade
          canTryOn={['basic', 'premium', 'gold'].includes(user?.tier)}
          onRequestSubscribe={() => {
            try {
              DeviceEventEmitter.emit('tryon_limit_reached', { tier: user?.tier || 'free', limit: 0, current: 0 });
            } catch {}
          }}
          // v1.12.311 — "Ajouter au dressing" depuis CreateLookModal : on émet AVANT
          // de naviguer pour que la modale d'ajout s'ouvre directement par-dessus
          // (le tab Garde-robe est déjà monté, son listener réagit immédiatement).
          onAddToWardrobe={() => {
            try {
              DeviceEventEmitter.emit('wardrobe_open_add');
              navigation.navigate('Garde-robe');
            } catch {}
          }}
        />
      )}
    </SafeAreaView>
  );
}

/* ── Modal produit accueil ── */
const PARTNER_COLORS_HOME = {
  zalando:'#FF6900', asos:'#111', hm:'#E50010', amazon:'#FF9900', zara:'#000',
};

function HomeProductModal({ product, userId, onClose, onBought }) {
  const { t } = useI18n();
  // v1.12.568 — back navigateur ferme le modal au lieu de quitter
  useWebBackToClose(true, onClose);
  const [proxied, setProxied] = React.useState(false);
  const [dead,    setDead]    = React.useState(false);
  const pColor = PARTNER_COLORS_HOME[product.partner] || '#666';
  const pLabel = product.partner === 'zalando' ? 'Zalando'
    : product.partner === 'hm'     ? 'H&M'
    : product.partner === 'asos'   ? 'ASOS'
    : product.partner === 'amazon' ? 'Amazon'
    : product.partner === 'zara'   ? 'Zara'
    : (product.partner || t('partner'));
  const hasUrl = !!(product.affiliateUrl || product.originalUrl);

  const imgUri = product.image;
  // ═══ v1.9.30 : Auto-proxy TOUS comme Zara (static.tous.com lent) ═══
  const isZara = imgUri && imgUri.includes('static.zara.net');
  const isTous = imgUri && imgUri.includes('static.tous.com');
  const needsAutoProxy = isZara || isTous;
  const src = !imgUri ? null
    : needsAutoProxy ? 'https://wsrv.nl/?url=' + encodeURIComponent(imgUri) + '&w=400'
    : proxied ? 'https://wsrv.nl/?url=' + encodeURIComponent(imgUri) + '&w=400'
    : imgUri;

  return (
    <Modal visible animationType="slide" presentationStyle="overFullScreen">
      <SafeAreaView style={{ flex: 1, backgroundColor: '#fff' }}>
        {/* Header */}
        <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', padding: 16 }}>
          <TouchableOpacity onPress={onClose} style={{ width:36, height:36, alignItems:'center', justifyContent:'center' }}>
            <Text style={{ fontSize: 18, color: '#111' }}>✕</Text>
          </TouchableOpacity>
          <View style={{ paddingHorizontal:12, paddingVertical:5, borderRadius:20, backgroundColor: pColor }}>
            <Text style={{ fontSize:11, fontWeight:'700', color:'#fff', letterSpacing:1 }}>{pLabel}</Text>
          </View>
        </View>

        <ScrollView showsVerticalScrollIndicator={false}>
          {/* Image */}
          <View style={{ width:'100%', height:380 }}>
            {!imgUri || dead
              ? <View style={{ flex:1, backgroundColor:'#F5F5F5', alignItems:'center', justifyContent:'center' }}>
                  <Text style={{ fontSize:60 }}>{product.emoji || '👗'}</Text>
                </View>
              : <Image source={{ uri: src }} style={{ width:'100%', height:'100%', resizeMode:'cover' }}
                  onError={() => { if (!proxied && !isZara) setProxied(true); else setDead(true); }} />
            }
            {product.discount > 0 && (
              <View style={{ position:'absolute', top:12, left:12, backgroundColor:'#E53935', paddingHorizontal:8, paddingVertical:3, borderRadius:4 }}>
                <Text style={{ fontSize:11, fontWeight:'700', color:'#fff' }}>-{product.discount}%</Text>
              </View>
            )}
          </View>

          {/* Infos */}
          <View style={{ padding:20, gap:8 }}>
            <Text style={{ fontSize:10, letterSpacing:2, color:'#999', textTransform:'uppercase' }} numberOfLines={1}>{product.brand}</Text>
            <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start' }}>
              <Text style={{ flex:1, fontSize:18, fontWeight:'400', color:'#111', lineHeight:24, marginRight:8 }} numberOfLines={2} ellipsizeMode="tail">{product.name}</Text>
              <View style={{ alignItems:'flex-end' }}>
                <Text style={{ fontSize:20, fontWeight:'700', color:'#111' }} numberOfLines={1}>{product.price} €</Text>
                {product.originalPrice > product.price + 0.5 && (
                  <Text style={{ fontSize:12, color:'#999', textDecorationLine:'line-through' }} numberOfLines={1}>{product.originalPrice} €</Text>
                )}
              </View>
            </View>
            {product.color && (
              <Text style={{ fontSize:12, color:'#666' }}>{t('color_label')} {product.color}</Text>
            )}
            {product.description ? (
              <Text style={{ fontSize:13, color:'#555', lineHeight:20, marginTop:4 }}>{product.description}</Text>
            ) : null}
          </View>
        </ScrollView>

        {/* Footer */}
        <View style={{ padding:16, gap:10, borderTopWidth:1, borderTopColor:'#F0F0F0' }}>
          {hasUrl && (
            <TouchableOpacity
              style={{ backgroundColor: pColor, paddingVertical:14, borderRadius:8, alignItems:'center' }}
              onPress={() => openProductForPurchase(product, userId)}>
              <Text style={{ fontSize:14, fontWeight:'600', color:'#fff' }}>
                {t('view_on').replace('{partner}', pLabel)} ↗
              </Text>
            </TouchableOpacity>
          )}
          {hasUrl && (
            <TouchableOpacity
              style={{ backgroundColor:'#2E7D32', paddingVertical:14, borderRadius:8, alignItems:'center' }}
              onPress={() => onBought(product)}>
              <Text style={{ fontSize:14, fontWeight:'600', color:'#fff' }}>
                ✓ {t('bought_add_to_wardrobe')}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </SafeAreaView>
    </Modal>
  );
}

const s = StyleSheet.create({
  safe:             { flex: 1, backgroundColor: '#FFFFFF' },
  header:           { flexDirection:'row', justifyContent:'space-between', alignItems:'center', paddingHorizontal:spacing.xl, paddingTop:4, paddingBottom:spacing.sm },
  logo:             { fontSize:22, fontWeight:'200', letterSpacing:8, color:colors.text.primary },
  season:           { fontSize:9, letterSpacing:3, color:colors.text.faint, textTransform:'uppercase', marginTop:2 },
  headerRight:      { flexDirection:'row', alignItems:'center', gap:10 },
  avatar:           { width:36, height:36, borderRadius:18, borderWidth:1.5, borderColor:colors.bg.border, backgroundColor:colors.bg.surface, alignItems:'center', justifyContent:'center' },
  avatarText:       { fontSize:15, fontWeight:'500', color:colors.text.secondary },
  divider:          { height:1, backgroundColor:colors.bg.border },

  greetBlock:       { paddingHorizontal:spacing.xl, paddingTop:spacing.lg, paddingBottom:spacing.md },
  locationRow:      { flexDirection:'row', alignItems:'center', marginTop:8, flexWrap:'wrap', gap:4 },
  locationIcon:     { fontSize:12 },
  locationText:     { fontSize:12, color:colors.text.secondary, fontWeight:'500' },
  locationDot:      { fontSize:12, color:colors.text.faint },
  tempBadge:        { fontSize:13, fontWeight:'600', color:colors.text.primary },
  weatherDescText:  { fontSize:12, color:colors.text.faint },
  greetSub:         { fontSize:10, letterSpacing:3, color:colors.text.faint, textTransform:'uppercase', marginBottom:4 },
  greetName:        { fontSize:28, fontWeight:'300', color:colors.text.primary, letterSpacing:0.5 },
  // v1.12.216 — variantes premium pour l'en-tête Accueil
  greetSubElegant:  { fontSize:13, fontStyle:'italic', fontWeight:'400', letterSpacing:0.4, marginBottom:2 },
  greetNameElegant: { fontSize:32, fontWeight:'300', letterSpacing:1.2, lineHeight:38 },
  greetGoldRule:    { width:36, height:1, backgroundColor:'#D4AF72', marginTop:6, marginBottom:2, opacity:0.8 },
  weatherChip:      { flexDirection:'row', alignItems:'center', alignSelf:'flex-start', marginTop:14, paddingVertical:8, paddingHorizontal:14, borderRadius:22, backgroundColor:'#FBF7EE', borderWidth:1, borderColor:'rgba(212,175,114,0.35)', gap:8, shadowColor:'#9B7B5A', shadowOpacity:0.08, shadowRadius:4, shadowOffset:{width:0,height:1}, elevation:1 },
  weatherTemp:      { fontSize:15, fontWeight:'600', letterSpacing:0.3 },
  weatherSep:       { width:1, height:14, backgroundColor:'rgba(212,175,114,0.45)', marginHorizontal:2 },
  weatherCity:      { fontSize:12, fontWeight:'500', letterSpacing:0.2 },
  weatherDesc2:     { fontSize:12, fontStyle:'italic', letterSpacing:0.2 },

  section:          { paddingHorizontal:spacing.xl, marginBottom:spacing.lg },
  sectionHeader:    { flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:spacing.sm },
  sectionLabel:     { fontSize:9, letterSpacing:3, fontWeight:'600', color:colors.text.faint, textTransform:'uppercase', marginBottom:spacing.sm },
  seeAll:           { fontSize:11, color:colors.text.secondary, letterSpacing:0.5 },

  dressingCard:     { backgroundColor:'#FFFFFF', borderRadius:radius.lg, borderWidth:1, borderColor:colors.bg.border, overflow:'hidden' },

  // ═══ HUB STYLISTE IA ═══
  stylistHeader:       { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: spacing.md },
  stylistHeaderLeft:   { flex: 1 },
  stylistBadgeSmall:   { fontSize: 9, letterSpacing: 2, color: '#B8860B', fontWeight: '700', marginBottom: 4 },
  stylistTitle:        { fontSize: 22, fontWeight: '300', color: colors.text.primary, letterSpacing: 0.3 },
  stylistSeeAll:       { fontSize: 11, color: colors.text.secondary, letterSpacing: 0.5, fontWeight: '500' },

  stylistCard:         { backgroundColor: '#FFFFFF', borderRadius: radius.lg, borderWidth: 1, borderColor: colors.bg.border, overflow: 'hidden' },
  stylistScoreRow:     { flexDirection: 'row', paddingVertical: spacing.md, paddingHorizontal: spacing.md },
  stylistScoreBox:     { flex: 1, alignItems: 'center' },
  stylistScoreDiv:     { width: 1, backgroundColor: colors.bg.border, marginVertical: 4 },
  stylistScoreValue:   { fontSize: 22, fontWeight: '600', color: colors.text.primary, letterSpacing: 0.3 },
  stylistScoreLabel:   { fontSize: 9, letterSpacing: 1.5, color: colors.text.faint, textTransform: 'uppercase', marginTop: 4 },

  completionBarWrap:   { paddingHorizontal: spacing.md, paddingBottom: spacing.md },
  completionBarBg:     { height: 5, backgroundColor: '#F0F0F0', borderRadius: 3, overflow: 'hidden' },
  completionBarFill:   { height: '100%', borderRadius: 3 },
  completionBarText:   { fontSize: 11, color: colors.text.secondary, marginTop: 8, fontWeight: '500', textAlign: 'center' },

  dressingImgWrap:     { position: 'relative' },
  dressingItemAiDot:   { position: 'absolute', bottom: 3, right: 3, width: 16, height: 16, borderRadius: 8, backgroundColor: '#FFD700', alignItems: 'center', justifyContent: 'center', borderWidth: 1.5, borderColor: '#FFF' },
  dressingItemAiText:  { fontSize: 8 },

  missingBlockHeader:  { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 },
  missingBlockIcon:    { fontSize: 20 },
  missingBlockTitle:   { fontSize: 13, fontWeight: '700', color: colors.text.primary, letterSpacing: 0.2 },
  missingBlockSub:     { fontSize: 10, color: colors.text.faint, marginTop: 2 },
  missingPillNew:      { flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 14, paddingVertical: 10, borderRadius: radius.md, backgroundColor: '#FFF5E6', borderWidth: 1.5, borderColor: '#FFE0B2' },
  missingIconNew:      { fontSize: 16 },
  missingLabelNew:     { fontSize: 12, fontWeight: '600', color: '#B8560A', letterSpacing: 0.2 },
  missingArrowNew:     { fontSize: 14, color: '#B8560A', fontWeight: '700', marginLeft: 2 },

  // ═══ TENDANCES POUR VOUS ═══
  trendHeader:         { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: spacing.md },
  trendLabel:          { fontSize: 9, letterSpacing: 2.5, color: '#B8860B', fontWeight: '700', marginBottom: 4 },
  trendTitle:          { fontSize: 18, fontWeight: '400', color: colors.text.primary },
  trendSeeAll:         { fontSize: 11, color: colors.text.secondary, letterSpacing: 0.5 },
  trendRow:            { gap: 12, paddingBottom: 4, paddingRight: spacing.md },
  trendCard:           { width: 170, backgroundColor: '#FFFFFF', borderRadius: radius.md, borderWidth: 1, borderColor: colors.bg.border, overflow: 'hidden' },
  trendImgWrap:        { width: '100%', height: 210, backgroundColor: '#F5F5F5', position: 'relative' },
  trendImg:            { width: '100%', height: '100%' },
  trendImgPlaceholder: { width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' },
  trendBadgePromo:     { position: 'absolute', top: 8, left: 8, backgroundColor: '#E53935', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  trendBadgePromoText: { fontSize: 10, fontWeight: '700', color: '#FFF', letterSpacing: 0.3 },
  trendBadgeCashback:  { position: 'absolute', top: 8, right: 8, width: 26, height: 26, borderRadius: 13, backgroundColor: '#4CAF50', alignItems: 'center', justifyContent: 'center' },
  trendBadgeCashbackText: { fontSize: 13 },
  trendBadgeMissing:   { position: 'absolute', bottom: 8, left: 8, right: 8, backgroundColor: 'rgba(0,0,0,0.78)', paddingHorizontal: 8, paddingVertical: 5, borderRadius: 6 },
  trendBadgeMissingText: { fontSize: 9, color: '#FFD700', fontWeight: '700', textAlign: 'center', letterSpacing: 0.2 },
  trendInfo:           { padding: 10 },
  trendBrand:          { fontSize: 8, letterSpacing: 1.5, color: '#999', marginBottom: 3 },
  trendName:           { fontSize: 11, fontWeight: '500', color: colors.text.primary, lineHeight: 14, minHeight: 28 },
  trendPriceRow:       { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 6 },
  trendPrice:          { fontSize: 14, fontWeight: '700', color: colors.text.primary },
  trendPriceOrig:      { fontSize: 10, color: '#999', textDecorationLine: 'line-through' },

  dressingStats:    { flexDirection:'row', borderBottomWidth:1, borderBottomColor:colors.bg.border },
  dressingStat:     { flex:1, alignItems:'center', paddingVertical:spacing.md },
  dressingDiv:      { width:1, backgroundColor:colors.bg.border },
  dressingStatNum:  { fontSize:22, fontWeight:'300', color:colors.text.primary },
  dressingStatLabel:{ fontSize:9, letterSpacing:1.5, color:colors.text.faint, textTransform:'uppercase', marginTop:2 },
  // v1.12.92 — agrandissement des tuiles dressing pour matcher les trending cards (170×210)
  dressingItems:    { paddingHorizontal:spacing.md, paddingVertical:spacing.md, gap:12 },
  dressingItem:     { width:170, backgroundColor:'#FFFFFF', borderRadius:radius.md, borderWidth:1, borderColor:colors.bg.border, overflow:'hidden' },
  dressingImg:      { width:'100%', height:210, resizeMode:'cover' },
  dressingImgPlaceholder: { width:'100%', height:210, backgroundColor:colors.bg.surface, alignItems:'center', justifyContent:'center' },
  dressingItemName: { fontSize:11, fontWeight:'500', color:colors.text.primary, lineHeight:14, paddingHorizontal:10, paddingVertical:8 },
  dressingViewAll:  { width:170, height:248, borderRadius:radius.md, borderWidth:1, borderColor:colors.bg.border, backgroundColor:'#FAFAFA', alignItems:'center', justifyContent:'center', gap:8 },
  dressingViewAllIcon: { fontSize:32, color:colors.text.secondary },
  dressingViewAllLabel: { fontSize:11, color:colors.text.secondary, letterSpacing:1, fontWeight:'600' },

  missingBlock:     { borderTopWidth:1, borderTopColor:colors.bg.border, padding:spacing.md },
  missingTitle:     { fontSize:9, letterSpacing:2, color:colors.text.faint, textTransform:'uppercase', marginBottom:8 },
  missingRow:       { gap:8 },
  missingPill:      { flexDirection:'row', alignItems:'center', gap:5, paddingHorizontal:12, paddingVertical:7, borderRadius:radius.pill, borderWidth:1.5, borderColor:colors.bg.border, backgroundColor:'#FAFAFA' },
  missingIcon:      { fontSize:13 },
  missingLabel:     { fontSize:11, fontWeight:'500', color:colors.text.secondary, letterSpacing:0.3 },

  emptyDressing:    { marginHorizontal:spacing.xl, marginBottom:spacing.lg, padding:spacing.xl, borderRadius:radius.lg, borderWidth:1.5, borderColor:'#D4AF72', borderStyle:'dashed', alignItems:'center', gap:8, backgroundColor:'#FFFBF5' },
  emptyDressingIcon:{ fontSize:36 },
  emptyDressingTitle:{ fontSize:16, fontWeight:'700', color:colors.text.primary, letterSpacing:0.3 },
  emptyDressingDesc: { fontSize:12, color:colors.text.faint, textAlign:'center', lineHeight:18 },
  // v1.12.304 — bouton + visible sur la CTA dressing vide
  emptyDressingPlusBtn: {
    marginTop: 8,
    flexDirection: 'row', alignItems: 'center', gap: 8,
    paddingHorizontal: 18, paddingVertical: 10,
    backgroundColor: '#1A1A1A',
    borderRadius: 100,
  },
  emptyDressingPlusIcon: { fontSize: 18, color: '#D4AF72', fontWeight: '700' },
  emptyDressingPlusText: { fontSize: 12, color: '#D4AF72', fontWeight: '700', letterSpacing: 1 },

  catRow:           { paddingBottom:spacing.md, paddingHorizontal:2, paddingRight:16, gap:8 },
  catPill:          { paddingHorizontal:12, paddingVertical:7, borderRadius:radius.pill, borderWidth:1.5, borderColor:colors.bg.border, backgroundColor:'#FFFFFF' },
  catPillActive:    { borderColor:colors.text.primary, backgroundColor:colors.text.primary },
  catPillText:      { fontSize:11, fontWeight:'500', color:colors.text.secondary },

  productsRow:      { gap:12, paddingBottom:4, paddingLeft:2, paddingRight:16 },
  productCard:      { width:150, backgroundColor:'#FFFFFF', borderRadius:radius.md, borderWidth:1, borderColor:colors.bg.border, overflow:'hidden' },
  productImg:       { width:'100%', height:180, position:'relative' },
  productImgInner:  { width:'100%', height:'100%', resizeMode:'cover' },
  partnerBadge:     { position:'absolute', top:6, right:6, width:20, height:20, borderRadius:10, alignItems:'center', justifyContent:'center' },
  partnerText:      { fontSize:7, color:'#fff', fontWeight:'700' },
  productName:      { fontSize:11, fontWeight:'500', color:colors.text.primary, paddingHorizontal:8, paddingTop:8, lineHeight:15 },
  productBrand:     { fontSize:9, color:colors.text.faint, paddingHorizontal:8, marginTop:2, letterSpacing:0.5 },
  productBottom:    { flexDirection:'row', justifyContent:'space-between', alignItems:'center', paddingHorizontal:8, paddingVertical:8 },
  productPrice:     { fontSize:13, fontWeight:'600', color:colors.text.primary },

  emptyState:       { padding:spacing.lg, alignItems:'center' },
  emptyStateText:   { fontSize:13, color:colors.text.secondary },

  quickGrid:        { flexDirection:'row', flexWrap:'wrap', gap:10 },
  quickHeader:      { flexDirection: 'row', alignItems: 'flex-end', marginBottom: spacing.md },
  quickBadgeLabel:  { fontSize: 9, letterSpacing: 2.5, color: '#B8860B', fontWeight: '700', marginBottom: 4 },
  quickTitle:       { fontSize: 18, fontWeight: '400', color: colors.text.primary },
  quickCard:        {
    width: '48.5%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: radius.md,
    borderWidth: 1,
    paddingVertical: spacing.md,
    paddingHorizontal: 12,
    minHeight: 64,
  },
  quickCardLeft:    { width: 36, alignItems: 'center' },
  quickCardIcon:    { fontSize: 24 },
  quickCardRight:   { flex: 1 },
  quickCardLabel:   { fontSize: 12, fontWeight: '700', color: colors.text.primary, letterSpacing: 0.2 },
  quickCardSub:     { fontSize: 9, color: colors.text.secondary, marginTop: 2, letterSpacing: 0.2 },
  quickCardArrow:   { fontSize: 14, color: colors.text.faint, fontWeight: '600' },

  quickBtn:         { width:'47%', flexDirection:'row', alignItems:'center', gap:10, backgroundColor:'#FFFFFF', borderRadius:radius.md, borderWidth:1, borderColor:colors.bg.border, padding:spacing.md },
  quickIcon:        { fontSize:20 },
  quickLabel:       { fontSize:12, fontWeight:'500', color:colors.text.primary, letterSpacing:0.2 },
});
